import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RulerIcon, CheckCircleIcon, CloseIcon, DocumentIcon, ChartIcon, SchoolIcon, FileIcon, BellIcon } from '../../components/icons/ChatCategoryIcons.js';
import TossLoadingSpinner from '../../components/layout/TossLoadingSpinner';
import ProUpgradeModal from '../../components/chat/ProUpgradeModal';
import LoginPrompt from '../../Auth/LoginPrompt';
import { useToast } from '../../components/Toast';
import { fetchWithRetry, calcApiTimeout } from '../../utils/fetchWithRetry';
import { readSseJson } from '../../utils/sseJsonStream';
import { normalizePreCheckStats, summarizeFileStats } from '../../utils/filePreCheckStats';
import { appendRegenerationInstruction } from '../../utils/saenggibuRegeneration';
import { restoreSaenggibuModelMode } from '../../utils/saenggibuModelMode';
import {
  nextStudentCareerData,
  nextStudentClubData,
  nextStudentGenerationData,
  nextStudentHighSchoolData,
  supportsNextStudent,
} from '../../utils/saenggibuNextStudent';
import RegenerationChainList from '../../components/saenggibu/RegenerationChainList';
import RosterStudentPicker, { useClassRosters } from '../../components/saenggibu/RosterStudentPicker';
import '../../components/saenggibu/RosterStudentPicker.css';
import { addStudentMemo, linkSingleRecord } from '../../api/classRosterApi';
import { MEMO_MAX_CHARS, suggestMemoArea } from '../../utils/studentMemos';
import { hasAnyStyleProfileContent, isMyStyleActiveForCategory } from '../../utils/saenggibuStyleProfile';
import { analyzeSaenggibuFileFromLibrary, cancelSaenggibuGeneration, getFirstDayUnlock, getStyleProfiles, refineSaenggibuContent, saveMultiClassSaenggibuRecord, setStyleMasterEnabled, updateMultiClassSaenggibuVersion, updateSaenggibuVersion } from '../../api/saenggibuApi';
// 🔍 파일 사전 검사
import FilePreCheckModal from '../../components/common/FilePreCheckModal';
import FieldHelpTooltip from '../../components/common/FieldHelpTooltip';
import { preCheckFiles } from '../../api/chatApi';
import { uploadSaenggibuAttachment } from '../../firebase/storage';
// 📎 파일 첨부 한도 중앙 설정
import {
  getFileLimits,
  MAX_FILES_DEFAULT,
  MAX_SAENGGIBU_STORAGE_PDF_PAGES,
  MAX_SAENGGIBU_STORAGE_PPT_SLIDES,
} from '../../constants/fileLimits';
import {
  SUPPORTED_UPLOAD_ACCEPT,
  isClearlyBlockedUploadFile,
} from '../../constants/fileTypes';
import BatchProcessingContainer from '../../components/chat/BatchProcessingContainer';
// 📊 GA4 이벤트 트래킹
import { trackSaenggibuCreate } from '../../utils/analytics';
import { trackClassRosterEvent, trackRiseFeatureAction, trackSaenggibuUxEvent } from '../../utils/eventTracker';
import useCustomExamples from '../../hooks/useCustomExamples';
import useIsMobile from '../../hooks/useIsMobile';
import { useSubscription } from '../../contexts/SubscriptionContext';
import ClubForm from '../../components/saenggibu/ClubForm';
import SimilarSentencePanel from '../../components/saenggibu/SimilarSentencePanel';
import SaenggibuResultActions, { collectNormalSaenggibuResults } from '../../components/saenggibu/SaenggibuResultActions';
import SaenggibuResultMoreMenu from '../../components/saenggibu/SaenggibuResultMoreMenu';
import SaenggibuSuggestionMount from '../../components/saenggibu/SaenggibuSuggestionMount';
import useSaenggibuSuggestion from '../../hooks/useSaenggibuSuggestion';
import { runSaenggibuSuggestionAction } from '../../utils/saenggibuSuggestion';
import { buildSimilarEvaluationContext, resolveSimilarLaunchContext } from '../../utils/similarSentenceLaunch';
import {
  achievementCodeSuffix,
  activityNameSuffix,
  buildPlanItemNames,
  buildPlanSelectionMeta,
  nextPlanSelection,
  resolveInitialPlanSelection,
} from '../../utils/evalPlanSelection';
import SaenggibuAnalysisReviewModal from '../../components/saenggibu/SaenggibuAnalysisReviewModal';
import SaenggibuCategoryChangeDialog from '../../components/saenggibu/SaenggibuCategoryChangeDialog';
import SaenggibuFileDropController from '../../components/saenggibu/SaenggibuFileDropController';
import SaenggibuContentEditor from '../../components/saenggibu/SaenggibuContentEditor';
import useSaenggibuEvidence from '../../components/saenggibu/evidence/useSaenggibuEvidence';
import SaenggibuEvidenceText, { SaenggibuEvidenceSummary, useShowEvidenceNumbers } from '../../components/saenggibu/evidence/SaenggibuEvidenceText';
import SaenggibuEvidenceDialog from '../../components/saenggibu/evidence/SaenggibuEvidenceDialog';
import AchievementStandardPicker from '../../components/saenggibu/AchievementStandardPicker';
import AchievementStandardEntry from '../../components/saenggibu/AchievementStandardEntry';
import EvaluationElementsSuggest from '../../components/saenggibu/EvaluationElementsSuggest';
import SaenggibuAttachMenu from '../../components/saenggibu/SaenggibuAttachMenu';
import { LIBRARY_SOURCE, createLibraryPseudoFile, isLibraryPseudoFile } from '../../utils/saenggibuLibraryPick';
import {
  appendStandardLines,
  standardStillInText,
  attachAchievementStandardsToPayload,
  mergeSelectedStandards,
  recordIdentity,
} from '../../components/saenggibu/achievementStandardPicker.utils';
import {
  droppedStandardsNotice,
  refsFromHistoryRecord,
  restoreValidatedStandards,
} from '../../components/saenggibu/achievementStandardRestore';
import { calculateNEISBytes, clampSaenggibuTargetBytes, getSaenggibuMaxBytes } from '../../utils/neisBytes';
import {
  getSaenggibuCategoryChangeDialog,
  hasSaenggibuFileUploadState,
  hasSaenggibuResult,
  shouldClearSaenggibuResultForCategory,
  shouldConfirmSaenggibuCategoryChange,
  summarizeSaenggibuResultFile,
} from './saenggibuResultState';
import {
  buildCreativeAnalysisDraft,
  buildCreativeStudentDraft,
  buildCreativeUploadedFileMetadata,
  classifyCreativeInputProfile,
  getCreativeAnalysisSummaryItems,
  mergeCreativeAnalysisWithPrompt,
  removeCreativeAnalysisFromPrompt,
} from './creativeFileAnalysis';
import {
  MULTI_STUDENT_BATCH_CATEGORIES,
  buildBatchStateFromFileStudents,
  countBatchableFileStudents,
} from '../../utils/multiStudentFileBatch';
import SchoolEventTopics from './SchoolEventTopics';
import BehaviorTraitPicker from '../../components/saenggibu/BehaviorTraitPicker';
import { BEHAVIOR_TRAIT_GROUPS } from '../../data/saenggibu/behaviorTraitChips';
import { parsePromptPieces } from '../../utils/behaviorTraitSelection';
import '../../styles/components/saenggibu-generation.css';
import { 
  subjectSpecificExamples, 
  subjectEmphasisPoints, 
  sampleAchievements, 
  subjectPerformanceAssessment,
  subjectLearningAttitude,
  subjectInquiryActivities,
  subjectCreativity,
  subjectCollaboration,
  subjectCareerConnection,
  creativityEmphasisPoints,
  subjectList 
} from '../../data/subjectEnhancement';

const SAENGGIBU_STREAM_INACTIVITY_TIMEOUT_MS = 90_000;
const HIGH_SCHOOL_STORAGE_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const SAENGGIBU_STORAGE_FILE_TOTAL_LIMIT_BYTES = 50 * 1024 * 1024;
const SAENGGIBU_JSON_BODY_LIMIT_BYTES = 30 * 1024 * 1024;
const EVAL_PLAN_MAX_FILES = 5;
const EVAL_PLAN_TOTAL_LIMIT_BYTES = 30 * 1024 * 1024;
const EVAL_UNIT_COUNT_MAX = 500;
// 결과 카드 헤더 액션 버튼 공통 스타일 (복사 · AI로 다듬기) — 더보기(⋯) 28px 와 같은 높이
const resultActionButtonStyle = {
  padding: '4px 10px',
  minHeight: '28px',
  borderRadius: '5px',
  fontSize: '12px',
  fontWeight: '600',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};

const formatMB = (bytes = 0) => `${(bytes / 1024 / 1024).toFixed(1)}MB`;
const createTextFingerprint = (value = '') => {
  const text = String(value || '');
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${text.length}:${(hash >>> 0).toString(36)}`;
};

const buildStorageFileTotalLimitResult = (featureName, files, totalSize, limitBytes = SAENGGIBU_STORAGE_FILE_TOTAL_LIMIT_BYTES) => ({
  can_proceed: false,
  needs_confirmation: false,
  overall_status: 'error',
  title: '파일 용량을 줄여주세요',
  message: `${featureName} 파일 합계가 ${formatMB(totalSize)}예요. 한 번에 ${Math.round(limitBytes / 1024 / 1024)}MB까지만 분석할 수 있어요.`,
  total_files: files.length,
  total_size: totalSize,
  total_size_display: formatMB(totalSize),
  files_info: files.map(file => ({
    filename: file.name || '파일',
    status: 'warning',
    file_size: file.size || 0,
    file_size_display: formatMB(file.size || 0),
    issues: [],
  })),
  recommendations: [
    '여러 파일을 나눠 분석하거나, 파일을 압축해 주세요.',
  ],
});

const isLikelyFetchNetworkError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return message.includes('failed to fetch')
    || message.includes('networkerror')
    || message.includes('load failed')
    || message.includes('network request failed');
};

const getFriendlyGenerationErrorMessage = (error) => {
  if (error?.userMessage) return error.userMessage;
  if (isLikelyFetchNetworkError(error)) {
    return '요청이 브라우저에서 전송되지 못했어요. 첨부 파일 용량을 줄이거나 잠시 후 다시 시도해주세요.';
  }
  return error?.message || '생기부 생성에 실패했어요.';
};

const SaenggibuHelpLabel = ({ children, help }) => (
  <span className="sgb-label-with-help">
    {children}
    <FieldHelpTooltip title={help.title} items={help.items} ariaLabel={`${children} 안내`} />
  </span>
);

const HELP_TEXT = {
  behaviorRecord: {
    title: '어떤 파일을 올리면 좋나요?',
    items: [
      '학생별 누가기록, 상담 기록, 행동 관찰 메모, 담임 기록처럼 평소 태도와 관계가 보이는 자료를 올려주세요.',
      '좋은 점뿐 아니라 개선이 필요한 모습도 있으면 더 자연스럽게 정리할 수 있어요.',
    ],
  },
  subjectActivityName: {
    title: '활동명(성취기준)에는 무엇을 넣나요?',
    items: [
      '활동명, 수행평가명, 단원명, 성취기준을 함께 넣을 수 있어요.',
      '성취기준이 여러 개라면 줄바꿈해서 모두 넣어도 됩니다.',
      '예: 통계 포스터 제작 다음 줄에 [12확통03-01], [12확통03-02]처럼 여러 성취기준을 붙여넣으세요.',
    ],
  },
  subjectEvaluation: {
    title: '평가요소에는 무엇을 쓰나요?',
    items: [
      '평가방법, 평가기준, 평가내용, 학생 활동 특징을 적어주세요.',
      '학생별 수준을 나누고 싶다면 학교급에 맞는 수준별 특징이나 대표 행동을 함께 적으면 좋아요.',
    ],
  },
  subjectPlanFile: {
    title: '어떤 파일을 올리면 좋나요?',
    items: [
      '학교평가운영계획, 수행평가 계획서, 채점 기준표, 루브릭처럼 평가 기준이 들어간 파일을 올려주세요.',
      'AI가 성취기준과 평가요소를 읽어 세특 방향을 잡는 데 사용해요.',
    ],
  },
  highStudentActivity: {
    title: '학생 활동 자료에는 무엇을 넣나요?',
    items: [
      '학생 보고서, 탐구 산출물, 발표 대본, 실험 기록, 수행평가 결과처럼 학생이 실제로 한 활동을 넣어주세요.',
      '학생 자료가 가장 중요한 근거라 구체적인 주제와 과정이 있을수록 좋아요.',
    ],
  },
  highStudentFile: {
    title: '학생 활동 파일 예시',
    items: [
      '보고서 PDF, 발표 자료, 실험 기록 사진, 활동지, 포트폴리오처럼 학생 결과물이 담긴 파일을 올려주세요.',
      '여러 파일을 올릴 수 있고, AI는 학생 활동 내용을 우선 반영해요.',
    ],
  },
  highAchievement: {
    title: '성취기준에는 무엇을 넣나요?',
    items: [
      '성취기준, 평가계획표, 평가요소, 채점기준을 넣어주세요.',
      '학생 활동을 어떤 기준으로 해석할지 잡아주는 역할이라, 학생 자료와 함께 쓰면 더 정확해요.',
    ],
  },
  highStudentInfo: {
    title: '학생 정보에는 무엇을 쓰나요?',
    items: [
      '성취수준, 희망학과, 진로, 수업 태도, 강점, 보완점처럼 문장 톤을 정하는 정보를 적어주세요.',
      '필수는 아니지만 있으면 학생별 차이가 더 잘 살아나요.',
    ],
  },
  changcheFile: {
    title: '창체 파일 예시',
    items: [
      '자율활동 누가기록, 행사 참여 기록, 학급 활동 메모, 활동 보고서처럼 창체 활동 과정이 보이는 자료를 올려주세요.',
      '활동명만 있는 파일보다 학생 역할과 참여 모습이 있는 자료가 더 좋아요.',
    ],
  },
  clubInfo: {
    title: '동아리 정보에는 무엇을 쓰나요?',
    items: [
      '동아리명, 활동 목표, 주요 활동, 학생 역할, 활동 주제를 적어주세요.',
      '계획서는 방향을 잡는 자료이고, 실제 학생 활동 내용이 있으면 더 구체적으로 써져요.',
    ],
  },
  clubPlanFile: {
    title: '동아리 계획서 파일 예시',
    items: [
      '연간 동아리 계획서, 운영 계획, 활동 주제 목록, 지도 계획처럼 동아리의 방향이 담긴 파일을 올려주세요.',
      '학생별 활동 보고서가 있다면 학생 활동 파일에도 함께 올려주세요.',
    ],
  },
  careerActivity: {
    title: '진로 활동에는 무엇을 넣나요?',
    items: [
      '진로 탐구 보고서, 직업 조사, 학과 탐색, 인터뷰, 발표, 체험활동처럼 학생이 한 진로 관련 활동을 넣어주세요.',
      '희망 진로가 아직 없어도 탐색 과정이 있으면 문장을 만들 수 있어요.',
    ],
  },
  careerStudentInfo: {
    title: '학생 정보에는 무엇을 쓰나요?',
    items: [
      '희망학과, 희망 직업, 관심 분야, 강점, 진로 고민, 관련 과목 성향을 적어주세요.',
      '정답을 맞히는 항목이 아니라, 학생의 방향을 잡는 참고 정보예요.',
    ],
  },
  careerFile: {
    title: '진로 파일 예시',
    items: [
      '진로 탐구 보고서, 학과 조사 자료, 발표 자료, 상담 기록, 진로희망조사서, 활동 사진을 올려주세요.',
      '파일만 올려도 되지만, 희망 진로를 함께 적으면 더 자연스럽게 써져요.',
    ],
  },
  generationCount: {
    title: '몇 개를 만들까요?',
    items: [
      '같은 자료로 서로 다른 표현의 문장을 여러 개 만들어 비교할 수 있어요.',
      '창체에서는 학생 수를 뜻하고, 다른 항목에서는 다양한 문장 버전 수를 뜻해요.',
    ],
  },
};

const SUBJECT_LEVEL_SCHEMES = {
  elementary: [
    { key: 'excellent', label: '매우잘함', className: 'level-high' },
    { key: 'good', label: '잘함', className: 'level-mid' },
    { key: 'basic', label: '보통', className: 'level-basic' },
    { key: 'needsSupport', label: '노력요함', className: 'level-low' },
  ],
  default: [
    { key: 'high', label: '상', className: 'level-high' },
    { key: 'mid', label: '중', className: 'level-mid' },
    { key: 'low', label: '하', className: 'level-low' },
  ],
};

const getSubjectLevelScheme = (schoolLevel) => (
  schoolLevel === '초등' ? SUBJECT_LEVEL_SCHEMES.elementary : SUBJECT_LEVEL_SCHEMES.default
);

const createDefaultLevelCounts = (schoolLevel) => (
  getSubjectLevelScheme(schoolLevel).reduce((acc, level) => {
    acc[level.key] = 1;
    return acc;
  }, {})
);

const MAX_SUBJECT_CLASSES = 7;

const getLevelBadgeStyle = (level) => {
  if (level === '상' || level === '매우잘함') {
    return { color: 'var(--whatquiz-blue-700)', backgroundColor: 'var(--whatquiz-blue-100)' };
  }
  if (level === '중' || level === '잘함') {
    return { color: 'var(--whatquiz-green-600)', backgroundColor: 'var(--whatquiz-green-100)' };
  }
  if (level === '보통') {
    return { color: 'var(--whatquiz-gray-700)', backgroundColor: 'var(--whatquiz-gray-100)' };
  }
  return { color: 'var(--whatquiz-red-600)', backgroundColor: 'var(--whatquiz-red-100)' };
};

const toAnalysisText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  if (Array.isArray(value)) {
    return value
      .map(toAnalysisText)
      .filter(Boolean)
      .join('\n');
  }
  if (typeof value === 'object') {
    const preferredKeys = ['name', 'title', 'label', 'level', 'criteria', 'description', 'content', 'score', 'max_points'];
    const preferredParts = preferredKeys
      .map(key => toAnalysisText(value[key]))
      .filter(Boolean);

    if (preferredParts.length > 0) {
      return preferredParts.join(' / ');
    }

    return Object.entries(value)
      .slice(0, 6)
      .map(([key, itemValue]) => {
        const text = toAnalysisText(itemValue);
        return text ? `${key}: ${text}` : '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return '';
};

const pickAnalysisText = (data = {}, keys = []) => {
  for (const key of keys) {
    const text = toAnalysisText(data[key]);
    if (text) return text;
  }
  return '';
};

const appendDraftLine = (lines, label, value) => {
  const text = toAnalysisText(value);
  if (text) lines.push(`[${label}] ${text}`);
};

const createSummaryItem = (label, value) => {
  const text = toAnalysisText(value);
  return text ? { label, value: text } : null;
};

const buildSubjectAnalysisSummaryItems = (data = {}) => ([
  createSummaryItem('과목', data.subject),
  createSummaryItem('활동명', pickAnalysisText(data, ['activity_name', 'name'])),
  createSummaryItem('성취기준', pickAnalysisText(data, ['achievement_standard', 'achievement_standards', 'achievement'])),
  createSummaryItem('단원', data.unit),
  createSummaryItem('영역', data.domain),
  createSummaryItem('평가방법', pickAnalysisText(data, ['evaluation_method', 'method', 'assessment_method'])),
  createSummaryItem('평가요소', pickAnalysisText(data, ['evaluation_items', 'evaluation_elements', 'assessment_elements', 'evaluation_item'])),
  createSummaryItem('평가기준', pickAnalysisText(data, ['evaluation_criteria', 'criteria', 'rubric', 'scoring_criteria', 'scoring_levels'])),
  createSummaryItem('평가내용', pickAnalysisText(data, ['evaluation_content', 'assessment_content', 'task', 'task_description'])),
  createSummaryItem('평가시기', data.evaluation_period),
]).filter(Boolean);

const hasSubjectAnalysisData = (data = {}) => Boolean(pickAnalysisText(data, [
  'name',
  'activity_name',
  'achievement_standard',
  'achievement_standards',
  'activity_content',
  'records',
  'evaluation_method',
  'evaluation_criteria',
  'criteria',
  'rubric',
  'evaluation_content',
  'evaluation_items',
  'evaluation_elements',
  'assessment_elements',
]));

const buildSubjectActivityNameText = (data = {}) => {
  const lines = [];
  const activityName = pickAnalysisText(data, ['activity_name', 'name']);
  const achievementStandard = pickAnalysisText(data, ['achievement_standard', 'achievement_standards', 'achievement']);

  if (activityName) lines.push(activityName);
  if (achievementStandard && achievementStandard !== activityName) {
    lines.push(`[성취기준] ${achievementStandard}`);
  }

  return lines.join('\n').trim();
};

const getSubjectSubCategory = (activityName = '') => {
  const lines = activityName
    .split('\n')
    .map(line => line.trim());
  const activityLine = lines.find(line => line && !line.startsWith('[') && !line.includes('성취기준'));

  if (activityLine) {
    return (activityLine.split(/[-–—\s]/)[0]?.trim() || '').slice(0, 20);
  }

  const standardCodeLine = lines.find(line => /\[[0-9][^\]]{2,30}\]/.test(line));
  const standardCode = standardCodeLine?.match(/\[([0-9][^\]]{2,30})\]/)?.[1]?.trim();
  return standardCode ? standardCode.slice(0, 20) : '';
};

const buildSubjectReviewRawText = (activityName = '', activityContent = '') => {
  const sections = [];
  if (activityName) sections.push(`[활동명(성취기준)]\n${activityName}`);
  if (activityContent) sections.push(`[평가요소]\n${activityContent}`);
  return sections.join('\n\n');
};

// 평가 칩을 연달아 누를 때 안내 토스트가 쌓여 "모두 합쳐서 쓰기" 버튼을 가리지 않게 한다.
const PLAN_TOAST_OPTIONS = { replaceKey: 'saenggibu-plan' };

const SaenggibuGenerationForm = ({ 
  auth, 
  onError,
  onLoginRequired,
  uiMode,
  setUiMode,
  showFileHint,
  fileAttachRef,
  scrollToModeButtons,
  scrollToFileAttach,
  initialState,
  batchResetToken = 'initial'
}) => {
  const navigate = useNavigate();
  const { showToast, clearToasts } = useToast();
  const isMobile = useIsMobile();
  
  const promptTextareaRef = useRef(null);
  const generationStatusRef = useRef(null);
  const appliedCreativeFileDraftRef = useRef('');
  const restoredUploadedFilesMetaRef = useRef(
    Array.isArray(initialState?.uploadedFilesMeta) ? initialState.uploadedFilesMeta : []
  );
  const historySourceRecordIdRef = useRef(initialState?.sourceRecordId || '');
  const skipCategoryResetRef = useRef(initialState?.category || null);
  const proDefaultModelAppliedRef = useRef(false);
  const [activeInitialBatchState, setActiveInitialBatchState] = useState(
    () => initialState?.batchRestore || null
  );
  const { subscriptionStatus } = useSubscription();
  const isProUser = Boolean(subscriptionStatus?.isPro);

  // 가입 첫날 해제: 버전 수·학생 수만 PRO와 같게 연다 (모델·여러 반·파일은 그대로).
  const [firstDayUnlock, setFirstDayUnlock] = useState(null);
  useEffect(() => {
    if (!auth.currentUser || isProUser) {
      setFirstDayUnlock(null);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve()
      .then(() => getFirstDayUnlock())
      .then((data) => {
        if (cancelled || !data?.active) return;
        setFirstDayUnlock(data);
        trackSaenggibuUxEvent('saenggibu_first_day_unlock_seen', { variant: data.variant || '' });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.currentUser, isProUser]);
  const canUseProCounts = isProUser || Boolean(firstDayUnlock?.active);

  // 🎨 내 스타일 프로필 (진입 버튼 상태 표시용 — 적용 자체는 백엔드가 자동 처리)
  const [myStyleProfiles, setMyStyleProfiles] = useState({});
  // 🎨 내 스타일 전체 적용 마스터 스위치 (기본 ON — 서버 기본값과 동일)
  const [myStyleMasterEnabled, setMyStyleMasterEnabled] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (!auth.currentUser) {
      setMyStyleProfiles({});
      return undefined;
    }
    getStyleProfiles()
      .then((data) => {
        if (!cancelled) {
          setMyStyleProfiles(data?.profiles || {});
          setMyStyleMasterEnabled(data?.master_enabled !== false);
        }
      })
      .catch(() => {
        // 조회 실패는 조용히 무시 (버튼은 기본 상태로 표시)
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.currentUser]);

  const [creativeInputProfile, setCreativeInputProfile] = useState(() => (
    initialState?.category === '창체'
      ? classifyCreativeInputProfile(initialState?.prompt)
      : 'activity_list'
  ));
  
  // 생성 폼 상태
  const [generationData, setGenerationData] = useState({
    prompt: '',
    wordCount: 800, // 기본 800 Byte
    generationCount: initialState?.category === '창체'
      && classifyCreativeInputProfile(initialState?.prompt) === 'detailed_evidence'
      ? 1
      : 2,
    emphasizePoints: [],
    selectedModel: 'gemini-2.5-flash-lite' // 🆕 빠른 모드 기본값 (Free 유저)
  });

  // 교과 카테고리 전용 상태 (빠르게 모드)
  const [subjectData, setSubjectData] = useState({
    activityName: '',    // 활동명(성취기준)
    activityContent: '', // 활동내용
    attachedFile: null   // 첨부된 파일 정보
  });
  
  // 교과/세특 전용: 학교급별 성취수준 학생 수 설정
  const [levelCounts, setLevelCounts] = useState(() => createDefaultLevelCounts('중등'));
  const [subjectClassCount, setSubjectClassCountState] = useState(1);
  const [multiClassResults, setMultiClassResults] = useState([]);
  const [activeClassResultId, setActiveClassResultId] = useState('class-1');
  const [multiClassProgress, setMultiClassProgress] = useState({ current: 0, total: 0, message: '' });
  
  // 🆕 성취수준별 학생 수 제한 설정
  const LEVEL_LIMITS = {
    FREE_TOTAL_MAX: 3,   // Free 사용자 총합 최대
    PRO_TOTAL_MAX: 30    // Pro 사용자 총합 최대
  };
  
  // 교과/세특 전용: 학교급 선택 (초등/중등/고등)
  const [schoolLevel, setSchoolLevel] = useState('중등'); // 기본값: 중등
  const subjectLevelScheme = getSubjectLevelScheme(schoolLevel);
  const schoolLevelRef = useRef(schoolLevel);
  
  // 고등 모드 전용 상태
  const [highSchoolData, setHighSchoolData] = useState({
    achievementStandard: '', // 성취기준(평가 계획표)
    growthPoints: '',        // 학생의 성장 포인트/역량
    studentActivity: '',     // 🆕 학생 활동 자료 (텍스트 입력)
    files: [],              // 업로드된 파일들
    filesContent: [],       // 파일 메타 (base64 또는 Storage 경로)
    filesStats: []          // 파일별 통계 (pageCount, slideCount, contentLength)
  });
  const [highSchoolUploadProgress, setHighSchoolUploadProgress] = useState(null);
  const [separateReports, setSeparateReports] = useState(false);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [isAchievementPickerOpen, setIsAchievementPickerOpen] = useState(false);
  const [achievementRestoreNotice, setAchievementRestoreNotice] = useState('');
  
  // 드래그 앤 드롭 상태
  const [isDragging, setIsDragging] = useState(false);

  // 다중 버전 생성 관련 상태
  const [versions, setVersions] = useState([]);
  const [versionLevels, setVersionLevels] = useState([]); // 교과용 성취 수준 (상/중/하)
  const [resultContext, setResultContext] = useState(null);

  // PRO 고급 옵션 상태 (고정값으로 처리)
  const useAdvancedModel = false;
  const [showProModal, setShowProModal] = useState(false);
  const [proModalFeature, setProModalFeature] = useState('상위 모델 & 2026 최신데이터 팩트체크');

  // 🎨 내 스타일 마스터 스위치 (단일/배치 공용) — 판정 규칙은 utils/saenggibuStyleProfile (백엔드 profile_has_content 와 동일)
  // Free는 공통 스타일만 적용된다 (백엔드 get_active_profiles_for_generation(is_pro=False) 와 같은 규칙).
  const appliedStyleProfiles = isProUser ? myStyleProfiles : { global: myStyleProfiles.global };
  const hasAnyMyStyleProfile = hasAnyStyleProfileContent(appliedStyleProfiles);
  // 🎨 일괄 생성에서 고른 생기부 종류 (Step1 의 options.category 를 올려받는다) — 서버는 공통 + 이 종류의 프로필만 적용
  const [batchCategory, setBatchCategory] = useState('행동발달');

  const handleMyStyleMasterToggle = async (nextEnabled) => {
    const prev = myStyleMasterEnabled;
    setMyStyleMasterEnabled(nextEnabled);
    try {
      await setStyleMasterEnabled(nextEnabled);
    } catch (error) {
      setMyStyleMasterEnabled(prev);
      showToast('변경하지 못했어요. 다시 시도해주세요', 'error');
    }
  };

  // 🎨 내 스타일 진입 버튼 + 마스터 토글 한 줄 컨트롤 (단일 폼 라벨 줄 / 배치 상단 공용)
  const renderMyStyleControls = (isActiveForContext) => {
    const isMyStyleActive = myStyleMasterEnabled && isActiveForContext;
    const buttonLabel = isMyStyleActive
      ? '✓ 내 스타일 적용 중'
      : (hasAnyMyStyleProfile && !myStyleMasterEnabled ? '내 스타일 꺼짐' : '내 스타일 설정');
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        <button
          onClick={() => {
            trackSaenggibuUxEvent('saenggibu_style_entry', {
              saenggibu_category: selectedCategory,
              is_batch: uiMode === 'batch',
            });
            navigate('/saenggibu-my-style');
          }}
          style={{
            border: 'none',
            borderRadius: '8px',
            padding: '6px 10px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.15s',
            backgroundColor: isMyStyleActive ? 'var(--whatquiz-blue-50, #e8f3ff)' : 'transparent',
            color: isMyStyleActive ? 'var(--whatquiz-primary)' : 'var(--whatquiz-gray-400)'
          }}
        >
          {buttonLabel}
        </button>
        {hasAnyMyStyleProfile && (
          <label
            aria-label="내 스타일 전체 적용"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '44px',
              minHeight: '44px',
              cursor: 'pointer',
              margin: 0
            }}
          >
            <input
              type="checkbox"
              checked={myStyleMasterEnabled}
              onChange={(e) => handleMyStyleMasterToggle(e.target.checked)}
              style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
            />
            <span style={{
              position: 'relative',
              width: '36px',
              height: '20px',
              borderRadius: '9999px',
              backgroundColor: myStyleMasterEnabled ? '#3182f6' : '#d1d5db',
              transition: 'background-color 0.2s ease',
              flexShrink: 0
            }}>
              <span style={{
                position: 'absolute',
                top: '2px',
                left: myStyleMasterEnabled ? '18px' : '2px',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: '#ffffff',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
                transition: 'left 0.2s ease'
              }} />
            </span>
          </label>
        )}
      </div>
    );
  };

  // 🆕 생기부 모델 정의 (빠른 모드 / 고급 모드)
  const SAENGGIBU_MODELS = [
    { id: 'gemini-2.5-flash-lite', name: '빠른 모드', description: '빠르게 생성', isPro: false },
    { id: 'gemini-2.5-flash', name: '고급 모드', description: '더 정확하고 높은 퀄리티', isPro: true }
  ];

  const handleSchoolLevelChange = (level) => {
    setSchoolLevel(level);
    setLevelCounts(createDefaultLevelCounts(level));
  };

  useEffect(() => {
    schoolLevelRef.current = schoolLevel;
  }, [schoolLevel]);

  // 공통 구독 컨텍스트 기준으로 Pro 유저는 고급 모드를 최초 1회 기본값으로 적용.
  // 구독 정보보다 생성이 먼저 시작됐으면 바꾸지 않는다(결과와 입력 설정이 어긋나 다시 만들기 칸이 사라짐).
  useEffect(() => {
    if (isProUser && !proDefaultModelAppliedRef.current) {
      setGenerationData(prev => ({
        ...prev,
        selectedModel: prev.selectedModel === 'gemini-2.5-flash-lite'
          ? 'gemini-2.5-flash'
          : prev.selectedModel
      }));
      proDefaultModelAppliedRef.current = true;
    }

    if (!isProUser) {
      proDefaultModelAppliedRef.current = false;
    }
  }, [isProUser]);

  // 🆕 커스텀 예시 (훅으로 추출)
  const { customExamples, saveCustomExample, deleteCustomExample } = useCustomExamples({ auth, showToast });
  const [addingTo, setAddingTo] = useState(null); // { category, subcategory }
  const [newExampleText, setNewExampleText] = useState('');
  // handleAdvancedModelToggle 제거됨 - 교사 설정과 함께 단순화

  // 카테고리 선택 상태
  const [selectedCategory, setSelectedCategory] = useState(initialState?.category || '행동발달');
  const [selectedSubject, setSelectedSubject] = useState('국어'); // 교과 세부 과목

  // 목표 바이트 슬라이더 상한 (백엔드 get_saenggibu_target_max_bytes 미러: 기존 허용치 유지, 공식 상한이 더 크면 상향).
  // 카테고리·학교급이 바뀌어 상한이 내려가면 목표 바이트도 같이 내린다 (일괄 Step1 과 같은 동작).
  const singleMaxBytes = getSaenggibuMaxBytes(selectedCategory, schoolLevel);
  useEffect(() => {
    setGenerationData((prev) => {
      const clamped = clampSaenggibuTargetBytes(prev.wordCount, selectedCategory, schoolLevel);
      return clamped === prev.wordCount ? prev : { ...prev, wordCount: clamped };
    });
  }, [selectedCategory, schoolLevel]);
  
  // 🆕 동아리 탭 전용 상태
  const [clubData, setClubData] = useState({
    planFile: null,           // 동아리 계획서 파일
    planFileContent: null,    // 계획서 파일 내용 (base64)
    clubDescription: '',      // 동아리명/활동 설명
    studentActivity: '',      // 학생 활동 내용
    studentFiles: [],         // 학생 활동 파일들 (다중)
    studentFilesContent: [],  // 학생 파일 내용들 (base64 배열)
    studentFilesStats: []     // 학생 파일 통계 (pageCount, slideCount, contentLength)
  });
  
  const uploadSingleSaenggibuStorageFile = useCallback(async (file) => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      setShowLoginPrompt(true);
      throw new Error('로그인이 필요해요.');
    }

    setHighSchoolUploadProgress({ current: 1, total: 1, percent: 0 });
    try {
      return await uploadSaenggibuAttachment(file, userId, (percent) => {
        setHighSchoolUploadProgress({ current: 1, total: 1, percent });
      });
    } finally {
      setHighSchoolUploadProgress(null);
    }
  }, [auth.currentUser]);
  
  // 🆕 동아리 학생 활동 파일: 실제 업로드 핸들러 (사전 검사 통과 후 호출)
  const actualClubStudentFileUpload = useCallback(async (files, filesStats = []) => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      setShowLoginPrompt(true);
      return;
    }

    try {
      const filesArray = Array.isArray(files) ? files : Array.from(files);
      setHighSchoolUploadProgress({ current: 0, total: filesArray.length, percent: 0 });
      const fileContents = [];
      for (let index = 0; index < filesArray.length; index += 1) {
        const file = filesArray[index];
        const uploaded = await uploadSaenggibuAttachment(file, userId, (percent) => {
          setHighSchoolUploadProgress({
            current: index + 1,
            total: filesArray.length,
            percent,
          });
        });
        fileContents.push(uploaded);
      }
      setClubData(prev => ({
        ...prev,
        studentFiles: [...prev.studentFiles, ...filesArray],
        studentFilesContent: [...prev.studentFilesContent, ...fileContents],
        studentFilesStats: [...(prev.studentFilesStats || []), ...filesStats]
      }));
      showToast(`${filesArray.length}개 파일을 분석할 준비가 됐어요.`, 'success');
    } catch (error) {
      console.error('파일 변환 오류:', error);
      showToast('파일을 처리할 수 없었어요. 다른 형식으로 다시 시도해주세요.', 'error');
    } finally {
      setHighSchoolUploadProgress(null);
    }
  }, [auth.currentUser, showToast]);
  
  // 🔧 ref로 최신 함수 참조 유지
  const actualClubStudentFileUploadRef = useRef(actualClubStudentFileUpload);
  actualClubStudentFileUploadRef.current = actualClubStudentFileUpload;
  
  // 🔍 동아리 학생 활동 파일 사전 검사 (교과세특과 동일한 방식)
  const performClubStudentFilePreCheck = useCallback(async (files) => {
    const filesArray = Array.isArray(files) ? files : Array.from(files);
    
    // Storage 경로 전송이라 요청 본문 제한은 피하고, 사용자-facing 합계 정책만 적용한다.
    const combinedFiles = [
      ...(clubData.planFile ? [clubData.planFile] : []),
      ...(clubData.studentFiles || []),
      ...filesArray,
    ];
    const totalSize = combinedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
    const MAX_TOTAL_SIZE = SAENGGIBU_STORAGE_FILE_TOTAL_LIMIT_BYTES;
    
    if (totalSize > MAX_TOTAL_SIZE) {
      setPreCheckResult(buildStorageFileTotalLimitResult('동아리', combinedFiles, totalSize, MAX_TOTAL_SIZE));
      setPendingFiles(null);
      setPreCheckLoading(false);
      setShowPreCheckModal(true);
      return;
    }

    if (filesArray.some(file => file.size > 25 * 1024 * 1024)) {
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      actualClubStudentFileUploadRef.current(filesArray, []);
      return;
    }
    
    setPreCheckLoading(true);
    setShowPreCheckModal(true);
    
    // 📎 한도 (Free/Pro 공통 200k자) - constants/fileLimits.js
    const MAX_FILES = MAX_FILES_DEFAULT;
    const { contentLength: MAX_CONTENT_LENGTH } = getFileLimits(isProUser);
    
    // 이미 첨부된 파일 정보 합계
    const currentStats = {
      fileCount: clubData.studentFiles?.length || 0,
      ...summarizeFileStats(clubData.studentFilesStats || []),
    };
    
    // 파일 수 초과 체크
    const totalFileCount = currentStats.fileCount + filesArray.length;
    if (totalFileCount > MAX_FILES) {
      const availableSlots = Math.max(0, MAX_FILES - currentStats.fileCount);
      setPreCheckResult({
        can_proceed: false,
        needs_confirmation: false,
        overall_status: 'error',
        total_files: filesArray.length,
        total_size: filesArray.reduce((sum, f) => sum + f.size, 0),
        total_size_display: `${(filesArray.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(1)}MB`,
        files_info: filesArray.map(f => ({
          filename: f.name,
          status: 'ok',
          file_size: f.size,
          file_size_display: `${(f.size / 1024).toFixed(1)}KB`,
          issues: []
        })),
        recommendations: availableSlots > 0 
          ? [`현재 ${availableSlots}개의 파일만 추가로 올릴 수 있어요.`]
          : ['기존 파일을 삭제한 후 다시 시도해주세요.'],
        file_count_error: `파일은 최대 ${MAX_FILES}개까지 올릴 수 있어요. (현재 ${currentStats.fileCount}개 첨부됨, ${filesArray.length}개 추가 시도)`
      });
      setPendingFiles(null);
      setPreCheckLoading(false);
      return;
    }
    
    try {
      const result = await preCheckFiles(filesArray, 'saenggibu_storage', isProUser);
      
      // 새 파일들의 페이지/슬라이드/문자수 합계
      const newFileStats = normalizePreCheckStats(result.files_info || []);
      const newStats = summarizeFileStats(newFileStats);
      
      // 합산 제한 체크
      const totalPages = currentStats.pageCount + newStats.pageCount;
      const totalSlides = currentStats.slideCount + newStats.slideCount;
      const totalContentLength = currentStats.contentLength + newStats.contentLength;
      
      const exceededLimits = [];
      
      if (totalPages > MAX_SAENGGIBU_STORAGE_PDF_PAGES) {
        exceededLimits.push(`PDF 페이지: 현재 ${currentStats.pageCount}페이지 + 새 파일 ${newStats.pageCount}페이지 = 총 ${totalPages}페이지 (최대 ${MAX_SAENGGIBU_STORAGE_PDF_PAGES}페이지)`);
      }
      
      if (totalSlides > MAX_SAENGGIBU_STORAGE_PPT_SLIDES) {
        exceededLimits.push(`PPT 슬라이드: 현재 ${currentStats.slideCount}개 + 새 파일 ${newStats.slideCount}개 = 총 ${totalSlides}개 (최대 ${MAX_SAENGGIBU_STORAGE_PPT_SLIDES}개)`);
      }
      
      if (totalContentLength > MAX_CONTENT_LENGTH) {
        exceededLimits.push(`총 분석량: 현재 ${currentStats.contentLength.toLocaleString()}자 + 새 파일 ${newStats.contentLength.toLocaleString()}자 = 총 ${totalContentLength.toLocaleString()}자 (최대 ${MAX_CONTENT_LENGTH.toLocaleString()}자)`);
      }
      
      // 합산 초과 시 경고 추가
      if (exceededLimits.length > 0) {
        const modifiedResult = {
          ...result,
          needs_confirmation: true,
          overall_status: result.overall_status === 'ok' ? 'warning' : result.overall_status,
          recommendations: [
            ...(result.recommendations || []),
            ...exceededLimits.map(limit => limit)
          ],
          combined_warning: `이미 첨부된 파일과 합쳐서 제한을 넘어요. 초과분은 분석에서 빠져요.`,
          _isClubStudentFile: true  // 동아리 학생 파일 표시
        };
        
        modifiedResult._newFilesStats = newFileStats;
        
        setPreCheckResult(modifiedResult);
        setPendingFiles(filesArray);
        setPreCheckLoading(false);
        return;
      }
      
      // 결과에 새 파일 통계 저장
      result._newFilesStats = newFileStats;
      result._isClubStudentFile = true;  // 동아리 학생 파일 표시
      
      // 검사 통과 & 확인 불필요 시 모달 없이 바로 업로드 진행
      if (result.can_proceed && !result.needs_confirmation) {
        setShowPreCheckModal(false);
        setPreCheckLoading(false);
        // 사전 검사가 건너뛰어졌으면 토스트 표시
        if (result.message === '파일 검사를 건너뛰고 진행합니다.') {
          showToast('파일이 너무 크거나 많아 사전 검사를 건너뛰어요. 파일을 첨부할게요...', 'info');
        }
        actualClubStudentFileUploadRef.current(filesArray, result._newFilesStats);
        return;
      }
      
      // 확인이 필요하거나 진행 불가인 경우 모달 표시
      setPreCheckResult(result);
      setPendingFiles(filesArray);
      setPreCheckLoading(false);
      
    } catch (error) {
      console.error('동아리 학생 파일 사전 검사 오류:', error);
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      // 오류 시 토스트 표시 후 업로드 진행
      showToast('파일 검사를 건너뛰고 바로 첨부할게요.', 'warning');
      actualClubStudentFileUploadRef.current(filesArray, []);
    }
  }, [clubData.planFile, clubData.studentFiles, clubData.studentFilesStats, showToast, isProUser]);

  // 🆕 동아리 학생 활동 파일 업로드 핸들러 (다중) - 사전 검사 포함
  const handleClubStudentFilesUpload = useCallback((files) => {
    if (!auth.currentUser) {
      setShowLoginPrompt(true);
      return;
    }
    if (!isProUser) {
      setShowProModal(true);
      return;
    }
    
    const filesArray = Array.isArray(files) ? files : Array.from(files);
    
    const maxFileSize = HIGH_SCHOOL_STORAGE_FILE_SIZE_BYTES;
    const oversizedFiles = filesArray.filter(file => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      const mb = Math.round(maxFileSize / (1024 * 1024));
      showToast(`각 파일은 ${mb}MB 이하여야 해요.`, 'error');
      return;
    }
    
    // 파일 사전 검사 수행
    performClubStudentFilePreCheck(filesArray);
  }, [auth.currentUser, isProUser, showToast, performClubStudentFilePreCheck]);
  
  // 🆕 동아리 학생 활동 파일 제거
  const removeClubStudentFile = (index) => {
    setClubData(prev => ({
      ...prev,
      studentFiles: prev.studentFiles.filter((_, i) => i !== index),
      studentFilesContent: prev.studentFilesContent.filter((_, i) => i !== index),
      studentFilesStats: (prev.studentFilesStats || []).filter((_, i) => i !== index)
    }));
    showToast('파일을 제거했어요.', 'info');
  };
  
  // 🆕 진로 탭 전용 상태 (동아리와 동일한 구조)
  const [careerData, setCareerData] = useState({
    studentActivity: '',        // 학생 진로 탐구 내용 (텍스트)
    studentFiles: [],           // 학생 진로 파일들 (다중 - 진로 탐구 보고서, 계획서 등)
    studentFilesContent: [],    // 파일 내용들 (base64)
    studentFilesStats: [],      // 파일 통계
    careerInfo: '',             // 희망 진로/학과 정보 (텍스트)
    infoFile: null,             // 학생 정보 파일 (단일, AI 분석)
    infoFileContent: null       // 파일 내용 (base64)
  });
  
  // 🆕 진로 학생 파일: 실제 업로드 핸들러 (사전 검사 통과 후 호출)
  const actualCareerStudentFileUpload = useCallback(async (files, filesStats = []) => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      setShowLoginPrompt(true);
      return;
    }

    try {
      const filesArray = Array.isArray(files) ? files : Array.from(files);
      setHighSchoolUploadProgress({ current: 0, total: filesArray.length, percent: 0 });
      const fileContents = [];
      for (let index = 0; index < filesArray.length; index += 1) {
        const file = filesArray[index];
        const uploaded = await uploadSaenggibuAttachment(file, userId, (percent) => {
          setHighSchoolUploadProgress({
            current: index + 1,
            total: filesArray.length,
            percent,
          });
        });
        fileContents.push(uploaded);
      }
      setCareerData(prev => ({
        ...prev,
        studentFiles: [...prev.studentFiles, ...filesArray],
        studentFilesContent: [...prev.studentFilesContent, ...fileContents],
        studentFilesStats: [...(prev.studentFilesStats || []), ...filesStats]
      }));
      showToast(`${filesArray.length}개 파일을 분석할 준비가 됐어요.`, 'success');
    } catch (error) {
      console.error('파일 변환 오류:', error);
      showToast('파일을 처리할 수 없었어요. 다른 형식으로 다시 시도해주세요.', 'error');
    } finally {
      setHighSchoolUploadProgress(null);
    }
  }, [auth.currentUser, showToast]);
  
  // 🔧 ref로 최신 함수 참조 유지
  const actualCareerStudentFileUploadRef = useRef(actualCareerStudentFileUpload);
  actualCareerStudentFileUploadRef.current = actualCareerStudentFileUpload;
  
  // 🔍 진로 학생 파일 사전 검사 (동아리와 동일한 방식)
  const performCareerStudentFilePreCheck = useCallback(async (files) => {
    const filesArray = Array.isArray(files) ? files : Array.from(files);
    
    // Storage 경로 전송이라 요청 본문 제한은 피하고, 사용자-facing 합계 정책만 적용한다.
    const combinedFiles = [
      ...(careerData.infoFile ? [careerData.infoFile] : []),
      ...(careerData.studentFiles || []),
      ...filesArray,
    ];
    const totalSize = combinedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
    const MAX_TOTAL_SIZE = SAENGGIBU_STORAGE_FILE_TOTAL_LIMIT_BYTES;
    
    if (totalSize > MAX_TOTAL_SIZE) {
      setPreCheckResult(buildStorageFileTotalLimitResult('진로', combinedFiles, totalSize, MAX_TOTAL_SIZE));
      setPendingFiles(null);
      setPreCheckLoading(false);
      setShowPreCheckModal(true);
      return;
    }

    if (filesArray.some(file => file.size > 25 * 1024 * 1024)) {
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      actualCareerStudentFileUploadRef.current(filesArray, []);
      return;
    }
    
    setPreCheckLoading(true);
    setShowPreCheckModal(true);
    
    // 📎 한도 (Free/Pro 공통 200k자) - constants/fileLimits.js
    const MAX_FILES = MAX_FILES_DEFAULT;
    const { contentLength: MAX_CONTENT_LENGTH } = getFileLimits(isProUser);
    
    // 이미 첨부된 파일 정보 합계
    const currentStats = {
      fileCount: careerData.studentFiles?.length || 0,
      ...summarizeFileStats(careerData.studentFilesStats || []),
    };
    
    // 파일 수 초과 체크
    const totalFileCount = currentStats.fileCount + filesArray.length;
    if (totalFileCount > MAX_FILES) {
      const availableSlots = Math.max(0, MAX_FILES - currentStats.fileCount);
      setPreCheckResult({
        can_proceed: false,
        needs_confirmation: false,
        overall_status: 'error',
        total_files: filesArray.length,
        total_size: filesArray.reduce((sum, f) => sum + f.size, 0),
        total_size_display: `${(filesArray.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(1)}MB`,
        files_info: filesArray.map(f => ({
          filename: f.name,
          status: 'ok',
          file_size: f.size,
          file_size_display: `${(f.size / 1024).toFixed(1)}KB`,
          issues: []
        })),
        recommendations: availableSlots > 0 
          ? [`현재 ${availableSlots}개의 파일만 추가로 올릴 수 있어요.`]
          : ['기존 파일을 삭제한 후 다시 시도해주세요.'],
        file_count_error: `파일은 최대 ${MAX_FILES}개까지 올릴 수 있어요. (현재 ${currentStats.fileCount}개 첨부됨, ${filesArray.length}개 추가 시도)`
      });
      setPendingFiles(null);
      setPreCheckLoading(false);
      return;
    }
    
    try {
      const result = await preCheckFiles(filesArray, 'saenggibu_storage', isProUser);
      
      // 새 파일들의 페이지/슬라이드/문자수 합계
      const newFileStats = normalizePreCheckStats(result.files_info || []);
      const newStats = summarizeFileStats(newFileStats);
      
      // 합산 제한 체크
      const totalPages = currentStats.pageCount + newStats.pageCount;
      const totalSlides = currentStats.slideCount + newStats.slideCount;
      const totalContentLength = currentStats.contentLength + newStats.contentLength;
      
      const exceededLimits = [];
      
      if (totalPages > MAX_SAENGGIBU_STORAGE_PDF_PAGES) {
        exceededLimits.push(`PDF 페이지: 현재 ${currentStats.pageCount}페이지 + 새 파일 ${newStats.pageCount}페이지 = 총 ${totalPages}페이지 (최대 ${MAX_SAENGGIBU_STORAGE_PDF_PAGES}페이지)`);
      }
      
      if (totalSlides > MAX_SAENGGIBU_STORAGE_PPT_SLIDES) {
        exceededLimits.push(`PPT 슬라이드: 현재 ${currentStats.slideCount}개 + 새 파일 ${newStats.slideCount}개 = 총 ${totalSlides}개 (최대 ${MAX_SAENGGIBU_STORAGE_PPT_SLIDES}개)`);
      }
      
      if (totalContentLength > MAX_CONTENT_LENGTH) {
        exceededLimits.push(`총 분석량: 현재 ${currentStats.contentLength.toLocaleString()}자 + 새 파일 ${newStats.contentLength.toLocaleString()}자 = 총 ${totalContentLength.toLocaleString()}자 (최대 ${MAX_CONTENT_LENGTH.toLocaleString()}자)`);
      }
      
      // 합산 초과 시 경고 추가
      if (exceededLimits.length > 0) {
        const modifiedResult = {
          ...result,
          needs_confirmation: true,
          overall_status: result.overall_status === 'ok' ? 'warning' : result.overall_status,
          recommendations: [
            ...(result.recommendations || []),
            ...exceededLimits.map(limit => limit)
          ],
          combined_warning: `이미 첨부된 파일과 합쳐서 제한을 넘어요. 초과분은 분석에서 빠져요.`,
          _isCareerStudentFile: true  // 진로 학생 파일 표시
        };
        
        modifiedResult._newFilesStats = newFileStats;
        
        setPreCheckResult(modifiedResult);
        setPendingFiles(filesArray);
        setPreCheckLoading(false);
        return;
      }
      
      // 결과에 새 파일 통계 저장
      result._newFilesStats = newFileStats;
      result._isCareerStudentFile = true;  // 진로 학생 파일 표시
      
      // 검사 통과 & 확인 불필요 시 모달 없이 바로 업로드 진행
      if (result.can_proceed && !result.needs_confirmation) {
        setShowPreCheckModal(false);
        setPreCheckLoading(false);
        if (result.message === '파일 검사를 건너뛰고 진행합니다.') {
          showToast('파일이 너무 크거나 많아 사전 검사를 건너뛰어요. 파일을 첨부할게요...', 'info');
        }
        actualCareerStudentFileUploadRef.current(filesArray, result._newFilesStats);
        return;
      }
      
      // 확인이 필요하거나 진행 불가인 경우 모달 표시
      setPreCheckResult(result);
      setPendingFiles(filesArray);
      setPreCheckLoading(false);
      
    } catch (error) {
      console.error('진로 학생 파일 사전 검사 오류:', error);
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      showToast('파일 검사를 건너뛰고 바로 첨부할게요.', 'warning');
      actualCareerStudentFileUploadRef.current(filesArray, []);
    }
  }, [careerData.infoFile, careerData.studentFiles, careerData.studentFilesStats, showToast, isProUser]);

  // 🆕 진로 학생 파일 업로드 핸들러 (다중) - 사전 검사 포함
  const handleCareerStudentFilesUpload = useCallback((files) => {
    if (!auth.currentUser) {
      setShowLoginPrompt(true);
      return;
    }
    if (!isProUser) {
      setShowProModal(true);
      return;
    }
    
    const filesArray = Array.isArray(files) ? files : Array.from(files);
    
    const maxFileSize = HIGH_SCHOOL_STORAGE_FILE_SIZE_BYTES;
    const oversizedFiles = filesArray.filter(file => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      const mb = Math.round(maxFileSize / (1024 * 1024));
      showToast(`각 파일은 ${mb}MB 이하여야 해요.`, 'error');
      return;
    }
    
    // 파일 사전 검사 수행
    performCareerStudentFilePreCheck(filesArray);
  }, [auth.currentUser, isProUser, showToast, performCareerStudentFilePreCheck]);
  
  // 🆕 진로 학생 파일 제거
  const removeCareerStudentFile = (index) => {
    setCareerData(prev => ({
      ...prev,
      studentFiles: prev.studentFiles.filter((_, i) => i !== index),
      studentFilesContent: prev.studentFilesContent.filter((_, i) => i !== index),
      studentFilesStats: (prev.studentFilesStats || []).filter((_, i) => i !== index)
    }));
    showToast('파일을 제거했어요.', 'info');
  };
  
  // 생성 상태
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCancellingGeneration, setIsCancellingGeneration] = useState(false);
  const [activeGenerationJobIds, setActiveGenerationJobIds] = useState([]);
  const activeGenerationJobIdsRef = useRef([]);
  const generationCancelRequestedRef = useRef(false);
  const [generationResult, setGenerationResult] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamProgressMessage, setStreamProgressMessage] = useState('');
  const [copyStatus, setCopyStatus] = useState({}); // 복사 상태 - 각 버전별로 관리
  const [copiedHistory, setCopiedHistory] = useState(new Set()); // 🆕 복사 이력 추적 (한번이라도 복사한 버전)
  const [manuallyEditedKeys, setManuallyEditedKeys] = useState(() => new Set());
  const [localError, setLocalError] = useState(''); // 생성 버튼 근처 로컬 에러
  const [expandedCategories, setExpandedCategories] = useState({}); // 카테고리 펼침 상태
  const [savedRecordId, setSavedRecordId] = useState(null);
  // 명단 학생 연결: { rosterId, rosterLabel, sid, name, recordId(담은 기록, 담기 전이면 null), versionIndex, auto }
  // 학생 화면 '이 학생 생기부 쓰기'로 들어오면 그 학생을 골라 둔 채로 시작한다(메모는 입력칸에 이미 들어 있다).
  // auto: 그렇게 들어온 학생만 기본 글로 자동으로 담고, 다른 글을 복사하면 그 글로 옮긴다.
  const [rosterLink, setRosterLink] = useState(() => {
    const entry = initialState?.rosterStudent;
    return entry?.rosterId && entry?.sid
      ? { rosterId: entry.rosterId, rosterLabel: entry.rosterLabel || '', sid: entry.sid, name: entry.name || '', memoCount: entry.memoCount || 0, recordId: null, versionIndex: 0, auto: true }
      : null;
  });
  const memoPromptRef = useRef(initialState?.rosterStudent ? String(initialState?.prompt || '').trim() : '');
  const [memoSavedText, setMemoSavedText] = useState('');
  // 담기 창을 연 결과 카드: { versionIndex }
  const [rosterPickTarget, setRosterPickTarget] = useState(null);
  const [unpersistedResultKeys, setUnpersistedResultKeys] = useState(() => new Set());
  // 이번 결과에 누적 반영된 재생성 요청 체인 (서버 saved 이벤트가 기준)
  const [regenerationChain, setRegenerationChain] = useState([]);
  // 선생님이 목록을 고치거나 지웠으면 다음 다시 만들기에 이 목록을 그대로 보낸다(서버 기록 계보 대신).
  const regenerationChainEditedRef = useRef(false);
  const [versionNumbers, setVersionNumbers] = useState([]);
  const [refinePanelKey, setRefinePanelKey] = useState(null);
  const [inlineSimilar, setInlineSimilar] = useState(null);
  const [refineMode, setRefineMode] = useState('tone_up');
  const [refinePresetId, setRefinePresetId] = useState('tone_up');
  const [refineInstruction, setRefineInstruction] = useState('');
  const [refineLoadingKey, setRefineLoadingKey] = useState(null);
  const [refinePreview, setRefinePreview] = useState(null);
  const [refinePreviewDraft, setRefinePreviewDraft] = useState('');
  const [refinePreviewEditingKey, setRefinePreviewEditingKey] = useState(null);
  const [refineUndo, setRefineUndo] = useState(null);
  const [manualEditKey, setManualEditKey] = useState(null);
  const [manualEditDraft, setManualEditDraft] = useState('');
  const [manualEditSavingKey, setManualEditSavingKey] = useState(null);
  const [regenerateInstruction, setRegenerateInstruction] = useState('');
  const [regeneratePresetId, setRegeneratePresetId] = useState(null);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showByteGuide, setShowByteGuide] = useState(false);
  const [showCategoryConfirm, setShowCategoryConfirm] = useState(null);
  const [settingsRevealed, setSettingsRevealed] = useState(false);
  const [showSaveCTA, setShowSaveCTA] = useState(false);
  const [similarPrefill, setSimilarPrefill] = useState(
    initialState?.category === '비슷한문장' ? (initialState.prompt || '') : ''
  );
  const [similarContext, setSimilarContext] = useState({
    categoryHint: initialState?.categoryHint || '',
    targetBytes: initialState?.targetBytes || '',
  });
  const [anonGenCount, setAnonGenCount] = useState(() => {
    return parseInt(sessionStorage.getItem('sgb-anon-gen-count') || '0');
  });
  const [loginPromptFeature, setLoginPromptFeature] = useState('saenggibu');

  // 사용자 상태 확인
  const isAnonymousUser = !auth.currentUser;
  const classRosters = useClassRosters(auth.currentUser?.uid || '');
  const linkRecordRef = useRef(null);
  const autoLinkingRef = useRef('');
  const shouldShowGenerationSettings = selectedCategory !== '비슷한문장' && (
    selectedCategory === '교과' || (!isAnonymousUser && (
    selectedCategory !== '행동발달' || settingsRevealed
  )));

  // 엑셀 일괄 모드 전용 상태
  const [batchStep, setBatchStep] = useState(1);
  const [batchData, setBatchData] = useState(null);
  const previousBatchResetTokenRef = useRef(batchResetToken);
  const isBatchResetPending = previousBatchResetTokenRef.current !== batchResetToken;
  const effectiveBatchStep = isBatchResetPending ? 1 : batchStep;

  useEffect(() => {
    if (!isBatchResetPending) return;
    previousBatchResetTokenRef.current = batchResetToken;
    setBatchStep(1);
    setBatchData(null);
    setActiveInitialBatchState(null);
  }, [batchResetToken, isBatchResetPending]);

  // 파일 업로드 관련 상태
  const [fileUpload, setFileUpload] = useState({
    file: null,
    files: [],
    uploading: false,
    error: null,
    analysis: null,
    students: [],
    selectedStudents: [],
    selectedItems: [],
    appliedEvalUnitCount: 0,
    planMode: 'merge',
    // 다중 학생 파싱 관련
    multiStudent: false,
    parsedStudents: [],  // [{id, name, records, traits, individual_traits}]
    selectedStudent: null,  // 현재 선택된 학생
    pendingReview: null,
    reviewConfirmed: false
  });

  const [analysisReview, setAnalysisReview] = useState({
    isOpen: false,
    review: null
  });

  const getCategoryLabel = useCallback((category) => {
    const labels = {
      '행동발달': '행동발달',
      '교과': '교과/세특',
      '교과_고등': '교과세특 고등',
      '창체': '창체 자율',
      '동아리': '동아리',
      '진로': '진로',
      '비슷한문장': '비슷한 문장'
    };
    return labels[category] || category || '생기부';
  }, []);
  const hasVisibleResult = hasSaenggibuResult({
    generationResult,
    versions,
    multiClassResults,
  });
  const resultCategory = resultContext?.category || selectedCategory;

  // 학생 화면에서 골라 들어온 학생은 기록이 저장되면 담는다. 다시 만든 기록은 서버가 원본을 따라 같은 학생에 묶는다.
  useEffect(() => {
    if (!rosterLink || !savedRecordId || isStreaming || isGenerating) return;
    if (rosterLink.recordId === savedRecordId || autoLinkingRef.current === savedRecordId) return;
    if (rosterLink.recordId == null) {
      autoLinkingRef.current = savedRecordId;
      linkRecordRef.current?.(rosterLink, savedRecordId, 0, 'before');
      return;
    }
    setRosterLink((prev) => (prev ? { ...prev, recordId: savedRecordId, versionIndex: 0 } : prev));
  }, [isGenerating, isStreaming, rosterLink, savedRecordId]);

  const suggestionApi = useSaenggibuSuggestion({
    recordId: savedRecordId,
    category: resultCategory,
    isBatch: false,
    enabled: Boolean(savedRecordId) && !isGenerating && !isStreaming && !isAnonymousUser,
  });
  const resultModel = resultContext?.selectedModel || generationData.selectedModel;
  const resultTargetBytes = resultContext?.targetBytes || generationData.wordCount;
  const evidenceEntries = useMemo(() => {
    if (multiClassResults.length > 0) {
      let flat = 0;
      const rows = [];
      multiClassResults.forEach((classResult, classIndex) => {
        (classResult.versions || []).forEach((content, versionIndex) => {
          if (!content) return;
          rows.push({ key: `c${classIndex}v${versionIndex}`, refineKey: `version-${flat}`, content });
          flat += 1;
        });
      });
      return rows;
    }
    if (versions.length > 1) {
      return versions.map((content, index) => (content
        ? { key: `v${versionNumbers[index] || index + 1}`, refineKey: `version-${index}`, content }
        : null)).filter(Boolean);
    }
    return generationResult ? [{ key: `v${versionNumbers[0] || 1}`, refineKey: 'main', content: generationResult }] : [];
  }, [multiClassResults, versions, versionNumbers, generationResult]);
  const evidenceApi = useSaenggibuEvidence({
    recordId: savedRecordId,
    entries: evidenceEntries,
    enabled: Boolean(savedRecordId) && !isGenerating && !isStreaming && !isAnonymousUser
      && !multiClassResults.some((result) => result.status === 'generating'),
  });
  const [showEvidenceNumbers, setShowEvidenceNumbers] = useShowEvidenceNumbers();
  const [evidenceDialog, setEvidenceDialog] = useState(null);
  const evidenceEntryFor = (refineKey) => evidenceEntries.find((entry) => entry.refineKey === refineKey) || null;
  const resultActionItems = collectNormalSaenggibuResults({
    generationResult,
    versions,
    versionLevels,
    multiClassResults,
    resultCategory,
  });
  const clearGeneratedResultState = useCallback(() => {
    setGenerationResult('');
    setVersions([]);
    setVersionLevels([]);
    setVersionNumbers([]);
    setResultContext(null);
    setStreamProgressMessage('');
    setCopyStatus({});
    setCopiedHistory(new Set());
    setManuallyEditedKeys(new Set());
    setLocalError('');
    onError?.(null);
    setShowSaveCTA(false);
    setSavedRecordId(null);
    setUnpersistedResultKeys(new Set());
    setRefinePanelKey(null);
    setInlineSimilar(null);
    setRefineMode('tone_up');
    setRefinePresetId('tone_up');
    setRefineInstruction('');
    setRefinePreview(null);
    setRefinePreviewDraft('');
    setRefinePreviewEditingKey(null);
    setRefineUndo(null);
    setManualEditKey(null);
    setManualEditDraft('');
    setManualEditSavingKey(null);
    setRegenerateInstruction('');
    setRegeneratePresetId(null);
    setRegenerationChain([]);
    regenerationChainEditedRef.current = false;
    setMultiClassResults([]);
    setActiveClassResultId('class-1');
    setMultiClassProgress({ current: 0, total: 0, message: '' });
  }, [onError]);

  const getAnalysisWarnings = useCallback((result, analyzedData) => {
    const warnings = [];
    if (result?.warning) warnings.push(result.warning);
    if (Array.isArray(result?.warnings)) warnings.push(...result.warnings);
    if (analyzedData?.confidence === 'low') {
      warnings.push('파일에서 정보를 읽기 어려워요. 내용을 꼭 확인해주세요.');
    }
    if (analyzedData?.error) warnings.push(analyzedData.error);
    return [...new Set(warnings.filter(Boolean))];
  }, []);

  const buildSubjectActivityDraft = useCallback((data = {}) => {
    const activityContentLines = [];
    appendDraftLine(activityContentLines, '단원', data.unit);
    appendDraftLine(activityContentLines, '영역', data.domain);
    appendDraftLine(activityContentLines, '평가방법', pickAnalysisText(data, ['evaluation_method', 'method', 'assessment_method']));
    appendDraftLine(activityContentLines, '평가요소', pickAnalysisText(data, ['evaluation_items', 'evaluation_elements', 'assessment_elements', 'evaluation_item']));
    appendDraftLine(activityContentLines, '평가기준', pickAnalysisText(data, ['evaluation_criteria', 'criteria', 'rubric', 'scoring_criteria', 'scoring_levels']));
    appendDraftLine(activityContentLines, '평가내용', pickAnalysisText(data, ['evaluation_content', 'assessment_content', 'task', 'task_description']));
    appendDraftLine(activityContentLines, '수업-평가 연계', data.class_evaluation_link);
    appendDraftLine(activityContentLines, '평가시기', data.evaluation_period);

    const activityRecords = pickAnalysisText(data, ['activity_content', 'records']);
    if (activityRecords) activityContentLines.push(activityRecords);

    const activityContent = activityContentLines.join('\n').trim();
    return {
      activityName: buildSubjectActivityNameText(data),
      activityContent: activityContent || activityRecords
    };
  }, []);

  const buildHighSchoolDraft = useCallback((data = {}) => {
    let achievementText = '';
    if (data.unit) achievementText += `[단원] ${data.unit}\n`;
    if (data.domain) achievementText += `[영역] ${data.domain}\n`;
    if (data.achievement_standard) achievementText += `[성취기준] ${data.achievement_standard}\n`;
    if (data.evaluation_method) achievementText += `[평가방법] ${data.evaluation_method}\n`;
    if (data.evaluation_criteria) achievementText += `[평가기준] ${data.evaluation_criteria}\n`;
    if (data.evaluation_content) achievementText += `[평가내용] ${data.evaluation_content}\n`;
    if (data.class_evaluation_link) achievementText += `[수업-평가 연계] ${data.class_evaluation_link}\n`;
    if (data.evaluation_ratio) achievementText += `[평가비율] ${data.evaluation_ratio}\n`;
    if (data.evaluation_period) achievementText += `[평가시기] ${data.evaluation_period}\n`;
    if (data.records && !achievementText.includes(data.records)) {
      achievementText += `\n${data.records}`;
    }
    return achievementText.trim() || data.achievement_standard || '';
  }, []);

  const buildClubDraft = useCallback((data = {}) => {
    let clubDescription = '';
    if (data.club_name) clubDescription += `[동아리명] ${data.club_name}\n`;
    if (data.club_type) clubDescription += `[유형] ${data.club_type}\n`;
    if (data.advisor) clubDescription += `[지도교사] ${data.advisor}\n`;
    if (data.meeting_schedule) clubDescription += `[활동 시간] ${data.meeting_schedule}\n`;
    if (data.objectives) clubDescription += `[목표] ${data.objectives}\n`;
    if (Array.isArray(data.main_activities)) {
      clubDescription += `[주요활동] ${data.main_activities.join(', ')}\n`;
    }
    if (Array.isArray(data.yearly_plan)) {
      clubDescription += '\n[연간 계획]\n';
      data.yearly_plan.forEach(plan => {
        if (plan.month && plan.activities) {
          const activities = Array.isArray(plan.activities) ? plan.activities.join(', ') : plan.activities;
          clubDescription += `${plan.month}: ${activities}\n`;
        }
      });
    }
    if (data.summary) clubDescription += `\n${data.summary}`;
    return clubDescription.trim();
  }, []);

  const buildCareerDraft = useCallback((data = {}) => {
    let careerInfo = '';
    if (data.hoped_major) careerInfo += `[희망 학과] ${data.hoped_major}\n`;
    if (data.hoped_career) careerInfo += `[희망 진로] ${data.hoped_career}\n`;
    if (data.strengths) {
      careerInfo += `[강점/특성] ${Array.isArray(data.strengths) ? data.strengths.join(', ') : data.strengths}\n`;
    }
    if (data.interests) {
      careerInfo += `[관심 분야] ${Array.isArray(data.interests) ? data.interests.join(', ') : data.interests}\n`;
    }
    if (data.career_plans) {
      careerInfo += `[진로 계획] ${Array.isArray(data.career_plans) ? data.career_plans.join(', ') : data.career_plans}\n`;
    }
    if (data.experiences) {
      careerInfo += `[경험/활동] ${Array.isArray(data.experiences) ? data.experiences.join(', ') : data.experiences}\n`;
    }
    if (data.career_activities) {
      careerInfo += `[진로 탐색 활동] ${Array.isArray(data.career_activities) ? data.career_activities.join(', ') : data.career_activities}\n`;
    }
    if (data.summary) careerInfo += `\n${data.summary}`;
    return careerInfo.trim();
  }, []);

  const openAnalysisReview = useCallback((review) => {
    const normalizedReview = {
      ...review,
      categoryLabel: review.categoryLabel || getCategoryLabel(review.target?.category || selectedCategory),
      startEditing: review.startEditing || review.confidence === 'low'
    };
    if (review.source === 'fileUpload') {
      setFileUpload(prev => ({
        ...prev,
        pendingReview: normalizedReview,
        reviewConfirmed: false
      }));
    }
    setAnalysisReview({
      isOpen: true,
      review: normalizedReview
    });
  }, [getCategoryLabel, selectedCategory]);

  const resetAnalysisReview = useCallback(() => {
    setAnalysisReview({
      isOpen: false,
      review: null
    });
  }, []);

  const handleAnalysisReviewConfirm = useCallback((review) => {
    const target = review.target || {};
    const fieldValues = review?.fieldValues || {};
    const text = ((fieldValues.text ?? review?.text) || '').trim();
    const subjectActivityName = ((fieldValues.activityName ?? target.activityName) || '').trim();
    const hasSubjectActivityName = target.type === 'subject' && Boolean(subjectActivityName);
    if (!text && !hasSubjectActivityName) {
      showToast('확인할 내용이 없어요.', 'warning');
      return;
    }

    if (target.type === 'creativePrompt') {
      const previousCreativeDraft = appliedCreativeFileDraftRef.current;
      setGenerationData(prev => ({
        ...prev,
        prompt: mergeCreativeAnalysisWithPrompt(prev.prompt, text, previousCreativeDraft)
      }));
      appliedCreativeFileDraftRef.current = text;
    } else if (target.type === 'prompt') {
      setGenerationData(prev => ({ ...prev, prompt: text }));
    } else if (target.type === 'subject') {
      if (target.uiMode === 'quick') {
        setSubjectData(prev => ({
          ...prev,
          activityName: subjectActivityName,
          activityContent: text
        }));
        if (target.evalUnitCount) {
          setFileUpload(prev => ({ ...prev, appliedEvalUnitCount: target.evalUnitCount }));
        }
      } else {
        const subjectPrefix = target.subject ? `[과목] ${target.subject}\n` : '';
        setGenerationData(prev => ({
          ...prev,
          prompt: `${subjectPrefix}[활동명]\n${subjectActivityName}\n\n[활동내용]\n${text}`.trim()
        }));
      }
    } else if (target.type === 'highSchool') {
      setHighSchoolData(prev => ({ ...prev, achievementStandard: text }));
      if (target.evalUnitCount) {
        setFileUpload(prev => ({ ...prev, appliedEvalUnitCount: target.evalUnitCount }));
      }
    } else if (target.type === 'club') {
      setClubData(prev => ({
        ...prev,
        clubDescription: text,
        planFile: target.planFile || prev.planFile,
        planFileContent: target.planFileContent || prev.planFileContent
      }));
    } else if (target.type === 'career') {
      setCareerData(prev => ({
        ...prev,
        careerInfo: text,
        infoFile: target.infoFile || prev.infoFile,
        infoFileContent: target.infoFileContent || prev.infoFileContent
      }));
    }

    setFileUpload(prev => ({
      ...prev,
      pendingReview: null,
      reviewConfirmed: true
    }));
    resetAnalysisReview();
    showToast(review.textEdited ? '수정한 내용을 입력했어요.' : '분석한 내용을 입력했어요.', 'success');
  }, [resetAnalysisReview, showToast]);
  
  // 🔍 파일 사전 검사 관련 상태
  const [showPreCheckModal, setShowPreCheckModal] = useState(false);
  const [preCheckResult, setPreCheckResult] = useState(null);
  const [pendingFile, setPendingFile] = useState(null); // 단일 파일
  const [pendingFiles, setPendingFiles] = useState(null); // 고등 모드 다중 파일
  const [preCheckLoading, setPreCheckLoading] = useState(false); // 사전 검사 로딩 상태
  
  // 슬라이더 filled track 헬퍼 (토스 스타일 - 부드러운 그라데이션)
  const getSliderBg = (value, min, max) => {
    const percent = ((value - min) / (max - min)) * 100;
    return `linear-gradient(to right, #3182F6 0%, #3182F6 ${percent}%, #E5E7EB ${percent}%, #E5E7EB 100%)`;
  };

  const getFileReviewStatus = useCallback((multiLabel = '항목') => {
    if (fileUpload.uploading) return { label: '분석 중...', tone: 'loading' };
    if (fileUpload.multiStudent && !fileUpload.selectedStudent && !fileUpload.appliedEvalUnitCount) {
      return { label: `${multiLabel} ${fileUpload.parsedStudents.length}개`, tone: 'select' };
    }
    if (fileUpload.pendingReview) return { label: '확인 필요', tone: 'review' };
    if (fileUpload.reviewConfirmed) return { label: '확정 완료', tone: 'done' };
    return { label: '분석됨', tone: 'done' };
  }, [fileUpload.appliedEvalUnitCount, fileUpload.multiStudent, fileUpload.parsedStudents.length, fileUpload.pendingReview, fileUpload.reviewConfirmed, fileUpload.selectedStudent, fileUpload.uploading]);

  const getFileReviewBadgeColor = useCallback((status) => {
    if (status.tone === 'review') return 'var(--whatquiz-warning)';
    if (status.tone === 'select' || status.tone === 'loading') return 'var(--whatquiz-primary)';
    return 'var(--whatquiz-success)';
  }, []);

  // 🆕 프로그레스바 순환 메시지
  const progressMessages = ['파일을 읽고 있어요', '내용을 분석하고 있어요', '핵심을 정리하고 있어요', '거의 다 됐어요'];
  const [progressMsgIdx, setProgressMsgIdx] = useState(0);
  const [progressMsgFade, setProgressMsgFade] = useState(true); // true = visible
  
  // 🔧 함수 참조를 위한 ref (useCallback 순서 독립성 확보)
  const actualFileUploadRef = useRef();
  const actualHighSchoolFileUploadRef = useRef();
  const openPlanItemsReviewRef = useRef();
  const fileAnalysisRequestRef = useRef({ id: 0, controller: null, category: null });

  const cancelFileAnalysisRequest = useCallback(() => {
    fileAnalysisRequestRef.current.controller?.abort();
    fileAnalysisRequestRef.current = {
      id: fileAnalysisRequestRef.current.id + 1,
      controller: null,
      category: null,
    };
  }, []);

  useEffect(() => () => {
    cancelFileAnalysisRequest();
  }, [cancelFileAnalysisRequest]);

  // 다중에서 나가면 넘겨받았던 학생 목록(단일 파일·기록 복원)은 버린다. 다시 들어올 때 또 채워지지 않게.
  const previousUiModeRef = useRef(uiMode);
  useEffect(() => {
    if (previousUiModeRef.current === 'batch' && uiMode !== 'batch') {
      setActiveInitialBatchState(null);
    }
    previousUiModeRef.current = uiMode;
  }, [uiMode]);

  useEffect(() => {
    if (uiMode !== 'batch') return;
    cancelFileAnalysisRequest();
    resetAnalysisReview();
    setShowPreCheckModal(false);
    setPreCheckResult(null);
    setPendingFile(null);
    setPendingFiles(null);
    setPreCheckLoading(false);
    setFileUpload(prev => (
      prev.uploading
        ? {
            ...prev,
            file: null,
            files: [],
            uploading: false,
            error: null,
            analysis: null,
            selectedItems: [],
            appliedEvalUnitCount: 0,
            multiStudent: false,
            parsedStudents: [],
            selectedStudent: null,
            pendingReview: null,
            reviewConfirmed: false,
          }
        : prev
    ));
  }, [cancelFileAnalysisRequest, resetAnalysisReview, uiMode]);

  useEffect(() => {
    if (shouldClearSaenggibuResultForCategory(resultContext, selectedCategory)) {
      clearGeneratedResultState();
    }
  }, [clearGeneratedResultState, resultContext, selectedCategory]);

  // 카테고리 변경 시 강조할 포인트 초기화 및 생성 개수 조정
  useEffect(() => {
    cancelFileAnalysisRequest();
    if (skipCategoryResetRef.current && selectedCategory === skipCategoryResetRef.current) {
      skipCategoryResetRef.current = null;
      return;
    }

    let defaultWordCount = 800;
    let defaultGenCount = 2;
    
    if (selectedCategory === '교과') {
      defaultGenCount = 10;
    }
    
    setGenerationData(prev => ({
      ...prev,
      prompt: '',
      emphasizePoints: [],
      generationCount: defaultGenCount,
      wordCount: defaultWordCount
    }));
    setExpandedCategories({});
    setSettingsRevealed(false);
    setCreativeInputProfile('activity_list');
    
    // 교과 카테고리용 입력 필드도 초기화
    setSubjectData({
      activityName: '',
      activityContent: '',
      attachedFile: null
    });
    appliedCreativeFileDraftRef.current = '';
    restoredUploadedFilesMetaRef.current = [];
    historySourceRecordIdRef.current = '';
    // 교과 전용: 현재 학교급에 맞는 성취수준별 학생 수 초기화
    setLevelCounts(createDefaultLevelCounts(schoolLevelRef.current));
    
    // 고등 모드 입력 필드 초기화
    setHighSchoolData({
      achievementStandard: '',
      growthPoints: '',
      studentActivity: '',
      files: [],
      filesContent: [],
      filesStats: []
    });
    setHighSchoolUploadProgress(null);
    setSelectedStandards([]);
    setIsAchievementPickerOpen(false);
    
    // 파일 업로드 상태 초기화
    setFileUpload({
      file: null,
      uploading: false,
      error: null,
      analysis: null,
      students: [],
      selectedStudents: [],
      selectedItems: [],
      appliedEvalUnitCount: 0,
      multiStudent: false,
      parsedStudents: [],
      selectedStudent: null,
      pendingReview: null,
      reviewConfirmed: false
    });
    setShowPreCheckModal(false);
    setPreCheckResult(null);
    setPendingFile(null);
    setPendingFiles(null);
    setPreCheckLoading(false);
    resetAnalysisReview();
    // 🆕 동아리/진로 전용 상태 초기화
    setClubData({
      planFile: null,
      planFileContent: null,
      clubDescription: '',
      studentActivity: '',
      studentFiles: [],
      studentFilesContent: [],
      studentFilesStats: []
    });
    setCareerData({
      studentActivity: '',
      studentFiles: [],
      studentFilesContent: [],
      studentFilesStats: [],
      careerInfo: '',
      infoFile: null,
      infoFileContent: null
    });
  }, [cancelFileAnalysisRequest, resetAnalysisReview, selectedCategory]);

  // 로그인 전 데이터 저장 함수
  const saveDataToLocalStorage = useCallback(() => {
    const dataToSave = {
      generationData,
      subjectData,
      selectedCategory,
      selectedSubject,
      schoolLevel,
      levelCounts,
      subjectClassCount,
      expandedCategories,
      uiMode,
      currentUrl: window.location.pathname + window.location.search, // 현재 URL 저장
      timestamp: Date.now()
    };
    localStorage.setItem('saenggibu_temp_data', JSON.stringify(dataToSave));
  }, [generationData, subjectData, selectedCategory, selectedSubject, schoolLevel, levelCounts, subjectClassCount, expandedCategories, uiMode]);

  // 로그인 후 데이터 복원 함수
  const restoreDataFromLocalStorage = useCallback(() => {
    try {
      const savedData = localStorage.getItem('saenggibu_temp_data');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        const timeDiff = Date.now() - parsedData.timestamp;
        
        // 30분 이내의 데이터만 복원 (1800000ms = 30분)
        if (timeDiff < 1800000) {
          setGenerationData(parsedData.generationData);
          if (parsedData.subjectData) {
            setSubjectData(parsedData.subjectData);
          }
          setSelectedCategory(parsedData.selectedCategory);
          setSelectedSubject(parsedData.selectedSubject);
          if (parsedData.schoolLevel) {
            setSchoolLevel(parsedData.schoolLevel);
            setLevelCounts(parsedData.levelCounts || createDefaultLevelCounts(parsedData.schoolLevel));
          }
          if (parsedData.subjectClassCount) {
            setSubjectClassCountState(Math.max(1, Math.min(MAX_SUBJECT_CLASSES, Number(parsedData.subjectClassCount) || 1)));
          }
          setExpandedCategories(parsedData.expandedCategories || {});
          if (parsedData.uiMode) {
            setUiMode(parsedData.uiMode);
          }
          
          // 복원 완료 후 임시 데이터 삭제
          localStorage.removeItem('saenggibu_temp_data');
          
          // 사용자에게 데이터 복원 알림
          showToast('이전에 작성하신 내용을 복원했어요.', 'success');
        } else {
          // 오래된 데이터는 삭제
          localStorage.removeItem('saenggibu_temp_data');
        }
      }
    } catch (error) {
      console.error('데이터 복원 중 오류:', error);
      localStorage.removeItem('saenggibu_temp_data');
    }
  }, [showToast, setUiMode]);

  // 🆕 프로그레스바 순환 메시지 타이머
  useEffect(() => {
    if (!fileUpload.uploading) {
      setProgressMsgIdx(0);
      setProgressMsgFade(true);
      return;
    }
    const interval = setInterval(() => {
      // 페이드아웃
      setProgressMsgFade(false);
      setTimeout(() => {
        setProgressMsgIdx(prev => (prev + 1) % progressMessages.length);
        setProgressMsgFade(true);
      }, 300); // 300ms 페이드아웃 후 텍스트 변경 + 페이드인
    }, 3000);
    return () => clearInterval(interval);
  }, [fileUpload.uploading, progressMessages.length]);

  // 로그인 상태 변경 시 데이터 복원
  useEffect(() => {
    if (auth.currentUser) {
      // 로그인 완료 시 저장된 데이터 복원 시도
      const savedData = localStorage.getItem('saenggibu_temp_data');
      if (savedData) {
        setTimeout(() => {
          restoreDataFromLocalStorage();
        }, 300); // 약간의 지연을 주어 상태 안정화
      }
    }
  }, [auth.currentUser, restoreDataFromLocalStorage]);

  // 히스토리에서 "다시 만들기"로 들어온 경우 폼 자동 채우기
  useEffect(() => {
    if (initialState?.refineFromRecord) {
      const restoredVersions = Array.isArray(initialState.generatedVersions)
        ? initialState.generatedVersions
        : [];
      const versionContents = restoredVersions.map(version => version.content || '');
      const firstContent = initialState.generatedContent || versionContents[0] || '';
      const restoredSchoolLevel = initialState.schoolLevel || '중등';

      setSelectedCategory(initialState.category || '행동발달');
      setSchoolLevel(restoredSchoolLevel);
      setLevelCounts(initialState.levelCounts || createDefaultLevelCounts(restoredSchoolLevel));
      setGenerationData(prev => ({
        ...prev,
        prompt: initialState.prompt || '',
        emphasizePoints: initialState.emphasizePoints || [],
        wordCount: initialState.wordCount || prev.wordCount,
        selectedModel: restoreSaenggibuModelMode(initialState.selectedModel, isProUser) || prev.selectedModel
      }));
      setSavedRecordId(initialState.recordId || null);
      setResultContext({
        category: initialState.category || '행동발달',
        schoolLevel: restoredSchoolLevel,
        selectedModel: restoreSaenggibuModelMode(initialState.selectedModel, isProUser) || generationData.selectedModel,
        targetBytes: initialState.wordCount || generationData.wordCount,
        inputSignature: null,
        originalContext: [
          `[카테고리]\n${initialState.category || '행동발달'}`,
          `[강조 포인트]\n${(initialState.emphasizePoints || []).join(', ') || '없음'}`,
          `[입력 내용]\n${initialState.prompt || ''}`,
        ].join('\n\n').slice(0, 30000),
      });
      setGenerationResult(firstContent);
      setVersions(versionContents.length > 0 ? versionContents : (firstContent ? [firstContent] : []));
      setVersionNumbers(restoredVersions.map(version => version.version || 1));
      setVersionLevels(restoredVersions.map(version => version.level || null));
      if (Array.isArray(initialState.multiClassResults) && initialState.multiClassResults.length > 0) {
        setMultiClassResults(initialState.multiClassResults);
        setSubjectClassCountState(Math.max(1, Math.min(MAX_SUBJECT_CLASSES, Number(initialState.subjectClassCount) || initialState.multiClassResults.length)));
        setActiveClassResultId(initialState.activeClassResultId || initialState.multiClassResults[0]?.id || 'class-1');
        setMultiClassProgress({
          current: initialState.multiClassResults.length,
          total: initialState.multiClassResults.length,
          message: `총 ${initialState.multiClassResults.reduce((sum, item) => sum + ((item.versions || []).filter(Boolean).length), 0)}명 결과를 불러왔어요.`,
        });
      } else {
        setMultiClassResults([]);
        setMultiClassProgress({ current: 0, total: 0, message: '' });
      }
      setSettingsRevealed(true);
      if ((initialState.category || '') === '비슷한문장') {
        setSimilarPrefill(initialState.prompt || firstContent || '');
        setSimilarContext({
          categoryHint: initialState.categoryHint || '',
          targetBytes: initialState.targetBytes || '',
        });
      }
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }, 250);
      showToast('기록의 문장을 이어서 다듬을 수 있어요.', 'success');
      return;
    }

    if (!initialState?.prompt) return;
    const cat = initialState.category || '행동발달';
    const restoredSchoolLevel = initialState.schoolLevel || '중등';
    setSelectedCategory(cat);
    setSchoolLevel(restoredSchoolLevel);
    if (cat === '교과') {
      setLevelCounts(initialState.levelCounts || createDefaultLevelCounts(restoredSchoolLevel));
    }
    setSettingsRevealed(true);

    const prompt = initialState.prompt;
    const emphasizePoints = initialState.emphasizePoints || [];

    const extractSection = (text, startTag, endTag) => {
      const startIdx = text.indexOf(startTag);
      if (startIdx === -1) return '';
      const contentStart = startIdx + startTag.length;
      if (endTag) {
        const endIdx = text.indexOf(endTag, contentStart);
        return endIdx === -1 ? text.slice(contentStart).trim() : text.slice(contentStart, endIdx).trim();
      }
      return text.slice(contentStart).trim();
    };

    if (cat === '교과') {
      setSubjectData(prev => ({
        ...prev,
        activityName: extractSection(prompt, '[활동명/성취기준]\n', '\n\n[활동내용]'),
        activityContent: extractSection(prompt, '[활동내용]\n', null)
      }));
      setGenerationData(prev => ({ ...prev, emphasizePoints }));
    } else if (cat === '교과_고등') {
      setHighSchoolData(prev => ({
        ...prev,
        achievementStandard: extractSection(prompt, '[성취기준/평가 계획표]\n', '\n\n['),
        studentActivity: extractSection(prompt, '[학생 활동 내용]\n', '\n\n['),
        growthPoints: extractSection(prompt, '[학생 정보', null).replace(/^[^\]]*\]\n/, '')
      }));
      setGenerationData(prev => ({ ...prev, emphasizePoints }));
    } else if (cat === '동아리') {
      setClubData(prev => ({
        ...prev,
        clubDescription: extractSection(prompt, '[동아리 정보]\n', '\n\n[학생 활동 내용]'),
        studentActivity: extractSection(prompt, '[학생 활동 내용]\n', null)
      }));
      setGenerationData(prev => ({ ...prev, emphasizePoints }));
    } else if (cat === '진로') {
      setCareerData(prev => ({
        ...prev,
        studentActivity: extractSection(prompt, '[진로 활동]\n', '\n\n[학생 정보]'),
        careerInfo: extractSection(prompt, '[학생 정보]\n', null)
      }));
      setGenerationData(prev => ({ ...prev, emphasizePoints }));
    } else if (cat === '비슷한문장') {
      setSimilarPrefill(prompt);
      setSimilarContext({
        categoryHint: initialState.categoryHint || '',
        targetBytes: initialState.targetBytes || '',
      });
      setGenerationData(prev => ({ ...prev, prompt, emphasizePoints }));
    } else {
      setGenerationData(prev => ({ ...prev, prompt, emphasizePoints }));
    }

    const historyRefs = refsFromHistoryRecord({
      achievementStandardKeys: initialState.achievementStandardKeys,
      achievementStandardRefs: initialState.achievementStandardRefs,
    });
    if (historyRefs.length > 0) {
      restoreValidatedStandards(historyRefs).then(({ droppedCount, records }) => {
        setSelectedStandards(records);
        setAchievementRestoreNotice(droppedStandardsNotice(droppedCount));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 비로그인 사용자의 경우 입력 데이터 자동 저장 (디바운싱)
  useEffect(() => {
    if (isAnonymousUser && (
      generationData.prompt.trim() || 
      generationData.emphasizePoints.length > 0 ||
      subjectData.activityName.trim() ||
      subjectData.activityContent.trim()
    )) {
      const timer = setTimeout(() => {
        saveDataToLocalStorage();
      }, 1000); // 1초 디바운싱

      return () => clearTimeout(timer);
    }
  }, [generationData, subjectData, selectedCategory, selectedSubject, expandedCategories, isAnonymousUser, saveDataToLocalStorage]);

  const hasInputContent = useCallback(() => {
    if (similarPrefill.trim()) return true;
    if (generationData.prompt.trim()) return true;
    if (subjectData.activityName.trim() || subjectData.activityContent.trim() || subjectData.attachedFile) return true;
    if (highSchoolData.achievementStandard.trim() || highSchoolData.growthPoints.trim() || highSchoolData.files.length > 0) return true;
    if (clubData.planFile || clubData.studentActivity.trim()) return true;
    if (careerData.studentActivity?.trim() || careerData.careerInfo?.trim() || careerData.studentFiles?.length > 0 || careerData.infoFile) return true;
    if (hasSaenggibuFileUploadState(fileUpload, null)) return true;
    return false;
  }, [
    careerData,
    clubData,
    fileUpload,
    generationData.prompt,
    highSchoolData,
    similarPrefill,
    subjectData,
  ]);

  const manualEditCurrentContent = (() => {
    if (manualEditKey === 'main') return generationResult;
    const match = manualEditKey?.match(/^version-(\d+)$/);
    return match ? (versions[Number(match[1])] || '') : '';
  })();
  const hasUnsavedResultEdits = Boolean(
    unpersistedResultKeys.size > 0
    || (manualEditKey && manualEditDraft !== manualEditCurrentContent)
    || refinePreviewDraft.trim()
    || refineInstruction.trim()
    || regenerateInstruction.trim()
  );
  const categoryChangeState = {
    hasInput: hasInputContent(),
    hasResult: hasVisibleResult,
    hasUnsavedResultEdits,
    isAuthenticated: !isAnonymousUser,
    savedRecordId,
  };
  const categoryChangeDialog = getSaenggibuCategoryChangeDialog(categoryChangeState);
  const shouldConfirmCategoryChange = shouldConfirmSaenggibuCategoryChange(categoryChangeState);

  const handleCategoryChange = useCallback((newCategory) => {
    if (newCategory === selectedCategory) return;
    if (isGenerating || isStreaming) {
      showToast('생성 중에는 카테고리를 바꿀 수 없어요. 먼저 생성을 중단해주세요.', 'info');
      return;
    }
    if (refineLoadingKey) {
      showToast('AI가 문장을 다듬는 중에는 카테고리를 바꿀 수 없어요.', 'info');
      return;
    }
    if (manualEditSavingKey) {
      showToast('수정 내용을 저장한 뒤 카테고리를 바꿔주세요.', 'info');
      return;
    }
    if (shouldConfirmCategoryChange) {
      setShowCategoryConfirm(newCategory);
    } else {
      clearGeneratedResultState();
      setSelectedCategory(newCategory);
    }
  }, [
    clearGeneratedResultState,
    isGenerating,
    isStreaming,
    manualEditSavingKey,
    refineLoadingKey,
    selectedCategory,
    shouldConfirmCategoryChange,
    showToast,
  ]);

  const confirmCategoryChange = useCallback(() => {
    if (showCategoryConfirm) {
      clearGeneratedResultState();
      setSelectedCategory(showCategoryConfirm);
      setShowCategoryConfirm(null);
    }
  }, [clearGeneratedResultState, showCategoryConfirm]);

  // 입력 감지 → 설정 영역 순차 노출
  useEffect(() => {
    if (settingsRevealed) return;
    if (hasInputContent()) {
      setSettingsRevealed(true);
    }
  }, [settingsRevealed, hasInputContent]);

  // 생성 개수 제한 — 무료 한도는 백엔드(main.py 무료 인원 검사)와 같아야 한다 (창체 10명, 그 외 2개 / 유료: 30명).
  // 교과는 이 슬라이더 대신 성취수준별 학생 수를 쓴다.
  const maxGenerationCount = (selectedCategory === '교과' || selectedCategory === '창체') ? 30 : (selectedCategory === '교과_고등' ? 10 : 10);
  const freeLimit = selectedCategory === '창체' ? 10 : 2;
  const generationSliderOpen = canUseProCounts || freeLimit > 2;
  const proLevelTotalMax = LEVEL_LIMITS.PRO_TOTAL_MAX;
  const totalLevelCount = subjectLevelScheme.reduce((sum, level) => sum + (levelCounts[level.key] || 0), 0);
  const levelSchemeLabels = subjectLevelScheme.map(level => level.label).join('·');
  const totalMultiClassCount = totalLevelCount * subjectClassCount;
  const multiClassTotalMax = MAX_SUBJECT_CLASSES * proLevelTotalMax;

  const setSubjectClassCount = (nextCount) => {
    const count = Math.max(1, Math.min(MAX_SUBJECT_CLASSES, Number(nextCount) || 1));
    if (count > 1 && !isProUser) {
      setShowProModal(true);
      return;
    }
    setSubjectClassCountState(count);
  };

  // 카테고리별 강조 포인트 (실제 교사들이 자주 사용하는 표현들)
  const emphasisPointCategories = {
    "학습태도": [
      "수업 참여도가 높음", "질문을 적극적으로 함", "예습·복습을 성실히 함", 
      "어려운 내용도 끝까지 이해하려 노력함", "학습 목표를 스스로 설정함",
      "오답 정리를 꼼꼼히 함", "학습 계획을 체계적으로 세움", "집중력이 뛰어남"
    ],
    "성격특성": [
      "성실하고 책임감이 강함", "밝고 긍정적인 성격", "차분하고 신중한 성격",
      "활발하고 에너지가 넘침", "섬세하고 배려심이 깊음", "유머감각이 뛰어남",
      "진솔하고 솔직한 성격", "온화하고 친근한 성격", "의지가 강하고 끈기있음"
    ],
    "대인관계": [
      "친구들과 원만한 관계 유지", "어려운 친구를 적극 도움", "갈등 상황에서 중재 역할",
      "새로운 친구와도 쉽게 친해짐", "친구들의 고민을 잘 들어줌", "팀워크를 중시함",
      "다양한 성격의 친구들과 어울림", "선후배 관계를 소중히 여김", "공감능력이 뛰어남"
    ],
    "리더십": [
      "모둠 활동에서 주도적 역할", "반 행사를 적극 기획함", "후배들을 잘 이끌어줌",
      "의견 조율 능력이 뛰어남", "솔선수범하는 모습", "책임감 있게 역할 수행",
      "다른 학생들에게 좋은 영향", "조직력과 추진력이 뛰어남", "민주적 의사결정 추구"
    ],
    "창의성": [
      "독창적인 아이디어 제시", "다양한 관점에서 문제 접근", "예술적 감성이 뛰어남",
      "새로운 방법을 시도함", "상상력이 풍부함", "융합적 사고력 보임",
      "창의적 표현력이 뛰어남", "혁신적인 해결방안 제시", "예술 활동에 적극 참여"
    ],
    "의사소통": [
      "자신의 생각을 명확히 표현", "논리적으로 설명함", "경청하는 자세가 좋음",
      "발표력이 뛰어남", "토론에서 합리적 의견 제시", "글쓰기 능력이 우수함",
      "비언어적 표현도 효과적", "상황에 맞는 언어 사용", "설득력 있는 의견 개진"
    ],
    "도전정신": [
      "어려운 과제에도 적극 도전", "새로운 분야에 관심 많음", "실패를 두려워하지 않음",
      "목표 달성을 위해 노력함", "한계를 뛰어넘으려 함", "포기하지 않는 끈기",
      "모험심이 있음", "성장을 위해 노력함", "변화를 긍정적으로 받아들임"
    ],
    "봉사정신": [
      "학급 일에 적극 참여", "환경 정리를 솔선수범", "어려운 친구를 도움",
      "학교 봉사활동에 열심", "지역사회 활동 참여", "약자를 배려하는 마음",
      "나눔의 실천", "공익을 위해 노력", "타인을 위한 희생 정신"
    ],
    "특기재능": [
      "수학적 사고력이 뛰어남", "언어 감각이 우수함", "과학적 탐구력 보임",
      "예술적 재능이 있음", "체육 활동에 소질", "정보 활용 능력 우수",
      "손재주가 뛰어남", "기계 조작 능력 좋음", "외국어 실력이 우수함"
    ],
    "성장모습": [
      "꾸준한 발전 모습 보임", "약점을 보완하려 노력", "피드백을 적극 수용",
      "자기반성을 통해 성장", "새로운 도전으로 발전", "경험을 통해 성숙해짐",
      "실수를 교훈으로 삼음", "지속적인 자기계발", "목표를 향해 꾸준히 노력"
    ]
  };

  // 전체 강조 포인트 목록은 emphasisPointCategories에서 직접 사용

  // 카테고리 펼침/접기 토글
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // 개선된 카테고리별 예시 (교과는 세부 과목별로 맞춤화)
  const situationalExamples = [
    {
      category: "전체",
      examples: [
        "수업 참여도가 높고 친구들과 협력을 잘하는 학생",
        "조용하지만 성실하며 꾸준히 노력하는 학생",
        "창의적이고 발표를 잘하며 리더십이 있는 학생",
        "배려심이 깊고 친구들을 도와주는 학생",
        "호기심이 많고 질문을 자주 하는 학생",
        "책임감이 강하고 맡은 일을 끝까지 해내는 학생",
        "예의바르고 규칙을 잘 지키는 학생",
        "성실하고 차분한 성격으로 학습에 집중하는 학생",
        "밝고 긍정적인 성격으로 반 분위기를 좋게 만드는 학생",
        "어려운 상황에서도 포기하지 않고 끝까지 도전하는 학생",
        "친구들과 원만한 관계를 유지하며 갈등을 조정하는 학생",
        "자신의 의견을 논리적으로 표현하며 토론을 좋아하는 학생"
      ]
    },
    {
      category: "행동발달",
      // 칩 글자·순서·id 는 data/saenggibu/behaviorTraitChips.js 한 곳에서 관리한다.
      categories: BEHAVIOR_TRAIT_GROUPS.map((group) => ({ label: group.label, examples: group.chips })),
    },
    {
      category: "교과",
      examples: [
        "수업 내용을 정확히 이해하고 심화 학습에 도전하는 학생",
        "창의적인 아이디어를 제시하며 발표 능력이 뛰어난 학생",
        "토론 활동에서 논리적으로 자신의 의견을 표현하는 학생",
        "과제를 성실히 수행하고 완성도가 높은 학생",
        "어려운 문제에 끈기 있게 도전하는 모습을 보이는 학생",
        "수학 문제 해결에 흥미를 보이며 창의적 사고력을 발휘하는 학생",
        "독서를 좋아하고 글쓰기에 관심이 많은 학생",
        "과학 실험에 적극적으로 참여하고 탐구하는 학생",
        "미술 활동에서 독창적인 표현을 하는 학생",
        "음악 활동에서 뛰어난 감성을 보이는 학생",
        "영어 학습에 의욕적이며 소통하려는 의지가 강한 학생",
        "체육 시간에 협동심을 발휘하며 운동 능력이 뛰어난 학생",
        "사회 문제에 관심이 많고 비판적 사고를 하는 학생"
      ]
    },
    {
      category: "창체",
      examples: [
        "영양/식생활교육",
        "인성교육",
        "진로교육",
        "민주시민교육",
        "다문화교육",
        "통일교육",
        "독도교육",
        "경제/금융교육",
        "환경/지속가능발전교육",
        "정보통신활용교육",
        "성폭력예방교육",
        "흡연예방교육",
        "교육활동침해예방교육",
        "가정폭력예방교육",
        "아동학대예방교육",
        "장애인식개선교육",
        "사이버폭력예방교육",
        "화재예방교육",
        "기초생활질서교육",
        "친구사랑교육",
        "유괴예방교육",
        "양성평등교육",
        "디지털성범죄예방교육"
      ]
    }
  ];

  // 입력 핸들러
  const handleInputChange = (field, value) => {
    setGenerationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 생성 개수 변경 핸들러
  const handleGenerationCountChange = (count) => {
    if (count > freeLimit && !canUseProCounts) {
      setShowProModal(true);
      return;
    }
    setGenerationData(prev => ({
      ...prev,
      generationCount: count
    }));
  };

  // 예시 버튼 클릭 핸들러
  // 📝 우리 학교 행사 소재: 고른 관찰 모습을 활동 내용 끝에 한 줄로 덧붙인다 (기존 입력은 지우지 않는다)
  const handleSchoolEventTopicInsert = (text) => {
    const line = String(text || '').trim();
    if (!line) return;
    setGenerationData(prev => {
      const current = String(prev.prompt || '').trimEnd();
      const nextPrompt = current ? `${current}\n${line}` : line;
      const nextProfile = selectedCategory === '창체' ? classifyCreativeInputProfile(nextPrompt) : null;
      if (nextProfile) setCreativeInputProfile(nextProfile);
      return {
        ...prev,
        prompt: nextPrompt,
        ...(nextProfile
          ? { generationCount: nextProfile === 'detailed_evidence' ? 1 : Math.max(2, prev.generationCount) }
          : {}),
      };
    });
    if (localError) setLocalError('');
    setTimeout(() => promptTextareaRef.current?.focus(), 0);
  };

  // 행동발달 특성 칩: 칩 쪽에서 넘긴 함수로 '학생 특성' 글자를 바꾼다(빠르게 연달아 눌러도 최신 글자 기준).
  const handleBehaviorPromptChange = (updater) => {
    setGenerationData(prev => ({ ...prev, prompt: updater(prev.prompt || '') }));
    if (localError) setLocalError('');
  };

  const trackBehaviorTraitChip = (stage, meta = {}) => {
    trackSaenggibuUxEvent('saenggibu_trait_chip', {
      saenggibu_category: '행동발달',
      is_batch: false,
      stage,
      ...meta,
    });
  };

  const handleExampleClick = (type, text) => {
    if (type === 'request') {
      // 빠르게 모드의 행동발달과 창체는 나열식으로 콤마로 추가
      if (uiMode === 'quick' && (selectedCategory === '행동발달' || selectedCategory === '창체')) {
        setGenerationData(prev => ({
          ...prev,
          prompt: prev.prompt 
            ? (prev.prompt.endsWith(', ') || prev.prompt.endsWith(',') 
                ? `${prev.prompt} ${text}` 
                : `${prev.prompt}, ${text}`)
            : text
        }));
      } else {
        // 다른 모든 경우는 전체 교체 (교과 등)
        setGenerationData(prev => ({
          ...prev,
          prompt: text
        }));
      }
    } else if (type === 'emphasis') {
      setGenerationData(prev => ({
        ...prev,
        emphasizePoints: prev.emphasizePoints.includes(text) 
          ? prev.emphasizePoints.filter(point => point !== text)
          : [...prev.emphasizePoints, text]
      }));
    }
  };

  const handleSuggestionAction = (action, nextSuggestion, extra = {}) => {
    runSaenggibuSuggestionAction({
      action,
      suggestion: nextSuggestion,
      act: suggestionApi.act,
      dismiss: suggestionApi.dismiss,
      option: extra.option || nextSuggestion?.default_option,
      text: action === 'save_example'
        ? (
          extra.text
          || (nextSuggestion?.payload?.version_index != null
            ? versions[nextSuggestion.payload.version_index]
            : null)
          || generationResult
          || versions[0]
          || ''
        )
        : undefined,
      showToast,
      navigate,
      openBatch: () => {
        if (typeof setUiMode === 'function') {
          setUiMode('batch');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        navigate('/batch-saenggibu');
      },
    });
  };

  // 복사 기능 핸들러
  const handleCopyResult = async (versionIndex = null) => {
    const textToCopy = versionIndex !== null ? versions[versionIndex] : generationResult;
    if (!textToCopy) return;
    
    // 복사할 버전의 키 생성 (null이면 'main', 아니면 인덱스)
    const copyKey = versionIndex !== null ? `version-${versionIndex}` : 'main';
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      const copiedBytes = calculateNEISBytes(textToCopy);
      const copyTargetBytes = Number(resultTargetBytes) || 0;
      trackSaenggibuUxEvent('saenggibu_copy', {
        saenggibu_category: resultCategory || selectedCategory,
        is_batch: false,
        version_index: versionIndex == null ? 0 : versionIndex,
        record_id: savedRecordId,
        versions_count: Array.isArray(versions) ? versions.length : 1,
        content_bytes: copiedBytes,
        target_bytes: copyTargetBytes,
        ...(copyTargetBytes > 0
          ? { within_band: copiedBytes >= copyTargetBytes * 0.95 && copiedBytes <= copyTargetBytes * 1.05 }
          : {}),
      });
      suggestionApi.refetch({
        copied: true,
        versionIndex: versionIndex == null ? 0 : versionIndex,
      });
      setCopyStatus(prev => ({ ...prev, [copyKey]: 'copied' }));
      const copiedIndex = versionIndex == null ? 0 : versionIndex;
      if (rosterLink?.auto && savedRecordId && rosterLink.recordId === savedRecordId && rosterLink.versionIndex !== copiedIndex) {
        linkRecordRef.current?.(rosterLink, savedRecordId, copiedIndex, 'copy');
      }
      
      // 🆕 복사 이력에 추가 (한번이라도 복사하면 기록)
      setCopiedHistory(prev => new Set([...prev, copyKey]));
      
      setTimeout(() => {
        setCopyStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[copyKey];
          return newStatus;
        });
      }, 2000);
    } catch (err) {
      console.error('복사 실패:', err);
      setCopyStatus(prev => ({ ...prev, [copyKey]: 'error' }));
      setTimeout(() => {
        setCopyStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[copyKey];
          return newStatus;
        });
      }, 2000);
    }
  };

  const getRefineKey = (versionIndex = null) => (
    versionIndex !== null ? `version-${versionIndex}` : 'main'
  );

  const toggleInlineSimilar = (key, text) => {
    if (inlineSimilar?.key === key) {
      setInlineSimilar(null);
      return;
    }
    const launched = resolveSimilarLaunchContext(text, {}, { selectedCategory, resultCategory });
    if (!launched) return;
    const bytes = calculateNEISBytes(launched.text);
    setInlineSimilar({
      key,
      text: launched.text,
      categoryHint: launched.categoryHint,
      targetBytes: bytes >= 100 ? bytes : '',
      evaluationContext: buildSimilarEvaluationContext(resultCategory, {
        subjectActivityName: subjectData.activityName,
        highSchoolAchievement: highSchoolData.achievementStandard,
      }),
    });
    trackSaenggibuUxEvent('saenggibu_similar_inline_open', {
      saenggibu_category: resultCategory,
      is_batch: false,
    });
  };

  const renderInlineSimilarPanel = (key) => {
    if (!inlineSimilar || inlineSimilar.key !== key) return null;
    return (
      <SimilarSentencePanel
        key={`${key}-${inlineSimilar.text.length}`}
        variant="inline"
        isProUser={isProUser}
        isAnonymousUser={isAnonymousUser}
        prefillSentence={inlineSimilar.text}
        initialCategoryHint={inlineSimilar.categoryHint}
        initialTargetBytes={inlineSimilar.targetBytes}
        evaluationContext={inlineSimilar.evaluationContext}
        sourceRecordId={savedRecordId || ''}
        selectedModel={generationData.selectedModel}
        onClose={() => setInlineSimilar(null)}
        onNeedLogin={() => {
          setLoginPromptFeature('saenggibu-similar');
          setShowLoginPrompt(true);
        }}
        onNeedPro={() => {
          setProModalFeature('비슷한 문장');
          setShowProModal(true);
        }}
      />
    );
  };

  const getRefineContent = (versionIndex = null) => (
    versionIndex !== null ? (versions[versionIndex] || '') : generationResult
  );

  const updateMultiClassVersionByFlatIndex = (flatIndex, nextContent) => {
    if (flatIndex === null || flatIndex === undefined) return;
    let cursor = 0;
    setMultiClassResults(prev => prev.map(classResult => {
      const classVersions = classResult.versions || [];
      const nextVersions = [...classVersions];
      let changed = false;
      for (let idx = 0; idx < classVersions.length; idx += 1) {
        if (classVersions[idx]) {
          if (cursor === flatIndex) {
            nextVersions[idx] = nextContent;
            changed = true;
          }
          cursor += 1;
        }
      }
      return changed ? { ...classResult, versions: nextVersions } : classResult;
    }));
  };

  const getMultiClassPositionByFlatIndex = (flatIndex) => {
    if (flatIndex === null || flatIndex === undefined) return null;
    let cursor = 0;
    for (let classIndex = 0; classIndex < multiClassResults.length; classIndex += 1) {
      const classVersions = multiClassResults[classIndex].versions || [];
      for (let versionIndex = 0; versionIndex < classVersions.length; versionIndex += 1) {
        if (!classVersions[versionIndex]) continue;
        if (cursor === flatIndex) {
          return { classIndex, versionIndex };
        }
        cursor += 1;
      }
    }
    return null;
  };

  const updateRefineContent = (key, nextContent) => {
    if (key === 'main') {
      setGenerationResult(nextContent);
      setVersions(prev => {
        if (prev.length === 0) return prev;
        const next = [...prev];
        next[0] = nextContent;
        return next;
      });
      return;
    }

    const match = key.match(/^version-(\d+)$/);
    if (!match) return;
    const index = Number(match[1]);
    setVersions(prev => {
      const next = [...prev];
      next[index] = nextContent;
      return next;
    });
    updateMultiClassVersionByFlatIndex(index, nextContent);
    if (index === 0) {
      setGenerationResult(nextContent);
    }
  };

  const getVersionNumberFromRefineKey = (key) => {
    if (key === 'main') return versionNumbers[0] || 1;
    const match = key.match(/^version-(\d+)$/);
    if (!match) return 1;
    const index = Number(match[1]);
    return versionNumbers[index] || index + 1;
  };

  const markResultUnpersisted = (key) => {
    setUnpersistedResultKeys(prev => new Set([...prev, key]));
  };

  const markResultPersisted = (key) => {
    setUnpersistedResultKeys(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const persistRefinedContent = async (key, content, revision = null) => {
    if (!savedRecordId) return false;
    if (multiClassResults.length > 0) {
      const match = key.match(/^version-(\d+)$/);
      if (!match) return false;
      const position = getMultiClassPositionByFlatIndex(Number(match[1]));
      if (!position) return false;
      await updateMultiClassSaenggibuVersion(
        savedRecordId,
        position.classIndex,
        position.versionIndex,
        content,
        revision
      );
      return true;
    }
    await updateSaenggibuVersion(
      savedRecordId,
      getVersionNumberFromRefineKey(key),
      content,
      revision
    );
    return true;
  };

  const buildCurrentInputSignature = () => JSON.stringify({
    uiMode,
    category: selectedCategory,
    selectedSubject,
    schoolLevel,
    generation: {
      prompt: createTextFingerprint(generationData.prompt),
      wordCount: generationData.wordCount,
      generationCount: generationData.generationCount,
      emphasizePoints: generationData.emphasizePoints,
      selectedModel: generationData.selectedModel,
    },
    subject: {
      activityName: createTextFingerprint(subjectData.activityName),
      activityContent: createTextFingerprint(subjectData.activityContent),
      attachedFile: summarizeSaenggibuResultFile(subjectData.attachedFile),
      levelCounts,
      subjectClassCount,
    },
    highSchool: {
      achievementStandard: createTextFingerprint(highSchoolData.achievementStandard),
      growthPoints: createTextFingerprint(highSchoolData.growthPoints),
      studentActivity: createTextFingerprint(highSchoolData.studentActivity),
      files: (highSchoolData.filesContent || []).map(summarizeSaenggibuResultFile),
    },
    club: {
      description: createTextFingerprint(clubData.clubDescription),
      studentActivity: createTextFingerprint(clubData.studentActivity),
      planFile: summarizeSaenggibuResultFile(clubData.planFileContent),
      studentFiles: (clubData.studentFilesContent || []).map(summarizeSaenggibuResultFile),
    },
    career: {
      studentActivity: createTextFingerprint(careerData.studentActivity),
      careerInfo: createTextFingerprint(careerData.careerInfo),
      infoFile: summarizeSaenggibuResultFile(careerData.infoFileContent),
      studentFiles: (careerData.studentFilesContent || []).map(summarizeSaenggibuResultFile),
    },
  });

  const buildCurrentOriginalContext = () => {
    const sections = [
      `[카테고리]\n${selectedCategory}`,
      `[강조 포인트]\n${(generationData.emphasizePoints || []).join(', ') || '없음'}`
    ];

    if (selectedCategory === '교과') {
      sections.push(`[활동명/성취기준]\n${subjectData.activityName || ''}`);
      sections.push(`[활동내용]\n${subjectData.activityContent || ''}`);
      sections.push(`[학교급]\n${schoolLevel || ''}`);
      sections.push(`[성취수준 체계]\n${levelSchemeLabels || ''}`);
      sections.push(`[성취수준별 인원]\n${subjectLevelScheme.map(level => `${level.label}: ${levelCounts[level.key] || 0}명`).join(', ')}`);
    } else if (selectedCategory === '교과_고등') {
      sections.push(`[성취기준/평가 계획표]\n${highSchoolData.achievementStandard || ''}`);
      sections.push(`[학생 활동 내용]\n${highSchoolData.studentActivity || ''}`);
      sections.push(`[학생 정보]\n${highSchoolData.growthPoints || ''}`);
    } else if (selectedCategory === '동아리') {
      sections.push(`[동아리 정보]\n${clubData.clubDescription || ''}`);
      sections.push(`[학생 활동 내용]\n${clubData.studentActivity || ''}`);
    } else if (selectedCategory === '진로') {
      sections.push(`[진로 활동]\n${careerData.studentActivity || ''}`);
      sections.push(`[학생 정보]\n${careerData.careerInfo || ''}`);
    } else {
      sections.push(`[입력 내용]\n${generationData.prompt || ''}`);
    }

    return sections
      .filter(Boolean)
      .join('\n\n')
      .slice(0, 30000);
  };

  const buildRefineOriginalContext = () => (
    resultContext?.originalContext || buildCurrentOriginalContext()
  );

  const createCurrentResultContext = () => ({
    category: selectedCategory,
    schoolLevel,
    selectedModel: generationData.selectedModel,
    targetBytes: generationData.wordCount,
    inputSignature: buildCurrentInputSignature(),
    originalContext: buildCurrentOriginalContext(),
  });

  const isCurrentInputForResult = () => (
    Boolean(resultContext?.inputSignature)
    && resultContext.inputSignature === buildCurrentInputSignature()
  );
  const currentInputMatchesResult = hasVisibleResult && isCurrentInputForResult();

  const handleOpenRefinePanel = (versionIndex = null) => {
    const key = getRefineKey(versionIndex);
    if (refineLoadingKey && refineLoadingKey !== key) {
      showToast('AI가 다른 문장을 다듬고 있어요. 완료 후 다시 요청해주세요.', 'warning');
      return;
    }
    setRefinePanelKey(prev => (prev === key ? null : key));
    setRefinePreview(prev => (prev?.key === key ? prev : null));
    setManualEditKey(null);
    setRefineInstruction('');
  };

  const handleOpenManualEdit = (versionIndex = null) => {
    if (refineLoadingKey) {
      showToast('AI가 문장을 다듬고 있어요. 완료 후 직접 수정해주세요.', 'warning');
      return;
    }
    const key = getRefineKey(versionIndex);
    const content = getRefineContent(versionIndex);
    setManualEditKey(prev => (prev === key ? null : key));
    setManualEditDraft(content);
    setRefinePanelKey(null);
    setRefinePreview(null);
    setRefinePreviewDraft('');
    setRefinePreviewEditingKey(null);
  };

  const handleSaveManualEdit = async () => {
    if (!manualEditKey || !manualEditDraft.trim()) return;
    const finalContent = manualEditDraft.trim();

    try {
      setManualEditSavingKey(manualEditKey);
      markResultUnpersisted(manualEditKey);
      updateRefineContent(manualEditKey, finalContent);
      const saved = await persistRefinedContent(manualEditKey, finalContent, {
        mode: 'manual_edit',
        instruction: '교사가 직접 문장을 수정함',
        before_bytes: null,
        after_bytes: calculateNEISBytes(finalContent),
      });
      if (saved) {
        markResultPersisted(manualEditKey);
        const editedVersionIndex = manualEditKey === 'main'
          ? 0
          : Number(String(manualEditKey).replace('version-', '')) || 0;
        setManuallyEditedKeys((prev) => new Set(prev).add(manualEditKey));
        trackSaenggibuUxEvent('saenggibu_manual_edit', {
          saenggibu_category: resultCategory || selectedCategory,
          is_batch: false,
          version_index: editedVersionIndex,
        });
        suggestionApi.refetch({
          edited: true,
          versionIndex: editedVersionIndex,
        });
        window.dispatchEvent(new CustomEvent('saenggibuCreated', {
          detail: { recordId: savedRecordId }
        }));
        showToast('수정한 내용을 기록에 저장했어요', 'success');
      } else {
        showToast('화면에는 반영했어요. 기록에는 저장되지 않았어요', 'warning', 6000);
      }
      setManualEditKey(null);
      setManualEditDraft('');
    } catch (error) {
      console.error('생기부 직접 수정 저장 실패:', error);
      showToast('화면에는 반영했어요. 기록 저장은 다시 시도해주세요', 'warning', 6000);
    } finally {
      setManualEditSavingKey(null);
    }
  };

  const handleRequestRefine = async (versionIndex = null, mode = refineMode) => {
    const key = getRefineKey(versionIndex);
    if (refineLoadingKey) {
      showToast('이미 AI가 수정안을 만들고 있어요.', 'warning');
      return;
    }
    const currentContent = getRefineContent(versionIndex);
    if (!currentContent.trim()) return;

    const currentBytes = calculateNEISBytes(currentContent);
    const targetBytes = mode === 'reduce_bytes'
      ? Math.max(100, Math.min(currentBytes, Number(resultTargetBytes) || currentBytes))
      : undefined;

    const instructionText = refineInstruction.trim();
    const maskedContent = currentContent;
    const maskedInstruction = instructionText;
    const originalContext = buildRefineOriginalContext();
    const maskedOriginalContext = originalContext || '';

    try {
      setRefineLoadingKey(key);
      setRefinePanelKey(key);
      const response = await refineSaenggibuContent({
        record_id: savedRecordId,
        version_index: versionIndex,
        current_content: maskedContent,
        instruction: maskedInstruction,
        mode,
        scope: 'single_version',
        category: resultCategory,
        target_bytes: targetBytes,
        original_context: maskedOriginalContext,
        selected_model: resultModel
      });

      setRefineMode(mode);
      setRefinePreview({
        key,
        before: currentContent,
        after: response.content || '',
        byteCount: response.byte_count || calculateNEISBytes(response.content || ''),
        mode,
        revision: response.revision || null,
      });
      setRefinePreviewDraft(response.content || '');
      setRefinePreviewEditingKey(null);
      showToast('AI가 수정안을 만들었어요', 'success');
    } catch (error) {
      console.error('생기부 AI 다듬기 실패:', error);
      showToast(error.message || '생기부 문장을 다듬지 못했어요', 'error');
    } finally {
      setRefineLoadingKey(null);
    }
  };

  const handleApplyRefinePreview = async () => {
    if (!refinePreview?.key || !refinePreviewDraft.trim()) return;
    const finalContent = refinePreviewDraft.trim();
    setRefineUndo({
      key: refinePreview.key,
      content: refinePreview.before
    });
    markResultUnpersisted(refinePreview.key);
    updateRefineContent(refinePreview.key, finalContent);
    try {
      const saved = await persistRefinedContent(
        refinePreview.key,
        finalContent,
        refinePreview.revision
      );
      if (saved) {
        markResultPersisted(refinePreview.key);
        window.dispatchEvent(new CustomEvent('saenggibuCreated', {
          detail: { recordId: savedRecordId }
        }));
        showToast('수정안을 적용하고 기록에 저장했어요', 'success');
      } else {
        showToast('수정안은 적용했지만 기록에는 저장되지 않았어요', 'warning', 6000);
      }
    } catch (error) {
      console.error('생기부 수정 결과 저장 실패:', error);
      showToast('화면에는 적용했어요. 기록 저장은 다시 시도해주세요', 'warning', 6000);
    }
    setRefinePreview(null);
    setRefinePreviewDraft('');
    setRefinePreviewEditingKey(null);
    setRefinePanelKey(null);
  };

  const handleUndoRefine = async () => {
    if (!refineUndo?.key) return;
    markResultUnpersisted(refineUndo.key);
    updateRefineContent(refineUndo.key, refineUndo.content);
    try {
      const saved = await persistRefinedContent(refineUndo.key, refineUndo.content, {
        mode: 'undo_refine',
        instruction: 'AI 다듬기 적용 전 문장으로 되돌림',
        before_bytes: null,
        after_bytes: calculateNEISBytes(refineUndo.content),
      });
      if (saved) {
        markResultPersisted(refineUndo.key);
        showToast('이전 문장으로 되돌리고 기록에 저장했어요', 'success');
      } else {
        showToast('화면에는 되돌렸지만 기록에는 저장되지 않았어요', 'warning', 6000);
      }
    } catch (error) {
      console.error('생기부 되돌리기 저장 실패:', error);
      showToast('화면에는 되돌렸어요. 기록 저장은 다시 시도해주세요', 'warning', 6000);
    }
    setRefineUndo(null);
  };

  const openEvidence = (refineKey, sentence = null) => {
    if (!evidenceEntryFor(refineKey)) return;
    setEvidenceDialog({ refineKey, sentence });
  };

  const renderResultText = (refineKey, content) => {
    const entry = evidenceEntryFor(refineKey);
    if (!entry || isStreaming) return content;
    return (
      <SaenggibuEvidenceText
        content={content}
        evidence={evidenceApi.get(entry.key, content)}
        showNumbers={showEvidenceNumbers}
        onOpen={(sentence) => openEvidence(refineKey, sentence)}
      />
    );
  };

  const renderEvidenceSummary = (refineKey) => {
    const entry = evidenceEntryFor(refineKey);
    if (!entry || isStreaming) return null;
    return (
      <SaenggibuEvidenceSummary
        status={evidenceApi.statusOf(entry.key, entry.content)}
        evidence={evidenceApi.get(entry.key, entry.content)}
        onOpen={() => openEvidence(refineKey)}
        showNumbers={showEvidenceNumbers}
        onToggleNumbers={setShowEvidenceNumbers}
      />
    );
  };

  const requestEvidenceRevision = async (refineKey, { scope, sentenceText, sentenceIndex, instruction, targetBytes }) => {
    const versionIndex = refineKey === 'main' ? null : Number(String(refineKey).replace('version-', ''));
    const response = await refineSaenggibuContent({
      record_id: savedRecordId,
      version_index: versionIndex,
      current_content: getRefineContent(versionIndex),
      instruction,
      mode: scope === 'sentence' ? 'sentence' : 'custom',
      scope: scope === 'sentence' ? 'sentence' : 'single_version',
      sentence_text: scope === 'sentence' ? sentenceText : undefined,
      sentence_index: scope === 'sentence' ? sentenceIndex : undefined,
      category: resultCategory,
      target_bytes: targetBytes,
      original_context: buildRefineOriginalContext(),
      selected_model: resultModel,
    });
    return response?.content || '';
  };

  const applyEvidenceRevision = async (refineKey, nextContent) => {
    const before = getRefineContent(refineKey === 'main' ? null : Number(String(refineKey).replace('version-', '')));
    markResultUnpersisted(refineKey);
    updateRefineContent(refineKey, nextContent);
    try {
      const saved = await persistRefinedContent(refineKey, nextContent, {
        mode: 'evidence_sentence',
        instruction: '근거 보기에서 문장을 고침',
        before_bytes: calculateNEISBytes(before),
        after_bytes: calculateNEISBytes(nextContent),
      });
      if (!saved) return false;
      markResultPersisted(refineKey);
      setManuallyEditedKeys((prev) => new Set(prev).add(refineKey));
      window.dispatchEvent(new CustomEvent('saenggibuCreated', { detail: { recordId: savedRecordId } }));
      showToast('바꾼 문장을 기록에 저장했어요', 'success');
      return true;
    } catch (error) {
      console.error('근거 보기 문장 저장 실패:', error);
      return false;
    }
  };

  const renderEvidenceDialog = () => {
    if (!evidenceDialog) return null;
    const entry = evidenceEntryFor(evidenceDialog.refineKey);
    if (!entry) return null;
    const label = multiClassResults.length > 0 ? '' : (versions.length > 1
      ? resultCardLabel(Number(String(entry.refineKey).replace('version-', '')))
      : '');
    return (
      <SaenggibuEvidenceDialog
        open
        onClose={() => setEvidenceDialog(null)}
        subtitle={label}
        content={entry.content}
        evidence={evidenceApi.get(entry.key, entry.content)}
        status={evidenceApi.statusOf(entry.key, entry.content)}
        initialSentence={evidenceDialog.sentence}
        canRevise={Boolean(savedRecordId) && !refineLoadingKey}
        onRequestRevision={(payload) => requestEvidenceRevision(entry.refineKey, payload)}
        onApply={(next) => applyEvidenceRevision(entry.refineKey, next)}
        surface={multiClassResults.length > 0 ? 'multi_class' : 'single'}
      />
    );
  };

  const renderManualEditPanel = (versionIndex = null) => {
    const key = getRefineKey(versionIndex);
    if (manualEditKey !== key) return null;
    const isSaving = manualEditSavingKey === key;

    return (
      <SaenggibuContentEditor
        isSaving={isSaving}
        maxBytes={getSaenggibuMaxBytes(resultCategory)}
        onCancel={() => {
          setManualEditKey(null);
          setManualEditDraft('');
        }}
        onChange={setManualEditDraft}
        onSave={handleSaveManualEdit}
        rows={7}
        targetBytes={resultTargetBytes}
        value={manualEditDraft}
        variant="inline"
      />
    );
  };

  // 현재 카테고리에 따른 예시 반환 (교과는 세부 과목별 맞춤화)
  const getCurrentExamples = () => {
    if (selectedCategory === '교과' && selectedSubject) {
      const baseExamples = subjectSpecificExamples[selectedSubject] || subjectSpecificExamples['국어'];
      // 커스텀 예시 추가
      if (customExamples?.['교과']?.[selectedSubject]) {
        const customs = customExamples['교과'][selectedSubject].map(text => ({ text, isCustom: true }));
        return [...baseExamples, ...customs];
      }
      return baseExamples;
    }
    
    const category = situationalExamples.find(cat => cat.category === selectedCategory);
    
    // 행동발달 + 빠르게 모드: 카테고리별 구조 반환
    if (selectedCategory === '행동발달' && uiMode === 'quick' && category?.categories) {
      return category.categories;
    }
    
    let examples = category ? category.examples : situationalExamples[0].examples;
    
    // 창체인 경우 커스텀 예시 추가
    if (selectedCategory === '창체' && customExamples?.['창체']) {
      const customs = customExamples['창체'].map(text => ({ text, isCustom: true }));
      examples = [...examples, ...customs];
    }
    
    // 모바일에서는 예시 개수 제한 (창체만)
    if (isMobile && selectedCategory === '창체') {
      return examples.slice(0, 10);
    }
    
    return examples;
  };

  // 카테고리별 텍스트 입력창 placeholder 예시
  const getPlaceholderText = () => {
    const placeholders = {
      '행동발달': '학생 특성/행동 키워드 입력',
      '교과': '학습 태도와 활동 입력',
      '창체': '창체 활동 내용 입력',
      '동아리': '동아리 활동 내용 입력',
      '진로': '진로 활동 내용 입력'
    };
    return placeholders[selectedCategory] || '학생 특성을 입력해주세요';
  };

  // 현재 카테고리에 따른 강조 포인트 반환 (교과 8개, 창체 8개 카테고리)
  const getCurrentEmphasisPoints = () => {
    if (selectedCategory === '교과' && selectedSubject) {
      // 교과별 8개 카테고리 제공
      const subjectPoints = subjectEmphasisPoints[selectedSubject] || subjectEmphasisPoints['국어'];
      const achievements = sampleAchievements[selectedSubject] || sampleAchievements['국어'];
      const performance = subjectPerformanceAssessment[selectedSubject] || subjectPerformanceAssessment['국어'];
      const attitude = subjectLearningAttitude[selectedSubject] || subjectLearningAttitude['국어'];
      const inquiry = subjectInquiryActivities[selectedSubject] || subjectInquiryActivities['국어'];
      const creativity = subjectCreativity[selectedSubject] || subjectCreativity['국어'];
      const collaboration = subjectCollaboration[selectedSubject] || subjectCollaboration['국어'];
      const career = subjectCareerConnection[selectedSubject] || subjectCareerConnection['국어'];
      
      return {
        [`${selectedSubject} 학습활동`]: subjectPoints,
        [`${selectedSubject} 성취기준`]: achievements,
        [`${selectedSubject} 수행평가`]: performance,
        [`${selectedSubject} 학습태도`]: attitude,
        [`${selectedSubject} 탐구활동`]: inquiry,
        [`${selectedSubject} 창의성`]: creativity,
        [`${selectedSubject} 협력활동`]: collaboration,
        [`${selectedSubject} 진로연계`]: career
      };
    }
    if (selectedCategory === '창체') {
      // 창체 전용 진로/학과/대학 연계 중심 카테고리
      return creativityEmphasisPoints;
    }
    return emphasisPointCategories; // 기존 강조 포인트 카테고리들
  };

  // 🔝 프롬프트 입력창으로 스크롤하는 함수
  const scrollToPromptInput = () => {
    setTimeout(() => {
      if (promptTextareaRef.current) {
        // 프롬프트 입력창으로 부드럽게 스크롤하고 포커스
        promptTextareaRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        // 포커스도 함께 설정하여 사용자가 바로 입력할 수 있도록
        promptTextareaRef.current.focus();
      }
    }, 150); // 토스트 애니메이션 완료 후 스크롤
  };

  const scrollToGenerationStatus = () => {
    setTimeout(() => {
      if (generationStatusRef.current) {
        generationStatusRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 120);
  };

  // 🔍 파일 사전 검사 수행 (단일 파일)
  const performFilePreCheck = useCallback(async (file) => {
    if (selectedCategory === '동아리' || selectedCategory === '진로') {
      const existingFiles = selectedCategory === '동아리'
        ? [
            ...(clubData.planFile ? [clubData.planFile] : []),
            ...(clubData.studentFiles || []),
          ]
        : [
            ...(careerData.infoFile ? [careerData.infoFile] : []),
            ...(careerData.studentFiles || []),
          ];
      const combinedFiles = [...existingFiles, file];
      const totalSize = combinedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
      if (totalSize > SAENGGIBU_STORAGE_FILE_TOTAL_LIMIT_BYTES) {
        setPreCheckResult(buildStorageFileTotalLimitResult(selectedCategory, combinedFiles, totalSize));
        setPendingFile(null);
        setPreCheckLoading(false);
        setShowPreCheckModal(true);
        return;
      }

      if (file.size > 25 * 1024 * 1024) {
        try {
          const storageFile = await uploadSingleSaenggibuStorageFile(file);
          if (selectedCategory === '동아리') {
            setClubData(prev => ({
              ...prev,
              planFile: file,
              planFileContent: {
                ...storageFile,
                analyzed: false,
              },
            }));
            showToast('동아리 계획서 파일을 분석할 준비가 됐어요. 필요한 정보는 직접 입력해 주세요.', 'success');
          } else {
            setCareerData(prev => ({
              ...prev,
              infoFile: file,
              infoFileContent: {
                ...storageFile,
                analyzed: false,
              },
            }));
            showToast('진로 정보 파일을 분석할 준비가 됐어요. 필요한 정보는 직접 입력해 주세요.', 'success');
          }
        } catch (error) {
          console.error('대용량 생기부 파일 처리 오류:', error);
          showToast('파일을 분석할 수 없었어요. 네트워크를 확인한 뒤 다시 시도해주세요.', 'error');
        }
        setShowPreCheckModal(false);
        setPreCheckLoading(false);
        return;
      }
    }

    // 로딩 시작
    setPreCheckLoading(true);
    setShowPreCheckModal(true);
    
    try {
      const result = await preCheckFiles([file], 'saenggibu', isProUser);
      
      // 검사 통과 & 확인 불필요 시 모달 없이 바로 업로드 진행
      if (result.can_proceed && !result.needs_confirmation) {
        setShowPreCheckModal(false);
        setPreCheckLoading(false);
        actualFileUploadRef.current(file);  // 🔧 ref를 통해 최신 함수 호출
        return;
      }
      
      // 확인이 필요하거나 진행 불가인 경우에만 모달 표시
      setPreCheckResult(result);
      setPendingFile(file);
      setPreCheckLoading(false);
      
    } catch (error) {
      console.error('파일 사전 검사 오류:', error);
      // 오류 시에도 업로드 진행 가능
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      actualFileUploadRef.current(file);  // 🔧 ref를 통해 최신 함수 호출
    }
  }, [careerData.infoFile, careerData.studentFiles, clubData.planFile, clubData.studentFiles, isProUser, selectedCategory, showToast, uploadSingleSaenggibuStorageFile]);  // isProUser 는 사전검사 한도 표기용

  // 🔍 파일 사전 검사 수행 (고등 모드 - 다중 파일) - ChatPage.js와 동일한 합산 제한 체크
  const performHighSchoolFilePreCheck = useCallback(async (files) => {
    // FileList를 배열로 미리 복사 (API 호출 시 FileList가 소비될 수 있음)
    const filesArray = Array.isArray(files) ? files : Array.from(files);
    
    // 로딩 시작
    setPreCheckLoading(true);
    setShowPreCheckModal(true);
    
    // 제한 상수 (backend/main.py와 동기화)
    // 📎 한도 (Free/Pro 공통 200k자) - constants/fileLimits.js
    const MAX_FILES = MAX_FILES_DEFAULT;
    const { contentLength: MAX_CONTENT_LENGTH } = getFileLimits(isProUser);
    
    // 🆕 이미 첨부된 파일 정보 합계
    const currentStats = {
      fileCount: highSchoolData.files?.length || 0,
      ...summarizeFileStats(highSchoolData.filesStats || []),
    };
    
    // 🆕 파일 수 초과 체크 (프론트에서 먼저 체크)
    const totalFileCount = currentStats.fileCount + filesArray.length;
    if (totalFileCount > MAX_FILES) {
      const availableSlots = Math.max(0, MAX_FILES - currentStats.fileCount);
      setPreCheckResult({
        can_proceed: false,
        needs_confirmation: false,
        overall_status: 'error',
        total_files: filesArray.length,
        total_size: filesArray.reduce((sum, f) => sum + f.size, 0),
        total_size_display: `${(filesArray.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(1)}MB`,
        files_info: filesArray.map(f => ({
          filename: f.name,
          status: 'ok',
          file_size: f.size,
          file_size_display: `${(f.size / 1024).toFixed(1)}KB`,
          issues: []
        })),
        recommendations: availableSlots > 0 
          ? [`현재 ${availableSlots}개의 파일만 추가로 올릴 수 있어요.`]
          : ['기존 파일을 삭제한 후 다시 시도해주세요.'],
        file_count_error: `파일은 최대 ${MAX_FILES}개까지 올릴 수 있어요. (현재 ${currentStats.fileCount}개 첨부됨, ${filesArray.length}개 추가 시도)`
      });
      setPendingFiles(null);
      setPreCheckLoading(false);
      return;
    }

    const combinedFiles = [
      ...(highSchoolData.files || []),
      ...filesArray,
    ];
    const totalSize = combinedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
    if (totalSize > SAENGGIBU_STORAGE_FILE_TOTAL_LIMIT_BYTES) {
      setPreCheckResult(buildStorageFileTotalLimitResult('교과세특(고등)', combinedFiles, totalSize));
      setPendingFiles(null);
      setPreCheckLoading(false);
      setShowPreCheckModal(true);
      return;
    }

    const preCheckBodyRiskFiles = filesArray.filter(file => file.size > 25 * 1024 * 1024);
    if (preCheckBodyRiskFiles.length > 0) {
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      actualHighSchoolFileUploadRef.current(filesArray, []);
      return;
    }
    
    try {
      const result = await preCheckFiles(filesArray, 'saenggibu_storage', isProUser);
      
      // 🆕 새 파일들의 페이지/슬라이드/문자수 합계 (백엔드에서 실제 추출한 값 사용)
      const newFileStats = normalizePreCheckStats(result.files_info || []);
      const newStats = summarizeFileStats(newFileStats);
      
      // 🆕 합산 제한 체크
      const totalPages = currentStats.pageCount + newStats.pageCount;
      const totalSlides = currentStats.slideCount + newStats.slideCount;
      const totalContentLength = currentStats.contentLength + newStats.contentLength;
      
      const exceededLimits = [];
      
      if (totalPages > MAX_SAENGGIBU_STORAGE_PDF_PAGES) {
        exceededLimits.push(`PDF 페이지: 현재 ${currentStats.pageCount}페이지 + 새 파일 ${newStats.pageCount}페이지 = 총 ${totalPages}페이지 (최대 ${MAX_SAENGGIBU_STORAGE_PDF_PAGES}페이지)`);
      }
      
      if (totalSlides > MAX_SAENGGIBU_STORAGE_PPT_SLIDES) {
        exceededLimits.push(`PPT 슬라이드: 현재 ${currentStats.slideCount}개 + 새 파일 ${newStats.slideCount}개 = 총 ${totalSlides}개 (최대 ${MAX_SAENGGIBU_STORAGE_PPT_SLIDES}개)`);
      }
      
      if (totalContentLength > MAX_CONTENT_LENGTH) {
        exceededLimits.push(`총 분석량: 현재 ${currentStats.contentLength.toLocaleString()}자 + 새 파일 ${newStats.contentLength.toLocaleString()}자 = 총 ${totalContentLength.toLocaleString()}자 (최대 ${MAX_CONTENT_LENGTH.toLocaleString()}자)`);
      }
      
      // 🆕 합산 초과 시 경고 추가
      if (exceededLimits.length > 0) {
        const modifiedResult = {
          ...result,
          needs_confirmation: true,
          overall_status: result.overall_status === 'ok' ? 'warning' : result.overall_status,
          recommendations: [
            ...(result.recommendations || []),
            ...exceededLimits.map(limit => limit)
          ],
          combined_warning: `이미 첨부된 파일과 합쳐서 제한을 넘어요. 초과분은 분석에서 빠져요.`
        };
        
        // 🆕 결과에 새 파일 통계 저장 (업로드 시 사용)
        modifiedResult._newFilesStats = newFileStats;
        
        setPreCheckResult(modifiedResult);
        setPendingFiles(filesArray);
        setPreCheckLoading(false);
        return;
      }
      
      // 🆕 결과에 새 파일 통계 저장 (업로드 시 사용)
      result._newFilesStats = newFileStats;
      
      // 검사 통과 & 확인 불필요 시 모달 없이 바로 업로드 진행
      if (result.can_proceed && !result.needs_confirmation) {
        setShowPreCheckModal(false);
        setPreCheckLoading(false);
        actualHighSchoolFileUploadRef.current(filesArray, result._newFilesStats);  // 🔧 통계도 함께 전달
        return;
      }
      
      // 확인이 필요하거나 진행 불가인 경우에만 모달 표시
      setPreCheckResult(result);
      setPendingFiles(filesArray);
      setPreCheckLoading(false);
      
    } catch (error) {
      console.error('파일 사전 검사 오류:', error);
      // 오류 시에도 업로드 진행 가능
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      actualHighSchoolFileUploadRef.current(filesArray, []);  // 🔧 통계 없이 진행
    }
  }, [highSchoolData.files, highSchoolData.filesStats, isProUser]);  // 🔧 합산 체크 + Pro 한도 동기화

  // 🔍 사전 검사 확인 후 업로드 진행
  const handlePreCheckConfirm = useCallback(() => {
    setShowPreCheckModal(false);
    if (pendingFile) {
      actualFileUploadRef.current(pendingFile);  // 🔧 ref를 통해 최신 함수 호출
      setPendingFile(null);
    } else if (pendingFiles) {
      const filesStats = preCheckResult?._newFilesStats || [];

      if (preCheckResult?._isPlanFile) {
        actualFileUploadRef.current(pendingFiles);
      }
      // 🆕 동아리 학생 파일인 경우 별도 처리
      else if (preCheckResult?._isClubStudentFile) {
        actualClubStudentFileUploadRef.current(pendingFiles, filesStats);
      } 
      // 🆕 진로 학생 파일인 경우 별도 처리
      else if (preCheckResult?._isCareerStudentFile) {
        actualCareerStudentFileUploadRef.current(pendingFiles, filesStats);
      }
      else {
        // 고등 모드: 파일 통계도 함께 전달
        actualHighSchoolFileUploadRef.current(pendingFiles, filesStats);
      }
      setPendingFiles(null);
    }
    setPreCheckResult(null);
  }, [pendingFile, pendingFiles, preCheckResult]);  // 🆕 preCheckResult 추가 (통계 전달용)

  // 🔍 사전 검사 취소
  const handlePreCheckCancel = useCallback(() => {
    setShowPreCheckModal(false);
    setPendingFile(null);
    setPendingFiles(null);
    setPreCheckResult(null);
  }, []);

  const formatApiErrorDetail = (result, fallback = '파일 분석에 실패했어요.') => {
    const detail = result?.detail || result?.message || result?.error;
    if (Array.isArray(detail)) {
      const evalUnitLimitError = detail.find((item) => {
        const loc = Array.isArray(item?.loc) ? item.loc : [];
        return loc.includes('eval_unit_count') && item?.type === 'less_than_equal';
      });
      if (evalUnitLimitError) {
        return `평가계획서에서 감지된 평가 단위가 너무 많아요. 한 번에 ${EVAL_UNIT_COUNT_MAX}개 이하로 선택해주세요.`;
      }
      return detail
        .map((item) => item?.msg || item?.message || item?.detail || JSON.stringify(item))
        .filter(Boolean)
        .join('\\n') || fallback;
    }
    if (detail && typeof detail === 'object') {
      return detail.msg || detail.message || JSON.stringify(detail);
    }
    return detail || fallback;
  };

  // 📎 실제 파일 업로드 처리 (과제/활동자료 분석 - AI 자동 분석)
  const actualFileUpload = useCallback(async (fileInput) => {
    const filesArray = Array.isArray(fileInput) ? fileInput : Array.from(fileInput ? [fileInput] : []);
    const file = filesArray[0];
    if (!file) return;

    // 자료함에서 고른 자료: 업로드 대신 /file/from-library 로 분석. 응답 shape 가 같아 아래 처리는 공유한다.
    const libraryMaterialId = isLibraryPseudoFile(file) ? file.materialId : null;

    const previousFileUpload = fileUpload;
    const previousRestoredMeta = [...restoredUploadedFilesMetaRef.current];
    restoredUploadedFilesMetaRef.current = [];
    cancelFileAnalysisRequest();
    const requestId = fileAnalysisRequestRef.current.id + 1;
    const requestCategory = selectedCategory;
    const controller = new AbortController();
    fileAnalysisRequestRef.current = {
      id: requestId,
      controller,
      category: requestCategory,
    };
    const isCurrentRequest = () => (
      fileAnalysisRequestRef.current.id === requestId
      && fileAnalysisRequestRef.current.category === requestCategory
      && !controller.signal.aborted
    );

    // 🆕 이미지 파일 감지
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
    const isImage = filesArray.some((f) => {
      const dotIndex = f.name.lastIndexOf('.');
      const fileExtension = dotIndex >= 0 ? f.name.substring(dotIndex).toLowerCase() : '';
      return imageExtensions.includes(fileExtension);
    });

    resetAnalysisReview();
    setFileUpload({
      file,
      files: filesArray,
      uploading: true,
      error: null,
      analysis: null,
      students: [],
      selectedStudents: [],
      selectedItems: [],
      appliedEvalUnitCount: 0,
      multiStudent: false,
      parsedStudents: [],
      selectedStudent: null,
      pendingReview: null,
      reviewConfirmed: false,
    });

    showToast(
      libraryMaterialId
        ? '자료함 자료를 읽고 있어요'
        : isImage
        ? '이미지를 읽고 있어요 (손글씨와 내용을 인식하고 있어요)'
        : filesArray.length > 1
        ? `${filesArray.length}개 파일을 함께 읽고 있어요`
        : '파일을 읽고 있어요',
      'info'
    );

    try {
      let response;
      let result;
      if (libraryMaterialId) {
        result = await analyzeSaenggibuFileFromLibrary(
          { materialId: libraryMaterialId, category: selectedCategory },
          { signal: controller.signal }
        );
        response = { ok: true };
      } else {
        // API URL 설정
        let API_BASE_URL;
        if (process.env.NODE_ENV === 'development') {
          API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        } else if (window.location.hostname.includes('whatquiztest')) {
          API_BASE_URL = 'https://quiz-api-test-397277209875.us-central1.run.app';
        } else {
          API_BASE_URL = 'https://quiz-api-397277209875.us-central1.run.app';
        }

        const formData = new FormData();
        const isPlanFileCategory = selectedCategory === '교과' || selectedCategory === '교과_고등';
        if (isPlanFileCategory) {
          filesArray.forEach((planFile) => formData.append('files', planFile));
        } else {
          formData.append('file', file);
        }
        formData.append('category', selectedCategory);

        const token = await auth.currentUser.getIdToken();
        response = await fetchWithRetry(
          `${API_BASE_URL}/api/saenggibu/file/upload`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData,
            signal: controller.signal,
          },
          {
            maxRetries: 0,
            timeoutMs: calcApiTimeout('saenggibu-file-analysis', {
              fileCount: filesArray.length,
              hasImages: isImage,
            }),
            retryOnTimeout: false,
          }
        );

        result = await response.json();
      }
      if (!isCurrentRequest()) return;

      if (response.ok && result.success) {
        const analyzedData = result.analyzed_data;
        
        // 🎯 카테고리별 자동 입력 처리
        if (selectedCategory === '행동발달') {
          // 🆕 다중 학생 감지 여부 확인
          if (analyzedData.multi_student && analyzedData.parsed_students?.length > 0) {
            // 여러 학생이 감지됨 → 선택 UI 표시
            setFileUpload(prev => ({
              ...prev,
              uploading: false,
              error: null,
              analysis: result,
              multiStudent: true,
              parsedStudents: analyzedData.parsed_students,
              selectedStudent: null,  // 아직 선택 안 됨
              selectedItems: [],
              appliedEvalUnitCount: 0,
              pendingReview: null,
              reviewConfirmed: false
            }));
            
            showToast(`학생 ${analyzedData.student_count || analyzedData.parsed_students.length}명 감지됨! 학생을 선택해주세요.`, 'info');
            return;  // 여기서 리턴 (자동 입력 안 함)
            
          } else if (analyzedData.student_traits) {
            // 단일 학생 → 확인 후 입력
            let promptText = analyzedData.student_traits;
            if (analyzedData.individual_traits) {
              promptText += ', ' + analyzedData.individual_traits;
            }

            openAnalysisReview({
              source: 'fileUpload',
              target: { type: 'prompt', category: selectedCategory },
              title: 'AI가 행동특성을 이렇게 읽었어요',
              text: promptText,
              rawText: result.extracted_text_preview || promptText,
              confidence: analyzedData.confidence,
              warnings: getAnalysisWarnings(result, analyzedData),
              summaryItems: [
                { label: '행동특성', value: analyzedData.student_traits },
                analyzedData.individual_traits ? { label: '개별특성', value: analyzedData.individual_traits } : null
              ],
              editLabel: '행동특성 수정'
            });
          } else {
            throw new Error('파일에서 행동특성을 찾지 못했어요.');
          }
          
        } else if (selectedCategory === '교과') {
          // 다중 활동 감지 여부 확인
          if (analyzedData.multi_activity && analyzedData.parsed_activities?.length > 0) {
            // 여러 활동/과목이 감지됨 → 선택 UI 표시
            const activityNames = buildPlanItemNames(analyzedData.parsed_activities, activityNameSuffix);
            const processedActivities = analyzedData.parsed_activities.map((act, index) => {
              return {
                ...act,
                name: activityNames[index],
                displayOrder: index + 1,
                records: act.activity_content || act.achievement_standard || ''
              };
            });
            
            const initialPlan = resolveInitialPlanSelection(processedActivities);
            setFileUpload(prev => ({
              ...prev,
              uploading: false,
              error: null,
              analysis: result,
              multiStudent: false,
              parsedStudents: processedActivities,
              selectedStudent: null,
              selectedItems: initialPlan.selectedItems,
              planMode: initialPlan.planMode,
              appliedEvalUnitCount: 0,
              pendingReview: null,
              reviewConfirmed: false
            }));
            if (initialPlan.planMode === 'single') {
              showToast(`평가 ${processedActivities.length}개를 찾았어요. 이번에 쓸 평가를 하나 골라주세요.`, 'info', 6000, null, PLAN_TOAST_OPTIONS);
            } else {
              openPlanItemsReviewRef.current?.(processedActivities, result);
            }
            return;
            
          } else if (hasSubjectAnalysisData(analyzedData)) {
            // 단일 활동 → 확인 후 입력
            const { activityName, activityContent } = buildSubjectActivityDraft(analyzedData);

            openAnalysisReview({
              source: 'fileUpload',
              target: {
                type: 'subject',
                category: selectedCategory,
                uiMode,
                activityName,
                subject: analyzedData.subject
              },
              title: 'AI가 평가계획서를 이렇게 읽었어요',
              description: '이 내용은 학생별 기록을 해석하는 기준으로만 사용돼요. 맞게 읽었는지 확인해주세요.',
              text: activityContent,
              rawText: buildSubjectReviewRawText(activityName, activityContent) || result.extracted_text_preview || '',
              confidence: analyzedData.confidence,
              warnings: getAnalysisWarnings(result, analyzedData),
              summaryItems: buildSubjectAnalysisSummaryItems(analyzedData),
              editFields: [
                { key: 'activityName', label: '활동명(성취기준)', value: activityName, rows: 5 },
                { key: 'text', label: '평가요소', value: activityContent, rows: 8 }
              ],
              editLabel: '평가요소 수정'
            });
          }
          
        } else if (selectedCategory === '창체') {
          if (analyzedData.multi_student && analyzedData.parsed_students?.length > 0) {
            setCreativeInputProfile('detailed_evidence');
            setGenerationData(prev => ({ ...prev, generationCount: 1 }));
            const processedStudents = analyzedData.parsed_students.map((student, index) => ({
              ...student,
              id: student.id || `student_${index + 1}`,
              name: student.name || `학생 ${index + 1}`,
              records: buildCreativeStudentDraft(student, index),
              confidence: student.confidence || analyzedData.confidence,
            }));
            setFileUpload(prev => ({
              ...prev,
              uploading: false,
              error: null,
              analysis: result,
              multiStudent: true,
              parsedStudents: processedStudents,
              selectedStudent: null,
              selectedItems: [],
              pendingReview: null,
              reviewConfirmed: false,
            }));
            showToast(`학생 ${processedStudents.length}명의 활동 기록을 찾았어요. 한 명을 선택해주세요.`, 'info');
            return;
          }

          const creativeDraft = buildCreativeAnalysisDraft(analyzedData);
          const nextCreativeProfile = (
            analyzedData.file_type === 'student_work'
            || analyzedData.file_type === 'mixed'
            || analyzedData.has_concrete_details
          )
            ? 'detailed_evidence'
            : 'activity_list';
          setCreativeInputProfile(nextCreativeProfile);
          setGenerationData(prev => ({
            ...prev,
            generationCount: nextCreativeProfile === 'detailed_evidence'
              ? 1
              : Math.max(2, prev.generationCount),
          }));
          const hasCreativeContent = Boolean(
            analyzedData.activities
            || analyzedData.summary
            || analyzedData.activity_details?.length
          );
          if (creativeDraft && hasCreativeContent) {
            openAnalysisReview({
              source: 'fileUpload',
              target: {
                type: 'creativePrompt',
                category: selectedCategory,
                fileType: analyzedData.file_type,
              },
              title: 'AI가 창체 활동 근거를 이렇게 읽었어요',
              description: '활동명뿐 아니라 학생의 역할, 과정, 산출물을 함께 정리했어요. 사실과 다른 부분만 고쳐주세요.',
              text: creativeDraft,
              rawText: creativeDraft,
              rawSummaryLabel: '입력될 활동 내용 보기',
              confidence: analyzedData.confidence,
              warnings: getAnalysisWarnings(result, analyzedData),
              summaryItems: getCreativeAnalysisSummaryItems(analyzedData),
              editFields: [
                {
                  key: 'text',
                  label: '입력할 활동 내용',
                  value: creativeDraft,
                  rows: 12,
                },
              ],
              editLabel: '창체 활동 내용 수정',
              confirmLabel: generationData.prompt.trim()
                ? '기존 내용에 추가하기'
                : '이 내용으로 넣기',
            });
          } else {
            throw new Error('파일에서 창체 활동 근거를 찾지 못했어요.');
          }
          
        } else if (selectedCategory === '교과_고등') {
          // 다중 과목 감지 여부 확인
          if (analyzedData.multi_subject && analyzedData.parsed_subjects?.length > 0) {
            const subjectNames = buildPlanItemNames(analyzedData.parsed_subjects, achievementCodeSuffix);
            const processedSubjects = analyzedData.parsed_subjects.map((subj, index) => {
              return {
                ...subj,
                name: subjectNames[index],
                displayOrder: index + 1,
                records: subj.achievement_standard || '',
                traits: subj.evaluation_criteria || subj.evaluation_method || ''
              };
            });
            
            const initialPlan = resolveInitialPlanSelection(processedSubjects);
            setFileUpload(prev => ({
              ...prev,
              uploading: false,
              error: null,
              analysis: result,
              multiStudent: false,
              parsedStudents: processedSubjects,
              selectedStudent: null,
              selectedItems: initialPlan.selectedItems,
              planMode: initialPlan.planMode,
              appliedEvalUnitCount: 0,
              pendingReview: null,
              reviewConfirmed: false
            }));
            if (initialPlan.planMode === 'single') {
              showToast(`평가 ${processedSubjects.length}개를 찾았어요. 이번에 쓸 평가를 하나 골라주세요.`, 'info', 6000, null, PLAN_TOAST_OPTIONS);
            } else {
              openPlanItemsReviewRef.current?.(processedSubjects, result);
            }
            return;
            
          } else if (analyzedData.achievement_standard || analyzedData.evaluation_criteria) {
            // 단일 과목 → 확인 후 입력
            const finalAchievementText = buildHighSchoolDraft(analyzedData);

            openAnalysisReview({
              source: 'fileUpload',
              target: { type: 'highSchool', category: selectedCategory },
              title: 'AI가 평가계획서를 이렇게 읽었어요',
              description: '이 내용은 학생별 기록을 해석하는 기준으로만 사용돼요. 맞게 읽었는지 확인해주세요.',
              text: finalAchievementText,
              rawText: result.extracted_text_preview || finalAchievementText,
              confidence: analyzedData.confidence,
              warnings: getAnalysisWarnings(result, analyzedData),
              summaryItems: [
                analyzedData.subject ? { label: '과목', value: analyzedData.subject } : null,
                analyzedData.unit ? { label: '단원', value: analyzedData.unit } : null,
                analyzedData.evaluation_ratio ? { label: '평가비율', value: analyzedData.evaluation_ratio } : null,
                analyzedData.evaluation_period ? { label: '평가시기', value: analyzedData.evaluation_period } : null
              ],
              editLabel: '성취기준/평가정보 수정'
            });
          } else {
            showToast('파일에서 성취기준을 찾지 못했어요. 직접 입력해주세요.', 'warning');
          }
        } else if (selectedCategory === '동아리') {
          // 🆕 동아리 계획서 분석 결과 처리
          if (analyzedData.club_name || analyzedData.main_activities || analyzedData.yearly_plan) {
            // 동아리 정보 조합 후 확인
            const clubDescription = buildClubDraft(analyzedData);
            const planStorageFile = await uploadSingleSaenggibuStorageFile(file);
            if (!isCurrentRequest()) return;

            openAnalysisReview({
              source: 'fileUpload',
              target: {
                type: 'club',
                category: selectedCategory,
                planFile: file,
                planFileContent: {
                  ...planStorageFile,
                  analyzed: true
                }
              },
              title: 'AI가 동아리 계획서를 이렇게 읽었어요',
              text: clubDescription,
              rawText: result.extracted_text_preview || clubDescription,
              confidence: analyzedData.confidence,
              warnings: getAnalysisWarnings(result, analyzedData),
              summaryItems: [
                analyzedData.club_name ? { label: '동아리명', value: analyzedData.club_name } : null,
                Array.isArray(analyzedData.main_activities) ? { label: '주요활동', value: analyzedData.main_activities.join(', ') } : null,
                analyzedData.objectives ? { label: '목표', value: analyzedData.objectives } : null
              ],
              editLabel: '동아리 정보 수정'
            });
          } else {
            const planStorageFallback = await uploadSingleSaenggibuStorageFile(file);
            if (!isCurrentRequest()) return;
            setClubData(prev => ({
              ...prev,
              planFile: file,
              planFileContent: {
                ...planStorageFallback,
                analyzed: false
              }
            }));
            showToast('파일에서 동아리 정보를 찾지 못했어요. 직접 입력해주세요. (파일은 첨부됨)', 'warning');
          }
        } else if (selectedCategory === '진로') {
          // 🆕 진로 학생 정보 분석 결과 처리
          if (analyzedData.hoped_career || analyzedData.hoped_major || analyzedData.summary) {
            // 진로 정보 조합 후 확인
            const careerInfo = buildCareerDraft(analyzedData);
            const infoStorageFile = await uploadSingleSaenggibuStorageFile(file);
            if (!isCurrentRequest()) return;

            openAnalysisReview({
              source: 'fileUpload',
              target: {
                type: 'career',
                category: selectedCategory,
                infoFile: file,
                infoFileContent: {
                  ...infoStorageFile,
                  analyzed: true
                }
              },
              title: 'AI가 진로 정보를 이렇게 읽었어요',
              text: careerInfo,
              rawText: result.extracted_text_preview || careerInfo,
              confidence: analyzedData.confidence,
              warnings: getAnalysisWarnings(result, analyzedData),
              summaryItems: [
                analyzedData.hoped_major ? { label: '희망 학과', value: analyzedData.hoped_major } : null,
                analyzedData.hoped_career ? { label: '희망 진로', value: analyzedData.hoped_career } : null,
                analyzedData.summary ? { label: '요약', value: analyzedData.summary } : null
              ],
              editLabel: '진로 정보 수정'
            });
          } else {
            const infoStorageFallback = await uploadSingleSaenggibuStorageFile(file);
            if (!isCurrentRequest()) return;
            setCareerData(prev => ({
              ...prev,
              infoFile: file,
              infoFileContent: {
                ...infoStorageFallback,
                analyzed: false
              }
            }));
            showToast('파일에서 진로 정보를 찾지 못했어요. 직접 입력해주세요. (파일은 첨부됨)', 'warning');
          }
        }
        
        // 파일 업로드 상태 업데이트
        setFileUpload(prev => ({
          ...prev,
          uploading: false,
          error: null,
          analysis: result
        }));
        
        // 에러가 있으면 표시
        if (analyzedData.error) {
          showToast(analyzedData.error, 'warning');
        }
        
      } else {
        throw new Error(formatApiErrorDetail(result));
      }
    } catch (error) {
      if (error?.name === 'AbortError' || !isCurrentRequest()) return;
      console.error('파일 업로드 실패:', error);
      const canRestorePreviousFile = Boolean(
        previousFileUpload.analysis
        || previousFileUpload.file
        || previousRestoredMeta.length > 0
      );
      if (canRestorePreviousFile) {
        restoredUploadedFilesMetaRef.current = previousRestoredMeta;
        setFileUpload({
          ...previousFileUpload,
          uploading: false,
          error: null,
        });
        showToast('새 파일을 읽지 못해 이전 첨부 내용을 그대로 유지했어요.', 'error');
      } else {
        setFileUpload(prev => ({
          ...prev,
          uploading: false,
          error: error.message
        }));
        showToast(error.message, 'error');
      }
    } finally {
      if (isCurrentRequest()) {
        fileAnalysisRequestRef.current = {
          id: requestId,
          controller: null,
          category: requestCategory,
        };
      }
    }
  }, [cancelFileAnalysisRequest, fileUpload, selectedCategory, uiMode, auth.currentUser, generationData.prompt, showToast, openAnalysisReview, resetAnalysisReview, getAnalysisWarnings, buildSubjectActivityDraft, buildHighSchoolDraft, buildClubDraft, buildCareerDraft, uploadSingleSaenggibuStorageFile]);
  
  // 🔧 ref 업데이트 (최신 함수 참조 유지)
  actualFileUploadRef.current = actualFileUpload;

  // 📎 파일 업로드 처리 (사전 검사 포함)
  const handleFileUpload = useCallback(async (fileInput) => {
    if (!isProUser) {
      setShowProModal(true);
      return;
    }

    const filesArray = Array.isArray(fileInput) ? fileInput : Array.from(fileInput ? [fileInput] : []);
    if (filesArray.length === 0) return;

    const isPlanFileCategory = selectedCategory === '교과' || selectedCategory === '교과_고등';
    if (!isPlanFileCategory) {
      performFilePreCheck(filesArray[0]);
      return;
    }

    // 자료함에서 고른 자료는 실제 파일과 함께 묶어 올릴 수 없어, 새 파일을 올리면 교체된다.
    const currentFiles = (fileUpload.files?.length > 0
      ? fileUpload.files
      : (fileUpload.file ? [fileUpload.file] : [])
    ).filter((f) => !isLibraryPseudoFile(f));
    const combinedFiles = [...currentFiles, ...filesArray];
    const totalSize = combinedFiles.reduce((sum, f) => sum + (f.size || 0), 0);

    if (combinedFiles.length > EVAL_PLAN_MAX_FILES) {
      showToast(`평가계획서는 최대 ${EVAL_PLAN_MAX_FILES}개까지 올릴 수 있어요.`, 'error');
      return;
    }
    if (totalSize > EVAL_PLAN_TOTAL_LIMIT_BYTES) {
      showToast(`평가계획서 파일 합계가 ${formatMB(totalSize)}예요. 30MB 이하로 줄여주세요.`, 'error');
      return;
    }

    setPreCheckLoading(true);
    setShowPreCheckModal(true);
    try {
      const result = await preCheckFiles(filesArray, 'saenggibu', isProUser);
      if (result.can_proceed && !result.needs_confirmation) {
        setShowPreCheckModal(false);
        setPreCheckLoading(false);
        actualFileUploadRef.current(combinedFiles);
        return;
      }

      setPreCheckResult({ ...result, _isPlanFile: true });
      setPendingFiles(combinedFiles);
      setPreCheckLoading(false);
    } catch (error) {
      console.error('평가계획서 파일 사전 검사 오류:', error);
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      actualFileUploadRef.current(combinedFiles);
    }
  }, [fileUpload.file, fileUpload.files, isProUser, performFilePreCheck, selectedCategory, showToast]);

  // 📚 자료함에서 고르기 — 본인 자료라 PRO 게이트·사전검사 없이 바로 분석 (Phase 4b)
  const handleLibraryMaterialPick = useCallback((material) => {
    if (!material?.id || fileUpload.uploading) return;
    actualFileUploadRef.current([createLibraryPseudoFile(material)]);
  }, [fileUpload.uploading]);

  // 📎 파일 제거 처리
  const handleFileRemove = useCallback(() => {
    cancelFileAnalysisRequest();
    setShowPreCheckModal(false);
    setPreCheckResult(null);
    setPendingFile(null);
    setPendingFiles(null);
    setPreCheckLoading(false);
    restoredUploadedFilesMetaRef.current = [];
    const previousCreativeDraft = appliedCreativeFileDraftRef.current;
    if (previousCreativeDraft) {
      const nextPrompt = removeCreativeAnalysisFromPrompt(
        generationData.prompt,
        previousCreativeDraft
      );
      const nextProfile = classifyCreativeInputProfile(nextPrompt);
      setCreativeInputProfile(nextProfile);
      setGenerationData(prev => ({
        ...prev,
        prompt: nextPrompt,
        generationCount: nextProfile === 'detailed_evidence'
          ? 1
          : Math.max(2, prev.generationCount),
      }));
    }
    appliedCreativeFileDraftRef.current = '';
    setFileUpload({
      file: null,
      files: [],
      uploading: false,
      error: null,
      analysis: null,
      students: [],
      selectedStudents: [],
      multiStudent: false,
      parsedStudents: [],
      selectedStudent: null,
      pendingReview: null,
      reviewConfirmed: false
    });
    resetAnalysisReview();
    showToast('파일을 제거했어요.', 'info');
  }, [cancelFileAnalysisRequest, generationData.prompt, resetAnalysisReview, showToast]);

  const handlePlanFileRemove = useCallback((indexToRemove) => {
    if (fileUpload.uploading) return;
    const currentFiles = fileUpload.files?.length > 0
      ? fileUpload.files
      : (fileUpload.file ? [fileUpload.file] : []);
    const remainingFiles = currentFiles.filter((_, index) => index !== indexToRemove);
    if (remainingFiles.length === 0) {
      handleFileRemove();
      return;
    }
    showToast('남은 평가계획서로 다시 분석할게요.', 'info');
    actualFileUploadRef.current(remainingFiles);
  }, [fileUpload.file, fileUpload.files, fileUpload.uploading, handleFileRemove, showToast]);

  const renderPlanFileList = useCallback(() => {
    const planFiles = fileUpload.files?.length > 0
      ? fileUpload.files
      : (fileUpload.file ? [fileUpload.file] : []);
    if (planFiles.length <= 1) return null;

    return (
      <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {planFiles.map((planFile, index) => (
          <div key={`${planFile.name}-${index}`} style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 8px',
            backgroundColor: 'var(--whatquiz-white, #fff)',
            borderRadius: '999px',
            border: '1px solid var(--whatquiz-gray-200)',
            fontSize: '12px',
            maxWidth: '180px'
          }}>
            <span style={{ color: 'var(--whatquiz-gray-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {planFile.name}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handlePlanFileRemove(index);
              }}
              disabled={fileUpload.uploading}
              style={{
                background: 'none',
                border: 'none',
                color: fileUpload.uploading ? 'var(--whatquiz-gray-400)' : 'var(--whatquiz-error)',
                cursor: fileUpload.uploading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                padding: '0 2px',
                lineHeight: 1
              }}
              aria-label={`${planFile.name} 삭제`}
            >×</button>
          </div>
        ))}
      </div>
    );
  }, [fileUpload.file, fileUpload.files, fileUpload.uploading, handlePlanFileRemove]);
  
  const buildMergedSubjectPlanDraft = useCallback((items = []) => {
    const drafts = items.map((item) => ({ item, ...buildSubjectActivityDraft(item) }));
    if (drafts.length === 1) {
      return {
        activityName: drafts[0].activityName,
        activityContent: drafts[0].activityContent,
      };
    }

    return {
      activityName: drafts.map(({ item, activityName }, index) => {
        const label = item.subject ? `${item.subject} ${index + 1}` : `활동 ${index + 1}`;
        return `[활동 ${index + 1}] ${label}
${activityName || item.name || ''}`.trim();
      }).join('\n\n'),
      activityContent: drafts.map(({ item, activityContent }, index) => {
        const header = `[활동 ${index + 1}${item.subject ? ` - ${item.subject}` : ''}]`;
        return `${header}
${activityContent || item.records || ''}`.trim();
      }).join('\n\n'),
    };
  }, [buildSubjectActivityDraft]);

  const buildMergedHighSchoolPlanDraft = useCallback((items = []) => {
    if (items.length === 1) return buildHighSchoolDraft(items[0]);
    return items.map((item, index) => {
      const headerParts = [`[평가 단위 ${index + 1}]`];
      if (item.subject) headerParts.push(item.subject);
      if (item.unit) headerParts.push(item.unit);
      const body = buildHighSchoolDraft(item);
      return `${headerParts.join(' ')}\n${body}`.trim();
    }).join('\n\n');
  }, [buildHighSchoolDraft]);

  const applyPlanItemsToFields = useCallback((items = []) => {
    if (selectedCategory === '교과') {
      const { activityName, activityContent } = buildMergedSubjectPlanDraft(items);
      setSubjectData(prev => ({ ...prev, activityName, activityContent }));
    } else if (selectedCategory === '교과_고등') {
      setHighSchoolData(prev => ({ ...prev, achievementStandard: buildMergedHighSchoolPlanDraft(items) }));
    }
    setFileUpload(prev => ({ ...prev, appliedEvalUnitCount: items.length }));
  }, [buildMergedHighSchoolPlanDraft, buildMergedSubjectPlanDraft, selectedCategory]);

  const openPlanItemsReview = useCallback((items = [], analysisResult = null) => {
    if (!items.length) return;
    const analysisForReview = analysisResult || fileUpload.analysis;
    const planChoice = items.length > 1
      ? {
          confirmLabel: `${items.length}개 합쳐서 쓰기`,
          resetLabel: '하나씩 쓰기',
          resetAction: 'single',
          description: '평가들을 세특 한 편에 녹여 써요. 평가마다 따로 쓰려면 하나씩 쓰기를 눌러주세요.',
        }
      : { resetLabel: '다시 선택' };
    if (selectedCategory === '교과') {
      const { activityName, activityContent } = buildMergedSubjectPlanDraft(items);
      openAnalysisReview({
        source: 'fileUpload',
        target: {
          type: 'subject',
          category: selectedCategory,
          uiMode,
          activityName,
          subject: items.map(item => item.subject).filter(Boolean).join(', '),
          evalUnitCount: items.length,
        },
        title: 'AI가 평가계획서들을 이렇게 합쳐 읽었어요',
        description: '이 내용은 학생별 기록을 해석하는 기준으로만 사용돼요. 맞게 읽었는지 확인해주세요.',
        text: activityContent,
        rawText: buildSubjectReviewRawText(activityName, activityContent),
        confidence: analysisForReview?.analyzed_data?.confidence,
        warnings: getAnalysisWarnings(analysisForReview, analysisForReview?.analyzed_data || {}),
        itemLabel: `${items.length}개 평가 단위`,
        ...planChoice,
        summaryItems: items.slice(0, 5).map((item, index) => ({
          label: `선택 ${index + 1}`,
          value: item.name || item.subject || item.activity_name || `평가 단위 ${index + 1}`,
        })),
        editFields: [
          { key: 'activityName', label: '활동명(성취기준)', value: activityName, rows: 5 },
          { key: 'text', label: '평가요소', value: activityContent, rows: 8 }
        ],
        editLabel: '평가요소 수정'
      });
      return;
    }
    if (selectedCategory === '교과_고등') {
      const achievementText = buildMergedHighSchoolPlanDraft(items);
      openAnalysisReview({
        source: 'fileUpload',
        target: { type: 'highSchool', category: selectedCategory, evalUnitCount: items.length },
        title: 'AI가 평가계획서들을 이렇게 합쳐 읽었어요',
        description: '이 내용은 학생별 기록을 해석하는 기준으로만 사용돼요. 맞게 읽었는지 확인해주세요.',
        text: achievementText,
        rawText: achievementText,
        confidence: analysisForReview?.analyzed_data?.confidence,
        warnings: getAnalysisWarnings(analysisForReview, analysisForReview?.analyzed_data || {}),
        itemLabel: `${items.length}개 평가 단위`,
        ...planChoice,
        summaryItems: items.slice(0, 5).map((item, index) => ({
          label: `선택 ${index + 1}`,
          value: item.name || item.subject || item.unit || `평가 단위 ${index + 1}`,
        })),
        editLabel: '성취기준/평가정보 수정'
      });
    }
  }, [buildMergedHighSchoolPlanDraft, buildMergedSubjectPlanDraft, fileUpload.analysis, getAnalysisWarnings, openAnalysisReview, selectedCategory, uiMode]);

  openPlanItemsReviewRef.current = openPlanItemsReview;

  const handleSelectAllPlanItems = useCallback(() => {
    const allItems = fileUpload.parsedStudents || [];
    if (!allItems.length) return;
    setFileUpload(prev => ({
      ...prev,
      selectedItems: allItems,
      planMode: 'merge',
      selectedStudent: allItems.length === 1 ? allItems[0] : null,
      appliedEvalUnitCount: 0,
      pendingReview: null,
      reviewConfirmed: false,
    }));
    openPlanItemsReview(allItems);
  }, [fileUpload.parsedStudents, openPlanItemsReview]);


  const fileBatchStudentCount = fileUpload.multiStudent
    ? countBatchableFileStudents(selectedCategory, fileUpload.parsedStudents)
    : 0;
  const canMakeFileStudentsBatch = fileBatchStudentCount >= 2;
  const fileBatchShownRef = useRef(null);
  useEffect(() => {
    if (!canMakeFileStudentsBatch || fileBatchShownRef.current === fileUpload.parsedStudents) return;
    fileBatchShownRef.current = fileUpload.parsedStudents;
    trackSaenggibuUxEvent('saenggibu_multi_student_file', {
      stage: 'shown',
      category: selectedCategory,
      found_count: fileBatchStudentCount,
      is_pro: isProUser,
    });
  }, [canMakeFileStudentsBatch, fileBatchStudentCount, fileUpload.parsedStudents, isProUser, selectedCategory]);

  const handleMakeFileStudentsBatch = useCallback(() => {
    if (!canMakeFileStudentsBatch) return;
    trackSaenggibuUxEvent('saenggibu_multi_student_file', {
      stage: 'to_batch',
      category: selectedCategory,
      found_count: fileBatchStudentCount,
      is_pro: isProUser,
    });
    if (!isProUser) {
      setShowProModal(true);
      return;
    }
    setActiveInitialBatchState(buildBatchStateFromFileStudents({
      category: selectedCategory,
      students: fileUpload.parsedStudents,
      wordCount: generationData.wordCount,
    }));
    setBatchStep(1);
    setBatchData(null);
    // "한 명을 선택해주세요" 같은 단일 안내가 다중 화면에 남지 않게
    if (typeof clearToasts === 'function') clearToasts();
    setUiMode('batch');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [canMakeFileStudentsBatch, clearToasts, fileBatchStudentCount, fileUpload.parsedStudents, generationData.wordCount, isProUser, selectedCategory, setUiMode]);

  // 🆕 다중 항목 파일에서 항목 선택 처리 (학생/과목/활동)
  const handleStudentSelect = useCallback((item) => {
    if (selectedCategory === '교과' || selectedCategory === '교과_고등') {
      const nextItems = nextPlanSelection(fileUpload.planMode, fileUpload.selectedItems || [], item);
      setFileUpload(prev => ({
        ...prev,
        selectedItems: nextItems,
        selectedStudent: nextItems.length === 1 ? nextItems[0] : null,
        appliedEvalUnitCount: nextItems.length,
        pendingReview: null,
        reviewConfirmed: false,
      }));
      if (nextItems.length > 0) {
        applyPlanItemsToFields(nextItems);
      } else if (selectedCategory === '교과') {
        setSubjectData(prev => ({ ...prev, activityName: '', activityContent: '' }));
      } else {
        setHighSchoolData(prev => ({ ...prev, achievementStandard: '' }));
      }
      resetAnalysisReview();
      if (fileUpload.planMode === 'single') {
        showToast(`'${item.name}' 평가를 넣었어요.`, 'info', undefined, null, PLAN_TOAST_OPTIONS);
      }
      return;
    }

    // 이미 선택된 항목이면 선택 해제
    if (fileUpload.selectedStudent?.id === item.id) {
      setFileUpload(prev => ({
        ...prev,
        selectedStudent: null
      }));
      resetAnalysisReview();
      if (selectedCategory !== '창체') {
        setGenerationData(prev => ({ ...prev, prompt: '' }));
      }
      showToast('선택을 해제했어요.', 'info');
      return;
    }

    setFileUpload(prev => ({
      ...prev,
      selectedStudent: item
    }));
    if (canMakeFileStudentsBatch) {
      trackSaenggibuUxEvent('saenggibu_multi_student_file', {
        stage: 'pick',
        category: selectedCategory,
        found_count: fileBatchStudentCount,
      });
    }
    if (selectedCategory === '창체') {
      setCreativeInputProfile('detailed_evidence');
      setGenerationData(prev => ({ ...prev, generationCount: 1 }));
    }

    // 행발/창체: 확인 후 프롬프트에 입력
    let promptText = '';
    if (item.records) {
      promptText = item.records;
    }
    if (item.traits) {
      promptText += promptText ? '\n\n특성: ' + item.traits : item.traits;
    }
    if (item.individual_traits) {
      promptText += ', ' + item.individual_traits;
    }
    openAnalysisReview({
      source: 'fileUpload',
      target: {
        type: selectedCategory === '창체' ? 'creativePrompt' : 'prompt',
        category: selectedCategory,
      },
      title: selectedCategory === '창체'
        ? 'AI가 선택한 학생의 활동 근거를 이렇게 읽었어요'
        : 'AI가 선택한 학생 내용을 이렇게 읽었어요',
      description: selectedCategory === '창체'
        ? '선택한 학생에게 연결된 역할, 과정, 산출물만 모았어요. 사실과 다른 부분만 고쳐주세요.'
        : undefined,
      text: promptText.trim(),
      rawText: promptText.trim(),
      confidence: item.confidence || fileUpload.analysis?.analyzed_data?.confidence,
      warnings: getAnalysisWarnings(fileUpload.analysis, item),
      itemLabel: item.name,
      resetLabel: '다시 선택',
      summaryItems: [
        item.name ? { label: '학생', value: item.name } : null,
        selectedCategory === '창체' && item.activities
          ? { label: '활동', value: item.activities }
          : null,
        item.traits ? { label: '특성', value: item.traits } : null,
      ],
      editFields: selectedCategory === '창체'
        ? [{ key: 'text', label: '입력할 학생 활동 내용', value: promptText.trim(), rows: 12 }]
        : undefined,
      editLabel: selectedCategory === '창체' ? '학생 활동 내용 수정' : '학생 내용 수정',
      confirmLabel: selectedCategory === '창체' && generationData.prompt.trim()
        ? '기존 내용에 추가하기'
        : '이 내용으로 넣기',
    });
  }, [applyPlanItemsToFields, canMakeFileStudentsBatch, fileBatchStudentCount, fileUpload.planMode, fileUpload.selectedItems, fileUpload.selectedStudent, fileUpload.analysis, generationData.prompt, selectedCategory, showToast, resetAnalysisReview, openAnalysisReview, getAnalysisWarnings]);

  const switchPlanToSingle = useCallback(() => {
    const allItems = fileUpload.parsedStudents || [];
    const first = (fileUpload.selectedItems || [])[0] || allItems[0];
    if (!first) return;
    setFileUpload(prev => ({
      ...prev,
      planMode: 'single',
      selectedItems: [first],
      selectedStudent: first,
      appliedEvalUnitCount: 1,
      pendingReview: null,
      reviewConfirmed: false,
    }));
    applyPlanItemsToFields([first]);
    resetAnalysisReview();
    showToast('첫 번째 평가를 넣었어요. 다른 평가는 목록에서 바로 바꿀 수 있어요.', 'info', undefined, null, PLAN_TOAST_OPTIONS);
  }, [applyPlanItemsToFields, fileUpload.parsedStudents, fileUpload.selectedItems, resetAnalysisReview, showToast]);

  const renderPlanItemChips = (emptyHint) => {
    const items = fileUpload.parsedStudents || [];
    if (items.length < 2) return null;
    const selectedItems = fileUpload.selectedItems || [];
    const isSingle = fileUpload.planMode === 'single';
    const isSelected = (item) => selectedItems.some(selected => selected.id === item.id);
    let headline = emptyHint;
    if (isSingle) {
      headline = selectedItems[0] ? '하나씩 쓰기 · 고른 평가 1개로 써요' : `평가 ${items.length}개 중 이번에 쓸 평가를 골라주세요`;
    } else if (selectedItems.length > 0) {
      headline = `${selectedItems.length}개 합쳐서 쓰기`;
    }
    const subButtonStyle = {
      minHeight: '34px',
      padding: '7px 12px',
      borderRadius: '999px',
      border: '1px solid var(--whatquiz-gray-200)',
      backgroundColor: 'var(--whatquiz-white, #fff)',
      color: 'var(--whatquiz-gray-700)',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer'
    };
    return (
      <div style={{
        marginTop: '10px',
        padding: '14px',
        backgroundColor: '#f8f9fa',
        border: 'none',
        borderRadius: '16px'
      }}>
        <div style={{
          fontSize: '13px',
          color: 'var(--whatquiz-gray-700)',
          marginBottom: '8px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          {headline}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleStudentSelect(item)}
              aria-pressed={isSelected(item)}
              style={{
                minHeight: '36px',
                padding: '8px 14px',
                borderRadius: '999px',
                border: isSelected(item) ? 'none' : '1px solid #bcd9ff',
                background: isSelected(item) ? 'var(--whatquiz-primary)' : 'linear-gradient(135deg, #ffffff 0%, #eef6ff 100%)',
                color: isSelected(item) ? 'var(--whatquiz-white, #fff)' : 'var(--whatquiz-gray-700)',
                fontSize: '13px',
                fontWeight: isSelected(item) ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: isSelected(item) ? '0 4px 12px rgba(49, 130, 246, 0.20)' : '0 6px 16px rgba(49, 130, 246, 0.12)'
              }}
            >
              {isSelected(item) && <CheckCircleIcon size={12} color="currentColor" />}
              {item.name}
            </button>
          ))}
        </div>
        <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {isSingle ? (
            <button type="button" onClick={handleSelectAllPlanItems} style={subButtonStyle}>
              모두 합쳐서 쓰기
            </button>
          ) : (
            <>
              {selectedItems.length < items.length && (
                <button type="button" onClick={handleSelectAllPlanItems} style={subButtonStyle}>
                  전체 선택
                </button>
              )}
              <button type="button" onClick={switchPlanToSingle} style={subButtonStyle}>
                하나씩 쓰기
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  // 🎯 드래그 앤 드롭 핸들러
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isProUser && !fileUpload.uploading) {
      setIsDragging(true);
    }
  }, [isProUser, fileUpload.uploading]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!isProUser) {
      setShowProModal(true);
      return;
    }

    if (fileUpload.uploading) return;

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      const blockedFile = files.find(isClearlyBlockedUploadFile);
      if (blockedFile) {
        showToast('코드/문서/이미지 파일만 올릴 수 있어요.', 'error');
        return;
      }
      if (selectedCategory === '교과' || selectedCategory === '교과_고등') {
        handleFileUpload(files);
      } else {
        handleFileUpload(files[0]);
      }
    }
  }, [isProUser, fileUpload.uploading, handleFileUpload, selectedCategory, showToast]);

  // 🎯 고등 모드: 실제 파일 업로드 핸들러
  const actualHighSchoolFileUpload = useCallback(async (files, filesStats = []) => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      setShowLoginPrompt(true);
      return;
    }

    try {
      const filesArray = Array.isArray(files) ? files : Array.from(files);
      setHighSchoolUploadProgress({ current: 0, total: filesArray.length, percent: 0 });

      const filesData = [];
      for (let index = 0; index < filesArray.length; index += 1) {
        const file = filesArray[index];
        const uploaded = await uploadSaenggibuAttachment(file, userId, (percent) => {
          setHighSchoolUploadProgress({
            current: index + 1,
            total: filesArray.length,
            percent,
          });
        });
        filesData.push(uploaded);
      }

      // 🆕 상태 업데이트 (파일 통계도 함께 저장)
      setHighSchoolData(prev => ({
        ...prev,
        files: [...prev.files, ...filesArray],
        filesContent: [...prev.filesContent, ...filesData],
        filesStats: [...(prev.filesStats || []), ...filesStats]  // 🆕 통계 저장
      }));

      showToast(`${filesArray.length}개 파일을 분석할 준비가 됐어요.`, 'success');
    } catch (error) {
      console.error('파일 업로드 오류:', error);
      showToast('파일을 분석할 수 없었어요. 네트워크를 확인한 뒤 다시 시도해주세요.', 'error');
    } finally {
      setHighSchoolUploadProgress(null);
    }
  }, [auth.currentUser, showToast]);
  
  // 🔧 ref 업데이트 (최신 함수 참조 유지)
  actualHighSchoolFileUploadRef.current = actualHighSchoolFileUpload;

  // 🎯 고등 모드: 파일 업로드 핸들러 (사전 검사 포함)
  const handleHighSchoolFileUpload = useCallback((files) => {
    if (!auth.currentUser) {
      setShowLoginPrompt(true);
      return;
    }

    if (highSchoolUploadProgress) {
      showToast('파일을 분석하는 중이에요. 잠시만 기다려주세요.', 'info');
      return;
    }

    // 🆕 파일 개수 체크 (기본 첨부 한도와 동기화)
    const maxFiles = MAX_FILES_DEFAULT;
    const currentFileCount = highSchoolData.files?.length || 0;
    if (currentFileCount + files.length > maxFiles) {
      showToast(`최대 ${maxFiles}개의 파일만 첨부할 수 있어요. (현재 ${currentFileCount}개)`, 'error');
      return;
    }

    // 교과_고등 학생 자료는 Storage 직접 업로드라 Cloud Run 32MiB 본문 제한을 받지 않는다.
    const maxSize = HIGH_SCHOOL_STORAGE_FILE_SIZE_BYTES;
    const oversizedFiles = files.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      const mb = Math.round(maxSize / (1024 * 1024));
      showToast(`각 파일은 ${mb}MB 이하여야 해요.`, 'error');
      return;
    }

    // 파일 사전 검사 수행
    performHighSchoolFilePreCheck(files);
  }, [auth.currentUser, showToast, highSchoolData.files?.length, highSchoolUploadProgress, performHighSchoolFilePreCheck]);

  // 🎯 고등 모드: 파일 제거
  const removeHighSchoolFile = useCallback((index) => {
    setHighSchoolData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
      filesContent: prev.filesContent.filter((_, i) => i !== index),
      filesStats: (prev.filesStats || []).filter((_, i) => i !== index)  // 🆕 통계도 제거
    }));
    showToast('파일을 제거했어요.', 'info');
  }, [showToast]);

  // 🎯 고등 모드: 드래그 앤 드롭
  const [isHighSchoolDragging, setIsHighSchoolDragging] = useState(false);
  
  // 🎯 고등 모드: 학생 정보 섹션 접힘/펼침
  const [showStudentInfo, setShowStudentInfo] = useState(false);
  
  const handleHighSchoolDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsHighSchoolDragging(true);
  }, []);

  const handleHighSchoolDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsHighSchoolDragging(false);
  }, []);

  const handleHighSchoolDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsHighSchoolDragging(false);

    if (!auth.currentUser) {
      setShowLoginPrompt(true);
      return;
    }

    if (!isProUser) {
      setShowProModal(true);
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const invalidFiles = files.filter(file => isClearlyBlockedUploadFile(file));

      if (invalidFiles.length > 0) {
        showToast('코드/문서/이미지 파일만 올릴 수 있어요.', 'error');
        return;
      }

      handleHighSchoolFileUpload(files);
    }
  }, [auth.currentUser, isProUser, setShowProModal, handleHighSchoolFileUpload, showToast]);

  const handleGlobalFilesDropped = useCallback((targetId, droppedFiles) => {
    const files = Array.from(droppedFiles || []);
    if (files.length === 0) return;

    if (!auth.currentUser) {
      setShowLoginPrompt(true);
      return;
    }
    if (!isProUser) {
      setShowProModal(true);
      return;
    }
    if (fileUpload.uploading || highSchoolUploadProgress) {
      showToast('파일을 분석하는 중이에요. 잠시만 기다려주세요.', 'info');
      return;
    }

    const blockedFile = files.find(isClearlyBlockedUploadFile);
    if (blockedFile) {
      showToast('코드/문서/이미지 파일만 올릴 수 있어요.', 'error');
      return;
    }

    const uploadFirstFile = (uploadHandler) => {
      if (files.length > 1) {
        showToast('이 첨부란에는 파일 1개만 넣을 수 있어 첫 번째 파일을 첨부해요.', 'info');
      }
      uploadHandler(files[0]);
    };

    if (selectedCategory === '교과_고등') {
      if (targetId === 'student-activity') {
        handleHighSchoolFileUpload(files);
      } else {
        handleFileUpload(files);
      }
      return;
    }

    if (selectedCategory === '동아리') {
      if (targetId === 'student-activity') {
        handleClubStudentFilesUpload(files);
      } else {
        uploadFirstFile(handleFileUpload);
      }
      return;
    }

    if (selectedCategory === '진로') {
      if (targetId === 'student-activity') {
        handleCareerStudentFilesUpload(files);
      } else {
        uploadFirstFile(handleFileUpload);
      }
      return;
    }

    if (selectedCategory === '교과') {
      handleFileUpload(files);
      return;
    }

    uploadFirstFile(handleFileUpload);
  }, [
    auth.currentUser,
    fileUpload.uploading,
    handleCareerStudentFilesUpload,
    handleClubStudentFilesUpload,
    handleFileUpload,
    handleHighSchoolFileUpload,
    highSchoolUploadProgress,
    isProUser,
    selectedCategory,
    showToast,
  ]);

  const handleInvalidGlobalDrop = useCallback(() => {
    showToast('파란색으로 표시된 입력칸에 파일을 놓아주세요.', 'info');
  }, [showToast]);

  const handleDisabledGlobalDrop = useCallback(() => {
    showToast('생기부를 만드는 중에는 파일을 첨부할 수 없어요.', 'info');
  }, [showToast]);

  // 👥 학생 선택/해제 처리 (프롬프트에 즉시 반영) - 사용하지 않음 (파일 첨부 단순화)
  // eslint-disable-next-line no-unused-vars
  const handleStudentToggle = useCallback((studentId) => {
    const student = fileUpload.students.find(s => s.id === studentId);
    const isCurrentlySelected = fileUpload.selectedStudents.includes(studentId);
    
    if (isCurrentlySelected) {
      // 선택 해제 - 프롬프트에서 해당 학생 내용 제거
      const currentPrompt = generationData.prompt;
      let newPrompt = currentPrompt;
      
      // 파일에서 추출된 학생 정보 섹션에서 해당 학생만 제거
      const fileInfoRegex = /\[파일에서 추출된 학생 정보\]\n([\s\S]*?)(?=\n\n(?!\[)|$)/;
      const match = currentPrompt.match(fileInfoRegex);
      
      if (match) {
        const fileSection = match[1];
        const updatedFileSection = fileSection.replace(new RegExp(`\\[${student.name}\\]\\n${student.content.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\n\\n)?`, 'g'), '');
        
        if (updatedFileSection.trim()) {
          newPrompt = currentPrompt.replace(fileInfoRegex, `[파일에서 추출된 학생 정보]\n${updatedFileSection}`);
        } else {
          // 모든 학생이 제거되면 파일 정보 섹션 전체 제거
          newPrompt = currentPrompt.replace(/\[파일에서 추출된 학생 정보\]\n[\s\S]*?(?=\n\n(?!\[)|$)/, '').trim();
        }
      }
      
      setGenerationData(prev => ({
        ...prev,
        prompt: newPrompt
      }));
      
      showToast(`${student.name} 학생 정보를 제거했어요.`, 'info');
    } else {
      // 선택 - 프롬프트에 해당 학생 내용 추가
      const studentContent = `[${student.name}]\n${student.content}`;
      const currentPrompt = generationData.prompt;
      
      // 기존에 파일 정보 섹션이 있는지 확인
      const fileInfoRegex = /\[파일에서 추출된 학생 정보\]\n/;
      let newPrompt;
      
      if (fileInfoRegex.test(currentPrompt)) {
        // 기존 파일 정보 섹션에 추가
        newPrompt = currentPrompt.replace(
          /(\[파일에서 추출된 학생 정보\]\n)([\s\S]*?)(?=\n\n(?!\[)|$)/,
          `$1$2\n\n${studentContent}`
        );
      } else {
        // 새로운 파일 정보 섹션 생성
        newPrompt = currentPrompt ? 
          `${currentPrompt}\n\n[파일에서 추출된 학생 정보]\n${studentContent}` :
          `[파일에서 추출된 학생 정보]\n${studentContent}`;
      }
      
      setGenerationData(prev => ({
        ...prev,
        prompt: newPrompt
      }));
      
      showToast(`${student.name} 학생 정보를 추가했어요.`, 'success');
    }
    
    // 선택 상태 업데이트
    setFileUpload(prev => ({
      ...prev,
      selectedStudents: isCurrentlySelected
        ? prev.selectedStudents.filter(id => id !== studentId)
        : [...prev.selectedStudents, studentId]
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUpload.students, fileUpload.selectedStudents, showToast]);

  // 👥 전체 학생 선택 (프롬프트에 즉시 반영) - 사용하지 않음
  // eslint-disable-next-line no-unused-vars
  const handleSelectAllStudents = useCallback(() => {
    const allStudentContent = fileUpload.students.map(student => 
      `[${student.name}]\n${student.content}`
    ).join('\n\n');
    
    const currentPrompt = generationData.prompt;
    const newPrompt = currentPrompt ? 
      `${currentPrompt}\n\n[파일에서 추출된 학생 정보]\n${allStudentContent}` :
      `[파일에서 추출된 학생 정보]\n${allStudentContent}`;
    
    setGenerationData(prev => ({
      ...prev,
      prompt: newPrompt
    }));
    
    setFileUpload(prev => ({
      ...prev,
      selectedStudents: prev.students.map(s => s.id)
    }));
    
    showToast(`${fileUpload.students.length}명의 학생 정보가 모두 추가했어요.`, 'success');
  }, [fileUpload.students, generationData.prompt, showToast]);

  // 👥 전체 학생 선택 해제 (프롬프트에서 제거) - 사용하지 않음
  // eslint-disable-next-line no-unused-vars
  const handleClearStudentSelection = useCallback(() => {
    // 프롬프트에서 파일 정보 섹션 전체 제거
    const currentPrompt = generationData.prompt;
    const newPrompt = currentPrompt.replace(/\[파일에서 추출된 학생 정보\]\n[\s\S]*?(?=\n\n(?!\[)|$)/, '').trim();
    
    setGenerationData(prev => ({
      ...prev,
      prompt: newPrompt
    }));
    
    setFileUpload(prev => ({
      ...prev,
      selectedStudents: []
    }));
    
    showToast('모든 학생 정보를 제거했어요.', 'info');
  }, [generationData.prompt, showToast]);

  // 👥 학생 선택 패널 닫기 - 사용하지 않음
  // eslint-disable-next-line no-unused-vars
  const handleCloseStudentSelector = useCallback(() => {
    // 사용하지 않음 (파일 첨부 단순화)
  }, []);

  const buildUploadedFileMeta = useCallback(() => {
    if (!fileUpload.analysis?.file_info && !fileUpload.analysis?.file_infos?.length) {
      return restoredUploadedFilesMetaRef.current.map((meta = {}) => ({
        ext: meta.ext || '',
        mime_type: meta.mime_type || '',
        file_size: Number(meta.file_size || 0),
        category: meta.category || selectedCategory,
        confidence: meta.confidence || '',
        analyzed_keys: Array.isArray(meta.analyzed_keys) ? meta.analyzed_keys : [],
        parsed_length: Number(meta.parsed_length || 0),
        file_analysis_type: meta.file_analysis_type || '',
        has_concrete_details: Boolean(meta.has_concrete_details),
        multi_student: Boolean(meta.multi_student),
        warning_count: Number(meta.warning_count || 0),
      }));
    }
    const analyzedData = fileUpload.analysis.analyzed_data || {};
    const infos = fileUpload.analysis.file_infos?.length
      ? fileUpload.analysis.file_infos
      : [fileUpload.analysis.file_info];
    const metas = fileUpload.analysis.file_metas || [];
    const sourceFiles = fileUpload.files?.length ? fileUpload.files : (fileUpload.file ? [fileUpload.file] : []);
    const isStudentListFile = Boolean(fileUpload.multiStudent)
      && MULTI_STUDENT_BATCH_CATEGORIES.includes(selectedCategory)
      && (fileUpload.parsedStudents || []).length > 1;

    return infos.map((info = {}, index) => {
      const meta = metas[index] || {};
      const sourceFile = sourceFiles[index] || {};
      const filename = info.name || sourceFile.name || '';
      const ext = meta.ext || (filename.includes('.') ? filename.split('.').pop().toLowerCase() : '');
      const creativeMetadata = selectedCategory === '창체'
        ? buildCreativeUploadedFileMetadata(fileUpload.analysis, meta)
        : {};
      return {
        filename,
        ext,
        mime_type: meta.mime_type || info.type || sourceFile.type || '',
        file_size: meta.file_size || info.size || sourceFile.size || 0,
        category: selectedCategory,
        confidence: analyzedData.confidence || analyzedData.analysis_confidence || '',
        warning: fileUpload.analysis.warning || '',
        analyzed_keys: Object.keys(analyzedData).slice(0, 30),
        // Phase 4: 서버가 영속화한 계획서 원본 id(자료함 저장 제안용). 기록에는 이름 해시만 남는다.
        ...(info.plan_file_id ? { plan_file_id: info.plan_file_id } : {}),
        ...(info.reuse_match ? { reuse_match: true } : {}),
        // Phase 4b: 자료함에서 고른 자료. 서버가 재사용 제안(reuse_eval_plan) 대상에서 뺀다.
        ...(info.source === LIBRARY_SOURCE || isLibraryPseudoFile(sourceFile)
          ? { source: LIBRARY_SOURCE, material_id: info.material_id || sourceFile.materialId || '' }
          : {}),
        ...creativeMetadata,
        ...(isStudentListFile
          ? {
            multi_student: true,
            student_count: fileUpload.parsedStudents.length,
            picked_student_count: fileUpload.selectedStudent ? 1 : 0,
          }
          : {}),
      };
    });
  }, [fileUpload.analysis, fileUpload.file, fileUpload.files, fileUpload.multiStudent, fileUpload.parsedStudents, fileUpload.selectedStudent, selectedCategory]);

  const getGenerationSlowReasons = useCallback(() => {
    const reasons = [];
    const totalCount = selectedCategory === '교과'
      ? totalLevelCount
      : generationData.generationCount;
    if (generationData.selectedModel !== 'gemini-2.5-flash-lite') {
      reasons.push('고급 모델로 더 꼼꼼하게 작성 중');
    }
    if (totalCount >= 5) {
      reasons.push(`${totalCount}개 문구를 나눠 작성 중`);
    }
    if (
      highSchoolData.filesContent?.length > 0 ||
      clubData.studentFilesContent?.length > 0 ||
      clubData.planFileContent ||
      careerData.studentFilesContent?.length > 0 ||
      careerData.infoFileContent
    ) {
      reasons.push('첨부 내용을 함께 반영 중');
    }
    if (generationData.wordCount >= 1500) {
      reasons.push('목표 분량이 길어 문장을 다듬는 중');
    }
    return reasons;
  }, [careerData.infoFileContent, careerData.studentFilesContent, clubData.planFileContent, clubData.studentFilesContent, generationData.generationCount, generationData.selectedModel, generationData.wordCount, highSchoolData.filesContent, selectedCategory, totalLevelCount]);

  const getSaenggibuApiBaseUrl = () => {
    if (process.env.NODE_ENV === 'development') {
      return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    }
    if (window.location.hostname.includes('whatquiztest')) {
      return 'https://quiz-api-test-397277209875.us-central1.run.app';
    }
    return 'https://quiz-api-397277209875.us-central1.run.app';
  };

  const collectGenerationRequestFileStats = (requestData = {}) => {
    const groups = [
      ...(requestData.high_school_files || []),
      ...(requestData.club_data?.plan_files || []),
      ...(requestData.club_data?.student_files || []),
      ...(requestData.career_data?.student_files || []),
      ...(requestData.career_data?.info_file || []),
    ];
    return {
      file_count: groups.length,
      raw_size_bytes: groups.reduce((sum, file) => sum + (Number(file?.size) || 0), 0),
      storage_file_count: groups.filter(file => file?.storage_path).length,
      base64_file_count: groups.filter(file => file?.content).length,
    };
  };

  const reportSaenggibuClientError = async (eventType, requestData, extra = {}) => {
    try {
      const currentUser = auth.currentUser;
      const fileStats = collectGenerationRequestFileStats(requestData);
      await fetch(`${getSaenggibuApiBaseUrl()}/api/log/client-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: eventType,
          user_uid: currentUser?.uid || null,
          user_email: currentUser?.email || null,
          details: 'saenggibu_generate_stream_client_guard',
          extra: {
            category: requestData?.category || selectedCategory,
            generation_count: requestData?.generation_count || null,
            ...fileStats,
            ...extra,
          },
        }),
      });
    } catch (logError) {
      console.warn('생기부 클라이언트 진단 로그 전송 실패:', logError);
    }
  };

  const resetResultStateForGeneration = () => {
    clearGeneratedResultState();
  };

  const createGenerationSnapshot = () => ({
    generationResult,
    versions: [...versions],
    versionLevels: [...versionLevels],
    versionNumbers: [...versionNumbers],
    savedRecordId,
    unpersistedResultKeys: new Set(unpersistedResultKeys),
    regenerationChain: [...regenerationChain],
    regenerationChainEdited: regenerationChainEditedRef.current,
    multiClassResults: [...multiClassResults],
    activeClassResultId,
    multiClassProgress: { ...multiClassProgress },
    copiedHistory: new Set(copiedHistory),
    showSaveCTA,
    resultContext,
  });

  const restoreGenerationSnapshot = (snapshot) => {
    if (!snapshot) return;
    setGenerationResult(snapshot.generationResult || '');
    setVersions(snapshot.versions || []);
    setVersionLevels(snapshot.versionLevels || []);
    setVersionNumbers(snapshot.versionNumbers || []);
    setSavedRecordId(snapshot.savedRecordId || null);
    setUnpersistedResultKeys(snapshot.unpersistedResultKeys || new Set());
    setRegenerationChain(snapshot.regenerationChain || []);
    regenerationChainEditedRef.current = Boolean(snapshot.regenerationChainEdited);
    setMultiClassResults(snapshot.multiClassResults || []);
    setActiveClassResultId(snapshot.activeClassResultId || 'class-1');
    setMultiClassProgress(snapshot.multiClassProgress || { current: 0, total: 0, message: '' });
    setCopiedHistory(snapshot.copiedHistory || new Set());
    setShowSaveCTA(Boolean(snapshot.showSaveCTA));
    setResultContext(snapshot.resultContext || null);
    setRefinePanelKey(null);
    setInlineSimilar(null);
    setRefinePreview(null);
    setRefinePreviewDraft('');
    setRefinePreviewEditingKey(null);
    setManualEditKey(null);
  };

  const createGenerationJobId = () => {
    if (window.crypto?.randomUUID) {
      return `sgb_${window.crypto.randomUUID()}`;
    }
    return `sgb_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  };

  const syncActiveGenerationJobIds = (nextJobIds) => {
    activeGenerationJobIdsRef.current = nextJobIds;
    setActiveGenerationJobIds(nextJobIds);
  };

  const registerGenerationJob = (jobId) => {
    if (!jobId || activeGenerationJobIdsRef.current.includes(jobId)) return;
    syncActiveGenerationJobIds([...activeGenerationJobIdsRef.current, jobId]);
  };

  const clearActiveGenerationJobs = () => {
    syncActiveGenerationJobIds([]);
  };

  useEffect(() => () => {
    generationCancelRequestedRef.current = true;
    const activeJobIds = [...activeGenerationJobIdsRef.current];
    activeJobIds.forEach(jobId => {
      Promise.resolve(cancelSaenggibuGeneration(jobId)).catch(error => {
        console.warn('화면 이탈 중 생기부 생성을 중단하지 못했어요:', error);
      });
    });
  }, []);

  const createGenerationCancelledError = (message = '생성을 중단했어요.') => {
    const error = new Error(message);
    error.isGenerationCancelled = true;
    return error;
  };

  const handleCancelGeneration = async () => {
    if ((!isGenerating && !isStreaming) || isCancellingGeneration) return;
    generationCancelRequestedRef.current = true;
    setIsCancellingGeneration(true);
    setStreamProgressMessage('생성을 중단하고 있어요.');

    const jobIds = [...activeGenerationJobIdsRef.current];
    if (jobIds.length === 0) {
      showToast('생성 중단을 준비하고 있어요. 잠시만 기다려주세요.', 'info');
      setIsCancellingGeneration(false);
      return;
    }

    const results = await Promise.allSettled(
      jobIds.map(jobId => cancelSaenggibuGeneration(jobId))
    );
    const failedCount = results.filter(result => result.status === 'rejected').length;
    if (failedCount > 0) {
      showToast('일부 생성 중단 요청을 보내지 못했어요. 다시 눌러주세요.', 'warning', 6000);
      setIsCancellingGeneration(false);
      return;
    }
    showToast('생성 중단 요청을 보냈어요.', 'info');
  };

  const buildGenerationRequestPayload = ({
    regenerationInstruction = '',
    regeneratedFromRecordId = null,
    isRegeneration = false,
    overrides = {},
  } = {}) => {
    let finalPrompt = generationData.prompt;
    if (uiMode === 'quick' && selectedCategory === '교과') {
      finalPrompt = `[활동명/성취기준]\n${subjectData.activityName}\n\n[활동내용]\n${subjectData.activityContent}`;
    } else if (uiMode === 'quick' && selectedCategory === '교과_고등') {
      let highSchoolPrompt = '';
      if (highSchoolData.achievementStandard.trim()) {
        highSchoolPrompt += `[성취기준/평가 계획표]\n${highSchoolData.achievementStandard}\n\n`;
      }
      if (highSchoolData.studentActivity.trim()) {
        highSchoolPrompt += `[학생 활동 내용]\n${highSchoolData.studentActivity}\n\n`;
      }
      if (highSchoolData.growthPoints.trim()) {
        highSchoolPrompt += `[학생 정보(성취수준/진로/희망학과/성적 등)]\n${highSchoolData.growthPoints}`;
      }
      finalPrompt = highSchoolPrompt;
      if (highSchoolData.filesContent && highSchoolData.filesContent.length > 0) {
        finalPrompt += '\n\n[첨부된 학생 활동 파일]\n';
        highSchoolData.filesContent.forEach((fileData, index) => {
          finalPrompt += `\n파일 ${index + 1}: ${fileData.name}\n`;
        });
      }
    } else if (selectedCategory === '동아리') {
      let clubPrompt = '[동아리활동]\n';
      if (clubData.clubDescription.trim()) {
        clubPrompt += `[동아리 정보]\n${clubData.clubDescription}\n\n`;
      }
      if (clubData.studentActivity.trim()) {
        clubPrompt += `[학생 활동 내용]\n${clubData.studentActivity}`;
      }
      finalPrompt = clubPrompt;
    } else if (selectedCategory === '진로') {
      let careerPrompt = '[진로활동]\n\n';
      if (careerData.studentActivity?.trim()) {
        careerPrompt += `[진로 활동]\n${careerData.studentActivity.trim()}\n\n`;
      }
      if (careerData.careerInfo?.trim()) {
        careerPrompt += `[학생 정보]\n${careerData.careerInfo.trim()}\n`;
      }
      finalPrompt = careerPrompt;
    }

    const requestData = {
      prompt: finalPrompt,
      word_count: generationData.wordCount,
      generation_count: selectedCategory === '교과' ? totalLevelCount : generationData.generationCount,
      emphasize_points: generationData.emphasizePoints,
      use_advanced_model: useAdvancedModel,
      category: selectedCategory,
      selected_model: generationData.selectedModel,
    };

    const historySourceRecordId = historySourceRecordIdRef.current;
    if (isRegeneration || historySourceRecordId) {
      requestData.is_regeneration = true;
      requestData.regenerated_from_record_id = (
        regeneratedFromRecordId
        || savedRecordId
        || historySourceRecordId
        || undefined
      );
      if (regenerationInstruction) {
        requestData.regeneration_instruction = regenerationInstruction;
      }
    }

    const uploadedFileMeta = buildUploadedFileMeta();
    if (uploadedFileMeta.length > 0) {
      requestData.uploaded_files = uploadedFileMeta;
      requestData.eval_plan_files_count = uploadedFileMeta.length;
    }
    const appliedEvalUnitCount = Number(fileUpload.appliedEvalUnitCount) || 0;
    if (appliedEvalUnitCount > 0) {
      requestData.eval_unit_count = Math.min(appliedEvalUnitCount, EVAL_UNIT_COUNT_MAX);
    }
    if (selectedCategory === '교과' || selectedCategory === '교과_고등') {
      Object.assign(requestData, buildPlanSelectionMeta(
        fileUpload.planMode,
        Math.min((fileUpload.parsedStudents || []).length, EVAL_UNIT_COUNT_MAX),
      ));
    }

    if (selectedCategory === '교과') {
      requestData.level_counts = levelCounts;
      requestData.school_level = schoolLevel;
      const subCat = getSubjectSubCategory(subjectData.activityName);
      if (subCat) requestData.sub_category = subCat;
    } else if (selectedCategory === '교과_고등') {
      if (highSchoolData.filesContent && highSchoolData.filesContent.length > 0) {
        requestData.high_school_files = highSchoolData.filesContent;
        if (separateReports && highSchoolData.filesContent.length >= 2) {
          requestData.separate_reports = true;
        }
      }
      if (highSchoolData.studentActivity.trim()) {
        requestData.student_activity_text = highSchoolData.studentActivity.trim();
      }
      const std = (highSchoolData.achievementStandard || '').trim();
      if (fileUpload.appliedEvalUnitCount > 1) {
        const firstSubject = fileUpload.selectedItems?.[0]?.subject || '평가 단위';
        requestData.sub_category = `${firstSubject} 외 ${fileUpload.appliedEvalUnitCount - 1}건`.substring(0, 20);
      } else {
        const subCat = std.split(/[\n,]/)[0]?.trim();
        if (subCat && subCat.length <= 20) requestData.sub_category = subCat;
      }
    } else if (selectedCategory === '동아리') {
      requestData.club_data = {
        club_description: clubData.clubDescription,
        student_activity: clubData.studentActivity,
        plan_files: clubData.planFileContent ? [clubData.planFileContent] : [],
        student_files: clubData.studentFilesContent || [],
      };
      const clubName = (clubData.clubDescription || '').trim().split(/[\n,]/)[0]?.trim();
      if (clubName) requestData.sub_category = clubName.length > 20 ? clubName.substring(0, 20) : clubName;
    } else if (selectedCategory === '진로') {
      requestData.career_data = {
        student_activity: careerData.studentActivity,
        career_info: careerData.careerInfo,
        student_files: careerData.studentFilesContent || [],
        info_file: careerData.infoFileContent ? [careerData.infoFileContent] : [],
      };
      const careerInfo = (careerData.careerInfo || '').trim().split(/[\n,]/)[0]?.trim();
      if (careerInfo) requestData.sub_category = careerInfo.length > 20 ? careerInfo.substring(0, 20) : careerInfo;
    }

    // textarea에서 교사가 지운 성취기준은 payload에 넣지 않는다(상태는 유지될 수 있음).
    const standardsStillInText = (() => {
      if (selectedCategory === '교과') {
        const text = String(subjectData.activityName || '');
        return selectedStandards.filter((record) => standardStillInText(text, record));
      }
      if (selectedCategory === '교과_고등') {
        const text = String(highSchoolData.achievementStandard || '');
        return selectedStandards.filter((record) => standardStillInText(text, record));
      }
      return [];
    })();

    return attachAchievementStandardsToPayload({ ...requestData, ...overrides }, standardsStillInText);
  };

  const handleInsertAchievementStandards = (records) => {
    if (selectedCategory === '교과') {
      setSubjectData((prev) => ({
        ...prev,
        activityName: appendStandardLines(prev.activityName, records),
      }));
    } else if (selectedCategory === '교과_고등') {
      setHighSchoolData((prev) => ({
        ...prev,
        achievementStandard: appendStandardLines(prev.achievementStandard, records),
      }));
    }
    setSelectedStandards((prev) => mergeSelectedStandards(prev, records));
    setIsAchievementPickerOpen(false);
  };

  if (process.env.NODE_ENV === 'test') {
    SaenggibuGenerationForm.__buildGenerationRequestPayload = buildGenerationRequestPayload;
  }

  const runGenerationStream = async (requestData) => {
    if (generationCancelRequestedRef.current) {
      throw createGenerationCancelledError();
    }
    proDefaultModelAppliedRef.current = true;
    const API_BASE_URL = getSaenggibuApiBaseUrl();
    let idToken = null;
    if (auth.currentUser) {
      idToken = await auth.currentUser.getIdToken();
    }

    const fetchHeaders = { 'Content-Type': 'application/json' };
    if (idToken) {
      fetchHeaders['Authorization'] = `Bearer ${idToken}`;
    }
    const generationJobId = requestData.generation_job_id || createGenerationJobId();
    registerGenerationJob(generationJobId);
    const requestWithJobId = {
      ...requestData,
      generation_job_id: generationJobId,
    };
    const requestBody = JSON.stringify(requestWithJobId);
    const requestBodyBytes = new Blob([requestBody]).size;
    if (requestBodyBytes > SAENGGIBU_JSON_BODY_LIMIT_BYTES) {
      await reportSaenggibuClientError('SOFT_WARNING', requestWithJobId, {
        reason: 'request_body_too_large_client_guard',
        body_size_bytes: requestBodyBytes,
        body_size_display: formatMB(requestBodyBytes),
        limit_bytes: SAENGGIBU_JSON_BODY_LIMIT_BYTES,
      });
      const bodySizeError = new Error('첨부 파일이 너무 커서 생성 요청을 보낼 수 없어요.');
      bodySizeError.userMessage = `첨부 파일이 너무 커요. 현재 요청 크기는 ${formatMB(requestBodyBytes)}이고, 한 번에 ${Math.round(SAENGGIBU_JSON_BODY_LIMIT_BYTES / 1024 / 1024)}MB까지만 보낼 수 있어요. 파일을 줄이거나 나눠서 첨부해주세요.`;
      throw bodySizeError;
    }

    const timeoutMs = calcApiTimeout('saenggibu-generate', {
      studentCount: requestWithJobId.generation_count || 1,
      fileCount: (requestWithJobId.uploaded_files?.length || 0)
        + (requestWithJobId.high_school_files?.length || 0)
        + (requestWithJobId.club_data?.plan_files?.length || 0)
        + (requestWithJobId.club_data?.student_files?.length || 0)
        + (requestWithJobId.career_data?.student_files?.length || 0)
        + (requestWithJobId.career_data?.info_file?.length || 0),
    });

    let response;
    try {
      response = await fetchWithRetry(`${API_BASE_URL}/api/saenggibu/generate-stream`, {
        method: 'POST',
        headers: fetchHeaders,
        body: requestBody
      }, {
        maxRetries: 1,
        timeoutMs,
        retryOnTimeout: false,
      });
    } catch (fetchError) {
      await reportSaenggibuClientError('SOFT_WARNING', requestWithJobId, {
        reason: 'generate_stream_fetch_failed',
        error_message: String(fetchError?.message || fetchError).slice(0, 300),
        body_size_bytes: requestBodyBytes,
        body_size_display: formatMB(requestBodyBytes),
      });
      if (isLikelyFetchNetworkError(fetchError)) {
        fetchError.userMessage = getFriendlyGenerationErrorMessage(fetchError);
      }
      throw fetchError;
    }

    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
      } catch {
        errorData = { detail: '생기부 생성에 실패했어요.' };
      }
      const errorMsg = formatApiErrorDetail(errorData, '생기부 생성에 실패했어요.');
      setLocalError(errorMsg);
      showToast(errorMsg, 'error');
      scrollToPromptInput();
      throw new Error(errorMsg);
    }

    let parseFailureCount = 0;
    const noteStreamFailure = (error, raw) => {
      parseFailureCount += 1;
      if (parseFailureCount <= 3) {
        console.warn('[Saenggibu] SSE JSON parse failed', error, String(raw).slice(0, 200));
      }
      if (parseFailureCount === 3) {
        showToast('일부 진행 신호를 해석하지 못했어요. 생성이 계속 지연되면 다시 시도해주세요.', 'warning', 6000);
      }
    };

    const events = readSseJson(response.body, {
      idleTimeoutMs: SAENGGIBU_STREAM_INACTIVITY_TIMEOUT_MS,
      idleTimeoutError: () => new Error('생성 서버 응답이 잠시 멈췄어요. 같은 요청이 너무 오래 걸리면 입력을 조금 줄이거나 다시 시도해주세요.'),
      beforeRead: () => {
        if (generationCancelRequestedRef.current) {
          setStreamProgressMessage('생성을 중단하고 있어요.');
        }
      },
      onInvalidData: (raw) => noteStreamFailure(null, raw),
    });

    for await (const parsed of events) {
      try {
        if (parsed.type === 'version_start') {
          setLocalError('');
          const versionIndex = parsed.version - 1;
          setVersions(prev => {
            const newVersions = [...prev];
            newVersions[versionIndex] = '';
            return newVersions;
          });
          setVersionNumbers(prev => {
            const newVersionNumbers = [...prev];
            newVersionNumbers[versionIndex] = parsed.version;
            return newVersionNumbers;
          });
          if (parsed.level) {
            setVersionLevels(prev => {
              const newLevels = [...prev];
              newLevels[versionIndex] = parsed.level;
              return newLevels;
            });
          }
        } else if (parsed.type === 'content') {
          const versionIndex = parsed.version - 1;
          setVersions(prev => {
            const newVersions = [...prev];
            newVersions[versionIndex] = (newVersions[versionIndex] || '') + parsed.content;
            return newVersions;
          });
          if (parsed.version === 1) {
            setGenerationResult(prev => prev + parsed.content);
          }
        } else if (parsed.type === 'progress') {
          setLocalError('');
          if (parsed.message) {
            setStreamProgressMessage(parsed.message);
          }
        } else if (parsed.type === 'version_end') {
          // 버전 완료 신호는 서버-클라이언트 contract 확인용으로 유지한다.
        } else if (parsed.type === 'complete') {
          setIsStreaming(false);
        } else if (parsed.type === 'cancelled') {
          throw createGenerationCancelledError(parsed.message || '생성을 중단했어요.');
        } else if (parsed.type === 'anonymous_complete') {
          const newCount = anonGenCount + 1;
          setAnonGenCount(newCount);
          sessionStorage.setItem('sgb-anon-gen-count', String(newCount));
          setShowSaveCTA(true);
          trackRiseFeatureAction('saenggibu_generate', 'generate', {
            saenggibu_category: selectedCategory,
            anonymous: true,
            is_batch: false,
            request_type: requestData.is_regeneration ? 'single_regenerate' : 'single_initial',
            is_regeneration: Boolean(requestData.is_regeneration),
          });
        } else if (parsed.type === 'saved') {
          const recordId = parsed.record_id || null;
          setSavedRecordId(recordId);
          if (recordId) {
            setUnpersistedResultKeys(new Set());
          }
          // 다시 쓰기 지시문의 분량 요청이 적용된 결과는 그 목표로 바이트를 비교한다 (슬라이더는 그대로).
          const lengthTargetBytes = Number(parsed.length_target_bytes);
          if (Number.isFinite(lengthTargetBytes) && lengthTargetBytes > 0) {
            setResultContext((prev) => (prev ? { ...prev, targetBytes: lengthTargetBytes } : prev));
          }
          if (Array.isArray(parsed.regeneration_instruction_chain)) {
            setRegenerationChain(parsed.regeneration_instruction_chain);
          } else if (requestData.is_regeneration) {
            // 구버전 서버 응답 대비: 이번 요청만이라도 체인에 반영
            setRegenerationChain((prev) => appendRegenerationInstruction(prev, requestData.regeneration_instruction));
          } else {
            setRegenerationChain([]);
          }
          window.dispatchEvent(new CustomEvent('saenggibuCreated', {
            detail: { recordId }
          }));
          trackSaenggibuCreate(selectedCategory, generationData.wordCount, isProUser, {
            request_type: requestData.is_regeneration ? 'single_regenerate' : 'single_initial',
            is_regeneration: Boolean(requestData.is_regeneration),
          });
          trackRiseFeatureAction('saenggibu_generate', 'generate', {
            saenggibu_category: selectedCategory,
            anonymous: false,
            is_batch: false,
            request_type: requestData.is_regeneration ? 'single_regenerate' : 'single_initial',
            is_regeneration: Boolean(requestData.is_regeneration),
          });
        } else if (parsed.type === 'warning') {
          showToast(parsed.message || '경고가 발생했어요.', 'warning', 8000);
        } else if (parsed.type === 'error' || parsed.error) {
          const errorMsg = parsed.message || parsed.error || '생성 중 오류가 발생했어요.';
          setLocalError(errorMsg);
          showToast(errorMsg, 'error');
          setIsGenerating(false);
          setIsStreaming(false);
          scrollToPromptInput();
          const streamError = new Error(errorMsg);
          streamError.isStreamEventError = true;
          throw streamError;
        } else if (parsed.content) {
          setGenerationResult(prev => prev + parsed.content);
          setVersions(prev => {
            const newVersions = prev.length ? [...prev] : [''];
            newVersions[0] = (newVersions[0] || '') + parsed.content;
            return newVersions;
          });
        }
      } catch (e) {
        if (e.isStreamEventError) {
          throw e;
        }
        noteStreamFailure(e, JSON.stringify(parsed));
      }
    }
  };

  const runSubjectClassGeneration = async (requestData, classMeta) => {
    if (generationCancelRequestedRef.current) {
      throw createGenerationCancelledError();
    }
    const versionOffset = Number(classMeta.versionOffset || 0);
    const progressName = classMeta.progressName || classMeta.name;
    const API_BASE_URL = getSaenggibuApiBaseUrl();
    let idToken = null;
    if (auth.currentUser) {
      idToken = await auth.currentUser.getIdToken();
    }

    const fetchHeaders = { 'Content-Type': 'application/json' };
    if (idToken) {
      fetchHeaders['Authorization'] = `Bearer ${idToken}`;
    }
    const generationJobId = requestData.generation_job_id || createGenerationJobId();
    registerGenerationJob(generationJobId);
    const requestWithJobId = {
      ...requestData,
      generation_job_id: generationJobId,
    };
    const requestBody = JSON.stringify(requestWithJobId);
    const requestBodyBytes = new Blob([requestBody]).size;
    if (requestBodyBytes > SAENGGIBU_JSON_BODY_LIMIT_BYTES) {
      await reportSaenggibuClientError('SOFT_WARNING', requestWithJobId, {
        reason: 'multiclass_request_body_too_large_client_guard',
        body_size_bytes: requestBodyBytes,
        body_size_display: formatMB(requestBodyBytes),
        limit_bytes: SAENGGIBU_JSON_BODY_LIMIT_BYTES,
      });
      const bodySizeError = new Error(`${progressName} 생성 요청이 너무 커요.`);
      bodySizeError.userMessage = `${progressName} 생성 요청이 너무 커요. 첨부 파일이나 입력 내용을 줄여주세요.`;
      throw bodySizeError;
    }

    const timeoutMs = calcApiTimeout('saenggibu-generate', {
      studentCount: requestWithJobId.generation_count || 1,
      fileCount: requestWithJobId.uploaded_files?.length || 0,
    });

    let response;
    try {
      response = await fetchWithRetry(`${API_BASE_URL}/api/saenggibu/generate-stream`, {
        method: 'POST',
        headers: fetchHeaders,
        body: requestBody
      }, {
        maxRetries: 1,
        timeoutMs,
        retryOnTimeout: false,
      });
    } catch (fetchError) {
      await reportSaenggibuClientError('SOFT_WARNING', requestWithJobId, {
        reason: 'multiclass_generate_stream_fetch_failed',
        error_message: String(fetchError?.message || fetchError).slice(0, 300),
        body_size_bytes: requestBodyBytes,
        body_size_display: formatMB(requestBodyBytes),
      });
      if (isLikelyFetchNetworkError(fetchError)) {
        fetchError.userMessage = getFriendlyGenerationErrorMessage(fetchError);
      }
      throw fetchError;
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `${progressName} 생성에 실패했어요.`);
    }

    const classVersions = [...(classMeta.initialVersions || [])];
    const classLevels = [...(classMeta.initialLevels || [])];
    let recordId = null;

    const updateClassResult = (updates) => {
      setMultiClassResults(prev => prev.map(result => (
        result.id === classMeta.id
          ? {
              ...result,
              ...updates,
              versions: updates.versions || result.versions,
              levels: updates.levels || result.levels,
              totalCount: (updates.versions || result.versions || []).filter(Boolean).length,
            }
          : result
      )));
    };

    const events = readSseJson(response.body, {
      idleTimeoutMs: SAENGGIBU_STREAM_INACTIVITY_TIMEOUT_MS,
      idleTimeoutError: () => new Error(`${progressName} 생성 서버 응답이 잠시 멈췄어요. 다시 시도해주세요.`),
      beforeRead: () => {
        if (generationCancelRequestedRef.current) {
          setStreamProgressMessage('생성을 중단하고 있어요.');
        }
      },
      // 빠진 신호가 내용 조각일 수 있어 건너뛰지 않고 그 반을 실패로 둔다
      onInvalidData: () => {
        throw new Error(`${progressName} 생성 중 응답을 읽지 못했어요. 다시 시도해주세요.`);
      },
    });

    for await (const parsed of events) {
      if (parsed.type === 'version_start') {
        const index = versionOffset + parsed.version - 1;
        classVersions[index] = '';
        if (parsed.level) classLevels[index] = parsed.level;
        updateClassResult({
          status: 'generating',
          versions: [...classVersions],
          levels: [...classLevels],
        });
      } else if (parsed.type === 'content') {
        const index = versionOffset + parsed.version - 1;
        classVersions[index] = (classVersions[index] || '') + parsed.content;
        if (parsed.level) classLevels[index] = parsed.level;
        updateClassResult({
          status: 'generating',
          versions: [...classVersions],
          levels: [...classLevels],
        });
      } else if (parsed.type === 'progress') {
        setMultiClassProgress(prev => ({
          ...prev,
          message: `${progressName}: ${parsed.message || '생성 중이에요.'}`,
        }));
        if (parsed.message) setStreamProgressMessage(`${progressName}: ${parsed.message}`);
      } else if (parsed.type === 'saved') {
        recordId = parsed.record_id || null;
        updateClassResult({ recordId });
        window.dispatchEvent(new CustomEvent('saenggibuCreated', {
          detail: { recordId }
        }));
      } else if (parsed.type === 'warning') {
        showToast(`${progressName}: ${parsed.message || '품질 경고가 있어요.'}`, 'warning', 8000);
      } else if (parsed.type === 'cancelled') {
        throw createGenerationCancelledError(parsed.message || '생성을 중단했어요.');
      } else if (parsed.type === 'error' || parsed.error) {
        throw new Error(parsed.message || parsed.error || `${progressName} 생성 중 오류가 발생했어요.`);
      }
    }

    return {
      ...classMeta,
      recordId,
      versions: classVersions,
      levels: classLevels,
      totalCount: classVersions.filter(Boolean).length,
      status: 'done',
    };
  };

  const buildClientDiversityDigest = (classResults) => {
    const compact = (text = '') => text.replace(/\s+/g, '');
    const openingStems = [];
    const fragmentCounts = new Map();

    classResults.forEach((classResult) => {
      (classResult.versions || []).forEach((content, index) => {
        if (!content) return;
        const compactContent = compact(content);
        openingStems.push({
          class_id: classResult.name,
          row: index + 1,
          level: classResult.levels?.[index] || '',
          stem: compactContent.slice(0, 24),
        });
        for (let pos = 0; pos <= Math.max(0, compactContent.length - 15); pos += 1) {
          const fragment = compactContent.slice(pos, pos + 15);
          if (!fragment) continue;
          fragmentCounts.set(fragment, (fragmentCounts.get(fragment) || 0) + 1);
        }
      });
    });

    const frequentFragments = [...fragmentCounts.entries()]
      .filter(([, count]) => count >= 2)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([fragment, count]) => ({ fragment, count }));

    return {
      opening_stems: openingStems.slice(-20),
      frequent_fragments: frequentFragments,
      strategy_note: '이전 반과 같은 시작 조각, 핵심 표현, 마무리 관점을 반복하지 마세요.',
    };
  };

  const generateMultiClassSubject = async ({
    regenerationInstruction = '',
    regeneratedFromRecordId = null,
    isRegeneration = false,
  } = {}) => {
    const validClasses = Array.from({ length: subjectClassCount }, (_, index) => ({
      id: `class-${index + 1}`,
      name: `${index + 1}반`,
      levelCounts: { ...levelCounts },
      totalCount: totalLevelCount,
    }));

    if (totalLevelCount < 1 || totalLevelCount > proLevelTotalMax) {
      throw new Error(`상중하 학생 수 합계는 1~${proLevelTotalMax}명 사이로 맞춰주세요.`);
    }
    if (totalMultiClassCount > multiClassTotalMax) {
      throw new Error(`다반 생성은 최대 ${multiClassTotalMax}명까지 가능해요.`);
    }

    const classGroupId = `subject-${Date.now()}`;
    const results = [];
    const initialResults = validClasses.map((classItem) => ({
      ...classItem,
      versions: [],
      levels: [],
      recordId: null,
      status: 'pending',
    }));
    setMultiClassResults(initialResults);
    setActiveClassResultId(validClasses[0]?.id || 'class-1');
    setMultiClassProgress({ current: 0, total: validClasses.length, message: '다반 생성을 시작해요.' });

    for (let index = 0; index < validClasses.length; index += 1) {
      if (generationCancelRequestedRef.current) {
        throw createGenerationCancelledError();
      }
      const classItem = validClasses[index];
      setMultiClassProgress({
        current: index + 1,
        total: validClasses.length,
        message: `${classItem.name} 문장을 생성하고 있어요.`,
      });

      const diversityContext = buildClientDiversityDigest(results);
      const classContext = `${classItem.name}: ${subjectLevelScheme.map(level => `${level.label} ${classItem.levelCounts[level.key] || 0}명`).join(', ')}. 이전 반과 다른 관찰 렌즈와 시작 장면을 우선 배정.`;
      const requestData = buildGenerationRequestPayload({
        regenerationInstruction,
        regeneratedFromRecordId,
        isRegeneration,
        overrides: {
          generation_count: classItem.totalCount,
          level_counts: classItem.levelCounts,
          class_context: classContext,
          class_group_id: classGroupId,
          class_index: index + 1,
          total_classes: validClasses.length,
          generation_strategy: isRegeneration ? 'frontend_multi_class_regenerate' : 'frontend_multi_class_queue',
          diversity_context: diversityContext,
          skip_history_save: true,
        },
      });

      const classResult = await runSubjectClassGeneration(requestData, {
        id: classItem.id,
        name: classItem.name,
        levelCounts: classItem.levelCounts,
      });
      results.push(classResult);
      if (generationCancelRequestedRef.current) {
        throw createGenerationCancelledError();
      }
      setMultiClassResults(prev => prev.map(result => (
        result.id === classItem.id ? classResult : result
      )));
    }

    const flatVersions = results.flatMap(result => result.versions.filter(Boolean));
    const flatLevels = results.flatMap(result => result.levels || []);
    setVersions(flatVersions);
    setVersionLevels(flatLevels);
    setGenerationResult(flatVersions[0] || '');
    if (auth.currentUser && results.length > 0) {
      try {
        const saveResponse = await saveMultiClassSaenggibuRecord({
          prompt: `[활동명/성취기준]\n${subjectData.activityName}\n\n[활동내용]\n${subjectData.activityContent}`,
          word_count: generationData.wordCount,
          selected_model: generationData.selectedModel,
          school_level: schoolLevel,
          level_counts: levelCounts,
          class_results: results,
          generation_strategy: isRegeneration ? 'frontend_multi_class_regenerate' : 'frontend_multi_class_queue',
          is_regeneration: Boolean(isRegeneration),
          regenerated_from_record_id: regeneratedFromRecordId || '',
          // Phase 4: 첨부 재사용 감지 메타(서버가 이름 해시만 저장)
          uploaded_files: buildUploadedFileMeta(),
        });
        const groupRecordId = saveResponse?.record_id || null;
        setSavedRecordId(groupRecordId);
        if (groupRecordId) {
          setUnpersistedResultKeys(new Set());
          window.dispatchEvent(new CustomEvent('saenggibuCreated', {
            detail: { recordId: groupRecordId }
          }));
        }
      } catch (saveError) {
        showToast('생성은 완료됐지만 기록 저장 중 문제가 생겼어요.', 'warning', 8000);
        console.warn('[Saenggibu] multi-class save failed', saveError);
      }
    }
    setMultiClassProgress({
      current: validClasses.length,
      total: validClasses.length,
      message: `총 ${totalMultiClassCount}명 생성을 완료했어요.`,
    });
    showToast(`${validClasses.length}개 반, 총 ${totalMultiClassCount}명 생성을 완료했어요.`, 'success');
  };

  const getSingleRegeneratePresets = () => {
    const common = [
      {
        id: 'regen-plain',
        label: '더 담백하게',
        instruction: '전체적으로 과한 칭찬과 수식어를 줄이고, 담백한 생활기록부 문체로 다시 써줘.'
      },
      {
        id: 'regen-specific',
        label: '구체 행동 살리기',
        instruction: '추상적인 표현보다 입력 자료에 있는 구체 행동, 역할, 과정이 더 잘 보이게 다시 써줘.'
      },
      {
        id: 'regen-variation',
        label: '표현 바꾸기',
        instruction: '기존 결과와 같은 문장 구조를 반복하지 말고, 같은 입력을 바탕으로 완전히 다른 표현으로 다시 써줘.'
      },
      {
        id: 'regen-bytes',
        label: '분량 맞추기',
        instruction: '목표 바이트에 맞춰 너무 짧거나 길지 않게 균형 있게 다시 써줘.'
      },
    ];

    const byCategory = {
      '행동발달': [
        {
          id: 'regen-behavior-growth',
          label: '성장 과정 보이게',
          instruction: '좋은 학생이라는 결론보다 평소 행동의 변화, 관계 속 기여, 성장 과정이 자연스럽게 드러나게 다시 써줘.'
        },
        {
          id: 'regen-behavior-balanced',
          label: '아쉬운 점도 자연스럽게',
          instruction: '너무 완벽한 학생처럼 쓰지 말고, 아쉬운 점은 낙인찍지 않으면서 성장 가능성이 보이게 자연스럽게 반영해줘.'
        },
      ],
      '교과': [
        {
          id: 'regen-subject-thinking',
          label: '사고 과정 보이게',
          instruction: '활동을 했다는 나열보다 학생이 어떤 의문을 갖고, 어떻게 탐구하고, 어떤 방식으로 개념을 이해했는지 사고 흐름이 보이게 다시 써줘.'
        },
        {
          id: 'regen-subject-criteria',
          label: '성취기준 연결',
          instruction: '평가기준이나 성취수준이 학생별 관찰 내용과 자연스럽게 연결되도록 다시 써줘. 단, 참고자료 문구만 풀어쓰지 말아줘.'
        },
      ],
      '교과_고등': [
        {
          id: 'regen-high-inquiry',
          label: '심화탐구 느낌',
          instruction: '수업에서 배운 개념을 바탕으로 더 깊게 탐구한 흐름이 자연스럽게 보이게 다시 써줘.'
        },
        {
          id: 'regen-high-career',
          label: '진로와 연결',
          instruction: '진로와 연결되면 좋겠어. 억지스럽지 않게 탐구 내용과 관심 분야가 이어지게 다시 써줘.'
        },
        {
          id: 'regen-high-process',
          label: '과정 중심으로',
          instruction: '결과만 나열하지 말고 탐구 동기, 과정, 생각의 변화가 보이게 다시 써줘.'
        },
      ],
      '창체': [
        {
          id: 'regen-creative-role',
          label: '역할·기여 중심',
          instruction: '참여 사실보다 활동 속에서 맡은 역할, 공동체에 기여한 점, 실제 행동이 잘 보이게 다시 써줘.'
        },
        {
          id: 'regen-creative-less-list',
          label: '활동 나열 줄이기',
          instruction: '여러 활동을 줄줄이 나열하지 말고 핵심 활동과 학생별 특징이 보이도록 정리해서 다시 써줘.'
        },
      ],
      '동아리': [
        {
          id: 'regen-club-role',
          label: '역할 더 보이게',
          instruction: '동아리에서 이 학생이 맡은 역할과 기여가 더 잘 보이게 다시 써줘.'
        },
        {
          id: 'regen-club-output',
          label: '산출물 살리기',
          instruction: '활동 결과물이나 실험, 발표, 보고서 같은 산출물이 더 구체적으로 드러나게 다시 써줘.'
        },
        {
          id: 'regen-club-teamwork',
          label: '협업 강조',
          instruction: '동아리원들과 협업하고 소통한 모습이 더 자연스럽게 보이게 다시 써줘.'
        },
      ],
      '진로': [
        {
          id: 'regen-career-process',
          label: '탐색 흐름 살리기',
          instruction: '진로를 확정한 느낌보다 관심 분야를 탐색해가는 과정, 구체 활동, 후속 연결이 보이게 다시 써줘.'
        },
        {
          id: 'regen-career-no-overclaim',
          label: '진로 단정 줄이기',
          instruction: '희망 진로나 역량을 과하게 단정하지 말고, 관찰 가능한 탐색 활동과 준비 과정을 중심으로 다시 써줘.'
        },
      ],
    };

    return [...(byCategory[resultCategory] || []), ...common];
  };

  const handleSelectRegeneratePreset = (preset) => {
    setRegeneratePresetId(preset.id);
    setRegenerateInstruction(preset.instruction);
    trackSaenggibuUxEvent('saenggibu_regenerate_preset', {
      saenggibu_category: resultCategory || selectedCategory,
      is_batch: false,
      preset_id: preset.id,
    });
  };

  // 설정(학교급·영역·분량·문체)은 두고 학생 입력·결과·요청 목록만 비운 뒤 입력칸으로 올린다.
  const handleNextStudent = () => {
    const category = resultCategory || selectedCategory;
    trackSaenggibuUxEvent('saenggibu_next_student', {
      saenggibu_category: category,
      is_batch: false,
      surface: 'single',
      chain_count: regenerationChain.length,
      versions_count: versions.filter(Boolean).length,
    });
    clearGeneratedResultState();
    historySourceRecordIdRef.current = '';
    restoredUploadedFilesMetaRef.current = [];
    setGenerationData(nextStudentGenerationData);
    setFileUpload((prev) => (prev.multiStudent && prev.parsedStudents?.length > 1
      ? { ...prev, selectedStudent: null, selectedItems: [], appliedEvalUnitCount: 0 }
      : {
        file: null, files: [], uploading: false, error: null, analysis: null, students: [],
        selectedStudents: [], selectedItems: [], appliedEvalUnitCount: 0, multiStudent: false,
        parsedStudents: [], selectedStudent: null, pendingReview: null, reviewConfirmed: false,
      }));
    if (category === '교과_고등') setHighSchoolData(nextStudentHighSchoolData);
    if (category === '동아리') setClubData(nextStudentClubData);
    if (category === '진로') setCareerData(nextStudentCareerData());
    setRosterLink(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => promptTextareaRef.current?.focus({ preventScroll: true }), 300);
    showToast('설정은 그대로 두고 다음 학생을 쓸 수 있게 비웠어요', 'success');
  };

  const canLinkRoster = classRosters.length > 0 && !isAnonymousUser && selectedCategory !== '비슷한문장';
  const resultCardLabel = (index) => (
    (resultCategory === '교과' || resultCategory === '창체')
      ? `학생 ${index + 1}`
      : (index === 0 ? '기본' : `버전 ${index + 1}`)
  );
  const memoInput = String(generationData.prompt || '').trim();
  const canSaveInputAsMemo = Boolean(rosterLink?.recordId) && resultCategory === '행동발달' && memoInput.length > 0
    && memoInput !== memoPromptRef.current && memoInput !== memoSavedText;

  const saveInputAsMemo = async () => {
    const target = rosterLink;
    const text = memoInput.slice(0, MEMO_MAX_CHARS);
    try {
      await addStudentMemo(target.rosterId, target.sid, { text, area: suggestMemoArea(text) });
      setMemoSavedText(memoInput);
      trackClassRosterEvent('student_memo_from_form', { roster_id: target.rosterId, memo_chars: text.length });
      showToast(`${target.name} 메모로 남겼어요`, 'success', 5000, {
        label: '보기',
        onClick: () => navigate(`/classes/${encodeURIComponent(target.rosterId)}/students/${encodeURIComponent(target.sid)}`),
      });
    } catch (error) {
      showToast(error.message || '메모를 남기지 못했어요. 다시 시도해 주세요', 'error');
    }
  };

  const linkRecordToStudent = async (target, recordId, versionIndex, stage) => {
    const trackMeta = {
      saenggibu_category: resultCategory || selectedCategory,
      is_batch: false,
      surface: 'single',
      stage,
      version_index: versionIndex,
      record_id: recordId,
    };
    try {
      await linkSingleRecord(target.rosterId, { recordId, sid: target.sid, versionIndex });
      setRosterLink({ ...target, recordId, versionIndex });
      trackSaenggibuUxEvent('saenggibu_roster_link', trackMeta);
      showToast(
        stage === 'copy'
          ? `${target.name} 기록에 담긴 글을 바꿨어요 · ${resultCardLabel(versionIndex)}`
          : `${target.name} 기록에 담았어요`,
        'success',
        5000,
        {
          label: '보기',
          onClick: () => navigate(`/classes/${encodeURIComponent(target.rosterId)}/students/${encodeURIComponent(target.sid)}`),
        },
      );
    } catch (error) {
      trackSaenggibuUxEvent('saenggibu_roster_link', { ...trackMeta, stage: `${stage}_failed` });
      if (stage !== 'copy') showToast('학생에게 담지 못했어요. 다시 시도해 주세요', 'error');
    }
  };
  linkRecordRef.current = linkRecordToStudent;

  const handleRosterPick = ({ roster, student }) => {
    const versionIndex = rosterPickTarget?.versionIndex ?? 0;
    setRosterPickTarget(null);
    if (!savedRecordId) return;
    const target = { rosterId: roster.id, rosterLabel: roster.label || '', sid: student.sid, name: student.name };
    linkRecordToStudent(target, savedRecordId, versionIndex, 'after');
  };

  const handleRosterUnlink = async () => {
    const target = rosterLink;
    setRosterPickTarget(null);
    if (!target?.recordId) return;
    try {
      await linkSingleRecord(target.rosterId, { recordId: target.recordId, sid: null });
      setRosterLink(null);
      trackSaenggibuUxEvent('saenggibu_roster_link', {
        saenggibu_category: resultCategory || selectedCategory,
        is_batch: false,
        surface: 'single',
        stage: 'unlink',
        record_id: target.recordId,
      });
      showToast(`${target.name} 기록에서 뺐어요`, 'success');
    } catch (error) {
      showToast('빼지 못했어요. 다시 시도해 주세요', 'error');
    }
  };

  const renderCardRosterLink = (versionIndex) => {
    if (!canLinkRoster || !savedRecordId || isStreaming || isGenerating || multiClassResults.length > 0) return null;
    const linked = rosterLink?.recordId === savedRecordId && rosterLink.versionIndex === versionIndex;
    return (
      <button
        type="button"
        data-testid="sgb-card-roster"
        onClick={() => setRosterPickTarget({ versionIndex })}
        disabled={regenerateLoading}
        title={linked ? `${rosterLink.name} 기록에 담겼어요` : '명단의 학생 기록에 담기'}
        style={{
          ...resultActionButtonStyle,
          backgroundColor: linked ? '#e8f3ff' : 'transparent',
          color: '#3182f6',
          border: '1px solid #d6e9ff',
          cursor: regenerateLoading ? 'not-allowed' : 'pointer',
          opacity: regenerateLoading ? 0.55 : 1,
        }}
      >
        {linked ? `✓ ${rosterLink.name}` : '담기'}
      </button>
    );
  };

  const handleRegenerateAll = async () => {
    const instruction = regenerateInstruction.trim();
    if (regenerateLoading || isGenerating || isStreaming) {
      showToast('이미 작성 중이에요', 'warning');
      return;
    }
    if (!instruction) {
      showToast('어떤 방향으로 다시 만들지 적어주세요', 'warning');
      return;
    }
    if (!generationResult && versions.length === 0 && multiClassResults.length === 0) {
      showToast('먼저 생기부 문구를 만들어주세요', 'warning');
      return;
    }
    if (!currentInputMatchesResult) {
      showToast('입력 설정이 바뀌었어요. 아래 만들기 버튼으로 새 결과를 만들어주세요.', 'info');
      return;
    }
    if (isAnonymousUser && anonGenCount >= 1) {
      setLoginPromptFeature('saenggibu-continue');
      setShowLoginPrompt(true);
      return;
    }

    const selectedModelInfo = SAENGGIBU_MODELS.find(m => m.id === generationData.selectedModel);
    if (selectedModelInfo?.isPro && !isProUser) {
      setShowProModal(true);
      return;
    }

    if (!regeneratePresetId) {
      trackSaenggibuUxEvent('saenggibu_regenerate_custom', {
        saenggibu_category: resultCategory || selectedCategory,
        is_batch: false,
      });
    }

    const snapshot = createGenerationSnapshot();
    const baseResultContext = resultContext || createCurrentResultContext();
    // 이전 다시 쓰기의 분량 요청 목표가 남지 않게, 서버가 쓰는 슬라이더 값으로 되돌린다.
    const regenerationResultContext = {
      ...baseResultContext,
      targetBytes: generationData.wordCount || baseResultContext.targetBytes,
    };
    try {
      generationCancelRequestedRef.current = false;
      setIsCancellingGeneration(false);
      clearActiveGenerationJobs();
      setRegenerateLoading(true);
      setIsGenerating(true);
      setLocalError('');
      resetResultStateForGeneration();
      setResultContext(regenerationResultContext);
      setIsStreaming(true);

      if (multiClassResults.length > 0 && selectedCategory === '교과') {
        await generateMultiClassSubject({
          regenerationInstruction: instruction,
          regeneratedFromRecordId: snapshot.savedRecordId,
          isRegeneration: true,
        });
      } else {
        const requestData = buildGenerationRequestPayload({
          regenerationInstruction: instruction,
          regeneratedFromRecordId: snapshot.savedRecordId,
          isRegeneration: true,
        });
        if (snapshot.regenerationChainEdited) {
          requestData.regeneration_instruction_chain_override = snapshot.regenerationChain;
          trackSaenggibuUxEvent('saenggibu_regen_chain_override_sent', {
            saenggibu_category: resultCategory || selectedCategory,
            is_batch: false,
            surface: 'single',
            chain_count: snapshot.regenerationChain.length,
          });
        }
        await runGenerationStream(requestData);
      }
      setRegenerateInstruction('');
      setRegeneratePresetId(null);
      showToast('AI가 전체 문구를 다시 만들었어요', 'success');
    } catch (error) {
      if (error.isGenerationCancelled) {
        restoreGenerationSnapshot(snapshot);
        setLocalError('');
        showToast('생성을 중단했어요. 기존 결과는 그대로 유지했어요.', 'info');
        return;
      }
      restoreGenerationSnapshot(snapshot);
      const errorMsg = error.message || '전체 문구를 다시 만들지 못했어요.';
      setLocalError(errorMsg);
      showToast(`${errorMsg} 기존 결과는 그대로 유지했어요.`, 'error');
      onError(errorMsg);
    } finally {
      setIsGenerating(false);
      setIsStreaming(false);
      setRegenerateLoading(false);
      setIsCancellingGeneration(false);
      clearActiveGenerationJobs();
    }
  };

  // 생기부 생성 함수
  const generateSaenggibu = async () => {
    if (fileUpload.pendingReview) {
      const errorMsg = '파일 분석 내용을 먼저 확인해주세요.';
      setLocalError(errorMsg);
      showToast(errorMsg, 'warning');
      setAnalysisReview({ isOpen: true, review: fileUpload.pendingReview });
      return;
    }

    const isPlanItemSelection = (fileUpload.parsedStudents || []).length > 1 && (selectedCategory === '교과' || selectedCategory === '교과_고등');
    if (isPlanItemSelection && !(fileUpload.selectedItems || []).length) {
      const errorMsg = '파일에서 찾은 평가 단위를 먼저 선택해주세요.';
      setLocalError(errorMsg);
      showToast(errorMsg, 'warning');
      return;
    }
    if (fileUpload.multiStudent && !isPlanItemSelection && !fileUpload.selectedStudent) {
      const errorMsg = '파일에서 찾은 항목을 먼저 선택해주세요.';
      setLocalError(errorMsg);
      showToast(errorMsg, 'warning');
      return;
    }

    // 비로그인 사용자: 1회차 허용, 2회차부터 가입 필수
    if (isAnonymousUser) {
      saveDataToLocalStorage();
      if (anonGenCount >= 1) {
        setLoginPromptFeature('saenggibu-continue');
        setShowLoginPrompt(true);
        return;
      }
    }

    // 🆕 고급 모드 선택 + 무료 유저 체크
    const selectedModelInfo = SAENGGIBU_MODELS.find(m => m.id === generationData.selectedModel);
    if (selectedModelInfo?.isPro && !isProUser) {
      setShowProModal(true);
      return;
    }

    // 빠르게 모드 + 교과: 두 입력창 검증
    if (uiMode === 'quick' && selectedCategory === '교과') {
      if (subjectClassCount > 1 && !isProUser) {
        setShowProModal(true);
        return;
      }
      if (totalLevelCount > proLevelTotalMax) {
        const errorMsg = `성취수준별 학생 수는 총 ${proLevelTotalMax}명 이하로 맞춰주세요.`;
        setLocalError(errorMsg);
        showToast(errorMsg, 'error');
        return;
      }
      if (subjectClassCount > 1 && totalMultiClassCount > multiClassTotalMax) {
        const errorMsg = `다반 생성은 총 ${multiClassTotalMax}명 이하로 맞춰주세요.`;
        setLocalError(errorMsg);
        showToast(errorMsg, 'error');
        return;
      }
      if (!subjectData.activityName.trim() || !subjectData.activityContent.trim()) {
        const errorMsg = '활동명(성취기준)과 평가요소를 모두 입력해주세요.';
        setLocalError(errorMsg);
        showToast(errorMsg, 'error');
        onError(errorMsg);
        return;
      }
    } else if (uiMode === 'quick' && selectedCategory === '교과_고등') {
      // 빠르게 모드 + 교과_고등: 성취기준, 학생 활동 자료, 성장 포인트 중 하나 이상 필요
      const hasAchievementStandard = highSchoolData.achievementStandard.trim();
      const hasStudentActivity = highSchoolData.studentActivity.trim();
      const hasGrowthPoints = highSchoolData.growthPoints.trim();
      const hasFiles = highSchoolData.filesContent && highSchoolData.filesContent.length > 0;
      
      if (!hasAchievementStandard && !hasStudentActivity && !hasGrowthPoints && !hasFiles) {
        const errorMsg = '성취기준, 학생 활동 자료, 또는 학생 정보 중 하나 이상을 입력해주세요.';
        setLocalError(errorMsg);
        showToast(errorMsg, 'error');
        onError(errorMsg);
        return;
      }
    } else if (selectedCategory === '동아리') {
      // 🆕 동아리: 학생 활동 내용 필수
      const hasStudentActivity = clubData.studentActivity.trim();
      const hasClubDescription = clubData.clubDescription.trim();
      const hasFiles = clubData.studentFiles.length > 0 || clubData.planFile;
      
      if (!hasStudentActivity && !hasClubDescription && !hasFiles) {
        const errorMsg = '학생 활동 내용 또는 동아리 정보를 입력해주세요.';
        setLocalError(errorMsg);
        onError(errorMsg);
        return;
      }
    } else if (selectedCategory === '진로') {
      // 🆕 진로: 진로 활동 또는 학생 정보 입력 필수
      const hasStudentActivity = careerData.studentActivity?.trim();
      const hasCareerInfo = careerData.careerInfo?.trim();
      const hasFiles = careerData.studentFiles.length > 0;
      
      if (!hasStudentActivity && !hasCareerInfo && !hasFiles) {
        const errorMsg = '진로 활동 또는 학생 정보를 입력해주세요.';
        setLocalError(errorMsg);
        showToast(errorMsg, 'error');
        onError(errorMsg);
        return;
      }
    } else {
      // 다른 모드: 기존 프롬프트 검증
      if (!generationData.prompt.trim()) {
        const errorMsg = '생성할 생기부 내용을 입력해주세요.';
        setLocalError(errorMsg);
        showToast(errorMsg, 'error');
        onError(errorMsg);
        
        // 🔝 프롬프트 입력창으로 부드럽게 스크롤하고 포커스
        scrollToPromptInput();
        
        return;
      }
    }

    const previousResultSnapshot = hasVisibleResult ? createGenerationSnapshot() : null;
    const nextResultContext = createCurrentResultContext();

    setIsGenerating(true);
    setLocalError('');
    resetResultStateForGeneration();
    setRosterLink((prev) => (prev?.recordId ? null : prev));
    setResultContext(nextResultContext);
    generationCancelRequestedRef.current = false;
    setIsCancellingGeneration(false);
    clearActiveGenerationJobs();
    setIsStreaming(true);
    scrollToGenerationStatus();

    try {
      if (subjectClassCount > 1 && selectedCategory === '교과') {
        await generateMultiClassSubject();
      } else {
        const requestData = buildGenerationRequestPayload();
        await runGenerationStream(requestData);
      }

    } catch (error) {
      if (error.isGenerationCancelled) {
        if (previousResultSnapshot) {
          restoreGenerationSnapshot(previousResultSnapshot);
        } else {
          resetResultStateForGeneration();
        }
        setLocalError('');
        showToast(
          previousResultSnapshot
            ? '생성을 중단했어요. 기존 결과는 그대로 유지했어요.'
            : '생성을 중단했어요.',
          'info'
        );
        return;
      }
      if (previousResultSnapshot) {
        restoreGenerationSnapshot(previousResultSnapshot);
      } else {
        resetResultStateForGeneration();
      }
      const errorMsg = getFriendlyGenerationErrorMessage(error);
      setLocalError(errorMsg);
      showToast(
        previousResultSnapshot
          ? `${errorMsg} 기존 결과는 그대로 유지했어요.`
          : errorMsg,
        'error'
      );
      onError(errorMsg);
      
      // 🔝 프롬프트 입력창으로 부드럽게 스크롤
      scrollToPromptInput();
    } finally {
      setIsGenerating(false);
      setIsStreaming(false);
      setIsCancellingGeneration(false);
      clearActiveGenerationJobs();
    }
  };

  const renderRefinePanel = (versionIndex = null) => {
    const key = getRefineKey(versionIndex);
    if (refinePanelKey !== key) return null;

    const currentContent = getRefineContent(versionIndex);
    const isLoading = refineLoadingKey === key;
    const isRefineLocked = Boolean(refineLoadingKey);
    const preview = refinePreview?.key === key ? refinePreview : null;
    const categoryPresets = {
      '행동발달': [
        {
          id: 'behavior-strength',
          mode: 'tone_up',
          label: '강점 더 살리기',
          instruction: '이 친구의 강점을 더 살려줘. 태도도 올바르고 잘하는 친구야.'
        },
        {
          id: 'behavior-weakness',
          mode: 'tone_down',
          label: '아쉬운 점 반영',
          instruction: '너무 잘 써줬어. 그런데 이 친구는 태도에서 아쉬운 점이 있어. 너무 좋게만 쓰지 말고, 조금 안 좋은 부분도 자연스럽게 살려서 써줘.'
        },
        {
          id: 'behavior-specific',
          mode: 'custom',
          label: '구체 행동 넣기',
          instruction: '성실하다는 말보다 평소에 보인 구체적인 행동이 보이게 해줘.'
        },
        {
          id: 'behavior-relationship',
          mode: 'custom',
          label: '관계성 살리기',
          instruction: '친구들과의 관계나 배려하는 모습이 더 자연스럽게 보이게 해줘.'
        },
        {
          id: 'behavior-growth',
          mode: 'custom',
          label: '변화 보여주기',
          instruction: '처음 모습과 달라진 점, 성장한 점이 더 잘 보이게 해줘.'
        },
      ],
      '교과': [
        {
          id: 'subject-class-attitude',
          mode: 'custom',
          label: '수업 태도 살리기',
          instruction: '수업 시간에 보여준 태도와 참여 모습이 더 잘 드러나게 해줘.'
        },
        {
          id: 'subject-thinking',
          mode: 'custom',
          label: '사고 과정 보이게',
          instruction: '정답만 잘한 느낌보다 이 과목에서 어떻게 생각하고 접근했는지가 보이게 해줘.'
        },
        {
          id: 'subject-performance',
          mode: 'custom',
          label: '수행평가 살리기',
          instruction: '수행평가에서 한 활동과 결과물이 더 구체적으로 드러나게 해줘.'
        },
        {
          id: 'subject-question',
          mode: 'tone_up',
          label: '질문·탐구 강조',
          instruction: '수업 중 질문하거나 궁금해한 점을 바탕으로 탐구한 느낌이 더 살아나게 해줘.'
        },
        {
          id: 'subject-weakness',
          mode: 'tone_down',
          label: '보통 학생 톤',
          instruction: '너무 잘하는 학생처럼 보이진 않아. 평범하지만 수업을 따라가려는 모습이 자연스럽게 보이게 해줘.'
        },
      ],
      '교과_고등': [
        {
          id: 'high-report',
          mode: 'custom',
          label: '보고서 살리기',
          instruction: '학생 보고서나 탐구 활동에서 한 구체적인 주제와 과정이 더 잘 보이게 해줘.'
        },
        {
          id: 'high-inquiry',
          mode: 'tone_up',
          label: '심화탐구 느낌',
          instruction: '수업에서 배운 개념을 바탕으로 더 깊게 탐구한 학생처럼 자연스럽게 다듬어줘.'
        },
        {
          id: 'high-career',
          mode: 'custom',
          label: '진로와 연결',
          instruction: '진로와 연결되면 좋겠어. 억지스럽지 않게 탐구 내용과 관심 분야가 이어지게 해줘.'
        },
        {
          id: 'high-process',
          mode: 'custom',
          label: '과정 중심으로',
          instruction: '결과만 나열하지 말고 탐구 동기, 과정, 생각의 변화가 보이게 해줘.'
        },
        {
          id: 'high-plain',
          mode: 'tone_neutral',
          label: '과장 줄이기',
          instruction: '내용은 좋은데 너무 대단한 학생처럼 보이지 않게, 사실 중심으로 담백하게 다듬어줘.'
        },
      ],
      '창체': [
        {
          id: 'creative-role',
          mode: 'custom',
          label: '맡은 역할 살리기',
          instruction: '활동에서 맡은 역할과 실제로 한 일이 더 잘 보이게 해줘.'
        },
        {
          id: 'creative-participation',
          mode: 'custom',
          label: '참여도 보이게',
          instruction: '단순히 참여했다는 느낌보다 얼마나 적극적으로 참여했는지가 보이게 해줘.'
        },
        {
          id: 'creative-community',
          mode: 'tone_up',
          label: '공동체성 강조',
          instruction: '학급이나 공동체에 기여한 모습, 협력하고 배려한 모습이 더 살아나게 해줘.'
        },
        {
          id: 'creative-change',
          mode: 'custom',
          label: '태도 변화 넣기',
          instruction: '활동 전후의 태도 변화나 성장한 점이 자연스럽게 드러나게 해줘.'
        },
        {
          id: 'creative-weakness',
          mode: 'tone_down',
          label: '소극적인 점 반영',
          instruction: '너무 적극적인 학생처럼 쓰진 말고, 조금 소극적인 모습도 자연스럽게 반영해서 써줘.'
        },
      ],
      '동아리': [
        {
          id: 'club-role',
          mode: 'custom',
          label: '역할 더 보이게',
          instruction: '동아리에서 이 학생이 맡은 역할과 기여가 더 잘 보이게 해줘.'
        },
        {
          id: 'club-leadership',
          mode: 'tone_up',
          label: '주도성 살리기',
          instruction: '작은 역할이라도 주도적으로 제안하고 실행한 느낌이 더 살아나게 해줘.'
        },
        {
          id: 'club-output',
          mode: 'custom',
          label: '산출물 넣기',
          instruction: '활동 결과물이나 실험, 발표, 보고서 같은 산출물이 더 구체적으로 드러나게 해줘.'
        },
        {
          id: 'club-teamwork',
          mode: 'custom',
          label: '협업 강조',
          instruction: '동아리원들과 협업하고 소통한 모습이 더 자연스럽게 보이게 해줘.'
        },
        {
          id: 'club-passive',
          mode: 'tone_down',
          label: '소극적인 편',
          instruction: '너무 주도적인 학생처럼 쓰진 말고, 다소 소극적이지만 맡은 일은 해낸 느낌으로 써줘.'
        },
      ],
      '진로': [
        {
          id: 'career-exploration',
          mode: 'custom',
          label: '탐색 흐름 살리기',
          instruction: '진로가 정해진 느낌보다 탐색해가는 과정과 고민의 흐름이 보이게 해줘.'
        },
        {
          id: 'career-major',
          mode: 'tone_up',
          label: '관심 분야 연결',
          instruction: '학생의 관심 분야와 활동 내용이 자연스럽게 이어지게 해줘.'
        },
        {
          id: 'career-motive',
          mode: 'custom',
          label: '동기 더 보이게',
          instruction: '왜 이 진로 활동에 관심을 갖게 됐는지 동기가 더 잘 보이게 해줘.'
        },
        {
          id: 'career-growth',
          mode: 'custom',
          label: '진로 성장 보이게',
          instruction: '활동 후 생각이 어떻게 넓어졌는지, 진로 인식이 어떻게 변했는지 보이게 해줘.'
        },
        {
          id: 'career-unclear',
          mode: 'tone_neutral',
          label: '진로 아직 모호',
          instruction: '진로가 아직 뚜렷하진 않아. 단정하지 말고 탐색 중인 학생처럼 자연스럽게 써줘.'
        },
      ],
    };
    const commonPresets = [
      {
        id: 'common-shorter',
        mode: 'reduce_bytes',
        label: '조금만 줄여줘',
        instruction: '내용은 좋은데 조금 길어. 핵심은 유지하고 조금만 줄여줘.'
      },
      {
        id: 'common-plain',
        mode: 'tone_neutral',
        label: '평범하게 정리',
        instruction: '좋은데 조금 과한 느낌이 있어. 평범한 생기부 문장처럼 자연스럽게 정리해줘.'
      },
    ];
    const refineOptions = [
      ...(categoryPresets[resultCategory] || categoryPresets['행동발달']),
      ...commonPresets,
    ];

    return (
      <div style={{
        margin: '10px 12px 12px',
        padding: '12px 14px 14px',
        borderRadius: '14px',
        background: '#f8f9fa',
        border: '1px solid #eef0f3',
        position: 'relative'
      }}>
        <button
          type="button"
          onClick={() => setRefinePanelKey(null)}
          style={{
            position: 'absolute',
            top: '8px',
            right: '10px',
            border: 'none',
            background: 'transparent',
            color: '#8b95a1',
            cursor: 'pointer',
            fontSize: '18px',
            lineHeight: 1
          }}
          aria-label="AI 다듬기 닫기"
        >
          ×
        </button>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          marginBottom: '10px',
          paddingRight: '26px'
        }}>
          {refineOptions.map(option => (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                setRefinePresetId(option.id);
                setRefineMode(option.mode);
                setRefineInstruction(option.instruction);
              }}
              disabled={isRefineLocked || !currentContent.trim()}
              title={option.instruction || option.label}
              style={{
                border: 'none',
                borderRadius: '999px',
                padding: '7px 11px',
                background: refinePresetId === option.id ? '#3182f6' : '#eef2f7',
                color: refinePresetId === option.id ? '#fff' : '#4e5968',
                fontSize: '12px',
                fontWeight: 600,
                cursor: isRefineLocked ? 'not-allowed' : 'pointer',
                opacity: isRefineLocked ? 0.65 : 1
              }}
            >
              {option.label}
            </button>
          ))}
        </div>

        <textarea
          value={refineInstruction}
          onChange={(event) => {
            setRefinePresetId(null);
            setRefineMode('custom');
            setRefineInstruction(event.target.value);
          }}
          placeholder="추가 요청이 있으면 적어주세요. 예: 동아리장 역할이 더 드러나게 해줘"
          rows={3}
          disabled={isRefineLocked}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            resize: 'vertical',
            border: '1px solid #dfe3e8',
            borderRadius: '10px',
            padding: '10px 12px',
            fontSize: '13px',
            lineHeight: 1.5,
            outline: 'none',
            color: '#191f28',
            background: '#fff'
          }}
        />
        <p style={{
          margin: '6px 0 0',
          fontSize: '12px',
          color: '#8b95a1',
          lineHeight: 1.5
        }}>
          문체, 어투, 분량, 꼭 살릴 표현까지 자유롭게 요청할 수 있어요.
        </p>

        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => handleRequestRefine(versionIndex, refineMode)}
            disabled={isRefineLocked || !currentContent.trim()}
            style={{
              border: 'none',
              borderRadius: '10px',
              padding: '9px 14px',
              background: '#3182f6',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 700,
              cursor: isRefineLocked ? 'not-allowed' : 'pointer',
              opacity: isRefineLocked ? 0.65 : 1
            }}
          >
            {isLoading ? '다듬는 중...' : '요청하기'}
          </button>
          {refineUndo?.key === key && (
            <button
              type="button"
              onClick={handleUndoRefine}
              disabled={isRefineLocked}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#3182f6',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              이전 문장으로 되돌리기
            </button>
          )}
        </div>

        {preview && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            borderRadius: '12px',
            background: '#fff',
            border: '1px solid #e5e8eb'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#191f28' }}>
                수정안
              </span>
              <span style={{ fontSize: '12px', color: '#8b95a1' }}>
                {preview.byteCount}B
              </span>
            </div>
            {refinePreviewEditingKey === key ? (
              <textarea
                value={refinePreviewDraft}
                onChange={(event) => setRefinePreviewDraft(event.target.value)}
                rows={6}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  border: '1px solid #dfe3e8',
                  borderRadius: '10px',
                  padding: '10px 12px',
                  fontSize: '13px',
                  lineHeight: 1.7,
                  outline: 'none',
                  color: '#191f28',
                  background: '#fff'
                }}
              />
            ) : (
              <div style={{
                whiteSpace: 'pre-wrap',
                fontSize: '13px',
                lineHeight: 1.7,
                color: '#333d4b'
              }}>
                {refinePreviewDraft || preview.after}
              </div>
            )}
            <details style={{ marginTop: '10px' }}>
              <summary style={{ cursor: 'pointer', fontSize: '12px', color: '#6b7684' }}>
                기존 문장 보기
              </summary>
              <div style={{
                marginTop: '8px',
                whiteSpace: 'pre-wrap',
                fontSize: '12px',
                lineHeight: 1.6,
                color: '#6b7684'
              }}>
                {preview.before}
              </div>
            </details>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button
                type="button"
                onClick={() => setRefinePreviewEditingKey(
                  refinePreviewEditingKey === key ? null : key
                )}
                style={{
                  border: 'none',
                  borderRadius: '10px',
                  padding: '9px 14px',
                  background: '#eef2f7',
                  color: '#4e5968',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {refinePreviewEditingKey === key ? '수정 끝내기' : '수정하기'}
              </button>
              <button
                type="button"
                onClick={handleApplyRefinePreview}
                style={{
                  border: 'none',
                  borderRadius: '10px',
                  padding: '9px 14px',
                  background: '#191f28',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                적용하기
              </button>
              <button
                type="button"
                onClick={() => {
                  setRefinePreview(null);
                  setRefinePreviewDraft('');
                  setRefinePreviewEditingKey(null);
                }}
                style={{
                  border: 'none',
                  borderRadius: '10px',
                  padding: '9px 14px',
                  background: '#eef2f7',
                  color: '#4e5968',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const selectedPromptTraits = parsePromptPieces(generationData.prompt);

  const rosterPickLine = rosterLink && !isAnonymousUser && !hasVisibleResult && !isGenerating && multiClassResults.length === 0 ? (
    <span className="sgb-roster-line is-picked" data-testid="sgb-roster-line">
      <span>학생</span>
      <strong>{rosterLink.name}</strong>
      {rosterLink.rosterLabel && <span>{rosterLink.rosterLabel}</span>}
      {rosterLink.memoCount > 0 && <span>· 메모 {rosterLink.memoCount}개 넣음</span>}
      <button type="button" className="is-quiet" aria-label="학생 선택 취소" onClick={() => setRosterLink(null)}>×</button>
    </span>
  ) : null;

  return (
    <div className="sgb-form-root">
      <SaenggibuFileDropController
        enabled={uiMode !== 'batch' && selectedCategory !== '비슷한문장' && !isGenerating && !isStreaming}
        passthroughWhenDisabled={uiMode === 'batch'}
        onFilesDropped={handleGlobalFilesDropped}
        onDisabledDrop={handleDisabledGlobalDrop}
        onInvalidDrop={handleInvalidGlobalDrop}
      />

      {/* ========== 엑셀 일괄 모드 ========== */}
      {uiMode === 'batch' && (
        <div className="sgb-batch-container">

          {/* 🎨 내 스타일 상태/토글 — 일괄 생성도 서버가 동일하게 자동 적용 (공통 + 일괄에서 고른 종류만) */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginBottom: '4px'
          }}>
            {renderMyStyleControls(isMyStyleActiveForCategory(appliedStyleProfiles, batchCategory))}
          </div>

          <BatchProcessingContainer 
            resetToken={batchResetToken}
            onBack={() => {
              setUiMode('quick');
              setBatchStep(1);
              setBatchData(null);
            }}
            currentStep={effectiveBatchStep}
            setCurrentStep={setBatchStep}
            batchData={batchData}
            setBatchData={setBatchData}
            selectedModel={generationData.selectedModel}  // 🆕 모델 정보 전달
            isProUser={isProUser}                         // 🆕 PRO 사용자 여부 전달
            onShowProModal={() => setShowProModal(true)}  // 🆕 PRO 모달 콜백 전달
            initialBatchState={activeInitialBatchState}
            onResetSession={() => setActiveInitialBatchState(null)}
            onCategoryChange={setBatchCategory}
            // 단일에서 쓰던 종류로 다중을 시작한다 (다중 미지원 종류는 컨테이너가 무시)
            initialCategory={selectedCategory}
          />
        </div>
      )}

      {/* ========== 빠르게 & 자세히 모드 ========== */}
      {uiMode !== 'batch' && (
        <>
      {/* 생성 요청 입력 */}
      <div style={{
        marginBottom: '20px',
        marginTop: '0'
      }}>
        {/* 카테고리 선택 */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <p style={{ 
              fontSize: '12px', 
              color: 'var(--whatquiz-gray-400)', 
              margin: 0,
              fontWeight: '600',
              letterSpacing: '0.02em'
            }}>
              생기부 종류
            </p>
            {renderMyStyleControls(isMyStyleActiveForCategory(appliedStyleProfiles, selectedCategory))}
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {[
              { value: '행동발달', label: '행동발달' },
              { value: '교과', label: '교과/세특' },
              { value: '교과_고등', label: '교과/세특(고등)' },
              { value: '창체', label: '창체/자율' },
              { value: '동아리', label: '동아리' },
              { value: '진로', label: '진로' },
              { value: '비슷한문장', label: '비슷한 문장' }
            ].map((category) => (
              <button
                key={category.value}
                onClick={() => handleCategoryChange(category.value)}
                style={{
                  padding: '8px 14px',
                  minHeight: '44px',
                  border: selectedCategory === category.value ? '1.5px solid var(--whatquiz-primary)' : '1.5px solid transparent',
                  borderRadius: '8px',
                  backgroundColor: selectedCategory === category.value ? 'var(--whatquiz-blue-50)' : 'var(--whatquiz-gray-50)',
                  color: selectedCategory === category.value ? 'var(--whatquiz-primary)' : 'var(--whatquiz-gray-500)',
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  fontWeight: selectedCategory === category.value ? '600' : '500'
                }}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {selectedCategory === '비슷한문장' && (
          <SimilarSentencePanel
            isProUser={isProUser}
            isAnonymousUser={isAnonymousUser}
            prefillSentence={similarPrefill || (initialState?.category === '비슷한문장' ? initialState.prompt : '')}
            initialResults={
              initialState?.category === '비슷한문장' && Array.isArray(initialState.generatedVersions)
                ? initialState.generatedVersions.map((item, index) => ({
                    content: item.content || '',
                    byte_count: item.word_count || calculateNEISBytes(item.content || ''),
                    level: item.level || null,
                    version: item.version || index + 1,
                  }))
                : []
            }
            initialRecordId={initialState?.category === '비슷한문장' ? (initialState.recordId || '') : ''}
            onNeedLogin={() => {
              setLoginPromptFeature('saenggibu-similar');
              setShowLoginPrompt(true);
            }}
            onNeedPro={() => {
              setProModalFeature('비슷한 문장');
              setShowProModal(true);
            }}
            onOriginalChange={setSimilarPrefill}
            initialCategoryHint={similarContext.categoryHint || initialState?.categoryHint || ''}
            initialTargetBytes={similarContext.targetBytes || initialState?.targetBytes || ''}
            initialCount={initialState?.similarCount || 10}
            initialVariationMode={initialState?.similarVariationMode || 'structure_keep'}
            initialInstruction={initialState?.similarInstruction || ''}
            initialLevelDistribution={initialState?.similarLevelDistribution || null}
            selectedModel={generationData.selectedModel}
          />
        )}

        {/* 동아리 탭: 전용 입력 폼 (컴포넌트 분리) */}
        {selectedCategory === '동아리' && (
          <ClubForm
            clubData={clubData}
            setClubData={setClubData}
            fileUpload={fileUpload}
            isDragging={isDragging}
            isProUser={isProUser}
            auth={auth}
            handleFileUpload={handleFileUpload}
            handleFileRemove={handleFileRemove}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            handleClubStudentFilesUpload={handleClubStudentFilesUpload}
            removeClubStudentFile={removeClubStudentFile}
            setShowLoginPrompt={setShowLoginPrompt}
            setShowProModal={setShowProModal}
            selectedCategory={selectedCategory}
            rosterPickLine={rosterPickLine}
          />
        )}

        {/* 🆕 진로 탭: 전용 입력 폼 (동아리 구조와 동일) */}
        {selectedCategory === '진로' && (
          <div style={{ marginBottom: '15px' }}>
            {/* 진로 활동 파일 input (다중) */}
            <input
              type="file"
              id="career-activity-files-input"
              accept={SUPPORTED_UPLOAD_ACCEPT}
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const newFiles = Array.from(e.target.files);
                if (newFiles.length > 0) {
                  handleCareerStudentFilesUpload(newFiles);
                }
                e.target.value = '';
              }}
            />
            
            {/* 1. 진로 활동 (텍스트 + 파일 첨부) - 동아리의 "학생 활동 내용"과 동일 */}
            <div
              className="saenggibu-inline-file-drop-target"
              data-saenggibu-drop-target="student-activity"
              data-saenggibu-drop-label="진로 활동 파일을 여기에 놓아주세요"
              style={{ marginBottom: '16px' }}
            >
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--whatquiz-gray-800)', 
                marginBottom: '8px',
                fontWeight: '600'
              }}>
                <SaenggibuHelpLabel help={HELP_TEXT.careerActivity}>진로 활동</SaenggibuHelpLabel>
              </p>
              
              <textarea
                value={careerData.studentActivity}
                onChange={(e) => setCareerData(prev => ({ ...prev, studentActivity: e.target.value }))}
                placeholder="진로 활동 내용 입력"
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '12px',
                  border: '1px solid var(--whatquiz-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                  boxSizing: 'border-box'
                }}
              />
              
              {/* 파일 첨부 (다중 - 진로 탐구 보고서, 계획서, 활동 보고서 등) */}
              <div 
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                onClick={() => {
                  if (!auth.currentUser) {
                    setShowLoginPrompt(true);
                    return;
                  }
                  if (!isProUser) {
                    setShowProModal(true);
                    return;
                  }
                  document.getElementById('career-activity-files-input').click();
                }}
                style={{
                  marginTop: '6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: 'transparent',
                  borderRadius: '6px',
                  border: '1px dashed var(--whatquiz-gray-300)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  color: 'var(--whatquiz-primary)',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                <span className="sgb-upload-row">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
                  <span>
                    {careerData.studentFiles.length > 0 
                      ? `${careerData.studentFiles.length}개 파일 첨부됨` 
                      : '진로 활동 파일 첨부'}
                  </span>
                  <FieldHelpTooltip title={HELP_TEXT.careerFile.title} items={HELP_TEXT.careerFile.items} ariaLabel="진로 활동 파일 안내" />
                </span>
              </div>
              {rosterPickLine}
              
              {/* 업로드된 파일 목록 */}
              {careerData.studentFiles.length > 0 && (
                <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {careerData.studentFiles.map((file, index) => (
                    <div key={index} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 8px',
                      backgroundColor: 'var(--whatquiz-white, #fff)',
                      borderRadius: '4px',
                      border: '1px solid var(--whatquiz-gray-200)',
                      fontSize: '12px'
                    }}>
                      <span style={{ color: 'var(--whatquiz-gray-800)', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.name}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeCareerStudentFile(index);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--whatquiz-error)',
                          cursor: 'pointer',
                          fontSize: '14px',
                          padding: '8px',
                          minWidth: '36px',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1
                        }}
                        aria-label="삭제"
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* 2. 학생 정보 + 특성 (텍스트 + AI 자동 분석 파일) - 동아리 "동아리 정보(계획서)"와 동일 */}
            <div
              className="saenggibu-inline-file-drop-target"
              data-saenggibu-drop-target="student-info"
              data-saenggibu-drop-label="학생 정보 파일을 여기에 놓아주세요"
              style={{ marginBottom: '12px' }}
            >
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--whatquiz-gray-800)', 
                marginBottom: '8px',
                fontWeight: '600'
              }}>
                <SaenggibuHelpLabel help={HELP_TEXT.careerStudentInfo}>학생 정보</SaenggibuHelpLabel>
              </p>
              <textarea
                value={careerData.careerInfo}
                onChange={(e) => setCareerData(prev => ({ ...prev, careerInfo: e.target.value }))}
                placeholder="희망학과, 특성 등 학생 정보"
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px',
                  border: '1px solid var(--whatquiz-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                  boxSizing: 'border-box'
                }}
              />
              
              {/* 📎 파일 첨부 - AI 자동 분석 (동아리 계획서와 동일) */}
              {fileUpload.uploading && selectedCategory === '진로' ? (
                // 분석 중 - 동아리와 동일한 로딩 스타일
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: 'var(--whatquiz-blue-50)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileIcon size={16} color="#ff9800" />
                    <span style={{ color: 'var(--whatquiz-yellow-800)', flex: 1 }}>
                      {fileUpload.file?.name || '파일'}
                    </span>
                    <span style={{
                      backgroundColor: 'var(--whatquiz-warning)',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{
                        width: '10px',
                        height: '10px',
                        border: '2px solid white',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></span>
                      읽는 중
                    </span>
                  </div>
                </div>
              ) : (fileUpload.file || careerData.infoFile) && selectedCategory === '진로' ? (
                // 분석 결과 확인/확정 상태
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: fileUpload.pendingReview ? 'var(--whatquiz-yellow-100)' : 'var(--whatquiz-green-100)',
                  border: fileUpload.pendingReview ? '1px solid var(--whatquiz-warning)' : '1px solid #22c55e',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: fileUpload.pendingReview ? 'var(--whatquiz-yellow-800)' : 'var(--whatquiz-green-700)', flex: 1 }}>
                      <CheckCircleIcon size={14} color="currentColor" style={{ verticalAlign: 'middle', marginRight: '3px' }} /> {fileUpload.file?.name || careerData.infoFile?.name || careerData.infoFile}
                    </span>
                    <span style={{
                      backgroundColor: getFileReviewBadgeColor(getFileReviewStatus('항목')),
                      color: 'var(--whatquiz-white, #fff)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: '700',
                      whiteSpace: 'nowrap'
                    }}>
                      {getFileReviewStatus('항목').label}
                    </span>
                    {fileUpload.pendingReview && (
                      <button
                        type="button"
                        onClick={() => setAnalysisReview({ isOpen: true, review: fileUpload.pendingReview })}
                        style={{
                          border: 'none',
                          background: 'var(--whatquiz-blue-50)',
                          color: 'var(--whatquiz-primary)',
                          borderRadius: '10px',
                          padding: '2px 8px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        분석 내용 확인
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCareerData(prev => ({ ...prev, infoFile: null, infoFileContent: null, careerInfo: '' }));
                        handleFileRemove();
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--whatquiz-error)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '0',
                        lineHeight: 1
                      }}
                    >×</button>
                  </div>
                </div>
              ) : (
                // 기본 상태 - 파일 첨부 버튼
                <div 
                  onClick={() => {
                    if (!auth.currentUser) {
                      setShowLoginPrompt(true);
                      return;
                    }
                    if (!isProUser) {
                      setShowProModal(true);
                      return;
                    }
                    document.getElementById('career-info-file-input').click();
                  }}
                  style={{
                    marginTop: '6px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    borderRadius: '6px',
                    border: '1px dashed var(--whatquiz-gray-300)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    color: 'var(--whatquiz-primary)',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  <span className="sgb-upload-row">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
                    <span>학생 정보 파일 첨부</span>
                    <FieldHelpTooltip title={HELP_TEXT.careerStudentInfo.title} items={HELP_TEXT.careerStudentInfo.items} ariaLabel="진로 학생 정보 파일 안내" />
                  </span>
                </div>
              )}
              
              {/* 숨겨진 파일 input (AI 자동 분석용) */}
              <input
                type="file"
                id="career-info-file-input"
                accept={SUPPORTED_UPLOAD_ACCEPT}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    // 기존 handleFileUpload 사용 (AI 자동 분석)
                    handleFileUpload(file);
                  }
                  e.target.value = '';
                }}
              />
            </div>
          </div>
        )}

        {/* 교과 선택 시 세부 과목 선택 - 자세히 모드에서만 */}
        {uiMode === 'detailed' && selectedCategory === '교과' && (
          <div style={{ marginBottom: '15px' }}>
            <p style={{ 
              fontSize: '14px', 
              color: 'var(--whatquiz-gray-800)', 
              marginBottom: '10px',
              fontWeight: '600'
            }}>
과목 선택:
            </p>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: '15px'
            }}>
              {subjectList.map((subject) => (
                <button
                  key={subject}
                  onClick={() => setSelectedSubject(subject)}
                  className="sgb-subject-btn sgf-hover-bg sgf-hover-border"
                  style={{
                    '--sgf-bg': selectedSubject === subject ? 'var(--whatquiz-primary)' : 'var(--whatquiz-white, #fff)',
                    '--sgf-hover-bg': selectedSubject === subject ? 'var(--whatquiz-primary)' : 'var(--whatquiz-blue-50)',
                    '--sgf-border': selectedSubject === subject ? 'var(--whatquiz-primary)' : 'var(--whatquiz-gray-300)',
                    '--sgf-hover-border': 'var(--whatquiz-primary)',
                    color: selectedSubject === subject ? 'var(--whatquiz-white, #fff)' : 'var(--whatquiz-gray-800)',
                    fontWeight: selectedSubject === subject ? 'bold' : 'normal'
                  }}
                >
                  {subject}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 빠르게 모드 + 교과: 2개 입력창 */}
        {uiMode === 'quick' && selectedCategory === '교과' && (
          <div style={{ marginBottom: '15px' }}>
            {/* 교과용 파일 input */}
            <input
              type="file"
              id="subject-file-attach-input"
              accept={SUPPORTED_UPLOAD_ACCEPT}
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  handleFileUpload(files);
                }
                e.target.value = '';
              }}
              style={{ display: 'none' }}
            />
            
            {/* 활동명(성취기준) 입력 */}
            <div className="sgb-subject-field">
              <div className="sgb-subject-label">
                <SaenggibuHelpLabel help={HELP_TEXT.subjectActivityName}>활동명(성취기준):</SaenggibuHelpLabel>
              </div>
              <textarea
                id="subject-activity-name"
                aria-label="활동명(성취기준)"
                className="sgb-subject-textarea sgb-subject-textarea--activity"
                value={subjectData.activityName}
                onChange={(e) => setSubjectData(prev => ({ ...prev, activityName: e.target.value }))}
                placeholder={`활동명 또는 성취기준을 붙여넣으세요\n예: 통계 포스터 제작\n[12확통03-01] 확률변수와 확률분포의 뜻을 안다.`}
              />
              <AchievementStandardEntry
                notice={achievementRestoreNotice}
                onOpen={() => setIsAchievementPickerOpen(true)}
                testId="asp-open-subject"
              />
            </div>

            {/* 평가요소 입력 */}
            <div
              className="sgb-subject-field saenggibu-inline-file-drop-target"
              data-saenggibu-drop-target="primary"
              data-saenggibu-drop-label="학교평가운영계획을 여기에 놓아주세요"
            >
              <div className="sgb-subject-label">
                <SaenggibuHelpLabel help={HELP_TEXT.subjectEvaluation}>평가요소</SaenggibuHelpLabel>
              </div>
              <textarea
                id="subject-activity-content"
                className="sgb-subject-textarea sgb-subject-textarea--evaluation"
                value={subjectData.activityContent}
                onChange={(e) => setSubjectData(prev => ({ ...prev, activityContent: e.target.value }))}
                placeholder="평가방법, 평가기준, 평가내용, 학생 활동 특징"
              />
              <EvaluationElementsSuggest
                activityText={subjectData.activityName}
                category="교과"
                onInsert={(next) => setSubjectData((prev) => ({ ...prev, activityContent: next }))}
                schoolLevel={schoolLevel}
                selectedStandards={selectedStandards}
                subject={uiMode === 'detailed' ? selectedSubject : undefined}
                value={subjectData.activityContent}
              />
              
              {/* 📎 파일 첨부 - 간소화된 한 줄 UI. 자료함에 자료가 있으면 클릭 시 "파일 올리기 / 자료함에서 고르기" 메뉴 (Phase 4c) */}
              {(fileUpload.files?.length || 0) < EVAL_PLAN_MAX_FILES && (
                <SaenggibuAttachMenu
                  currentUser={auth.currentUser}
                  category={selectedCategory}
                  libraryEnabled={!fileUpload.file && !fileUpload.uploading}
                  onPick={handleLibraryMaterialPick}
                  onUploadClick={() => {
                    if (!auth.currentUser) {
                      setShowLoginPrompt(true);
                      return;
                    }
                    if (!isProUser) {
                      setShowProModal(true);
                      return;
                    }
                    document.getElementById('subject-file-attach-input').click();
                  }}
                  renderTrigger={(triggerProps) => (
                    <div
                      role="button"
                      tabIndex={0}
                      {...triggerProps}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      style={{
                        marginTop: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '8px',
                        padding: '6px 12px',
                        backgroundColor: 'transparent',
                        borderRadius: '6px',
                        border: isDragging ? '1.5px dashed var(--whatquiz-primary)' : '1px dashed var(--whatquiz-gray-300)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        color: 'var(--whatquiz-primary)',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <span className="sgb-upload-row">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
                        <b>학교평가운영계획</b> {(fileUpload.files?.length || 0) > 0 ? '추가' : '첨부'}
                        <FieldHelpTooltip title={HELP_TEXT.subjectPlanFile.title} items={HELP_TEXT.subjectPlanFile.items} ariaLabel="학교평가운영계획 파일 안내" />
                      </span>
                    </div>
                  )}
                />
              )}
              {rosterPickLine && <div className="sgb-roster-line-row">{rosterPickLine}</div>}
              
              {/* 첨부된 파일 정보 표시 (교과용) */}
              {fileUpload.file && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: fileUpload.uploading ? 'var(--whatquiz-blue-50)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? 'var(--whatquiz-blue-50)' : 'var(--whatquiz-green-50)',
                  border: fileUpload.uploading ? '1px solid var(--whatquiz-primary)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? '1px solid var(--whatquiz-primary)' : '1px solid var(--whatquiz-success)',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}>
                  {/* 파일 헤더 */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, overflow: 'hidden' }}>
                      <FileIcon size={16} color={fileUpload.uploading ? 'var(--whatquiz-primary)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? 'var(--whatquiz-primary)' : 'var(--whatquiz-success)'} />
                      <span style={{ 
                        color: fileUpload.uploading ? 'var(--whatquiz-yellow-800)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? 'var(--whatquiz-blue-700)' : 'var(--whatquiz-green-700)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {(fileUpload.files?.length || 0) > 1 ? `${fileUpload.files.length}개 파일` : fileUpload.file.name}
                      </span>
                      <span style={{
                        backgroundColor: getFileReviewBadgeColor(getFileReviewStatus('과목')),
                        color: 'var(--whatquiz-white, #fff)',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {getFileReviewStatus('과목').label}
                      </span>
                      {fileUpload.pendingReview && (
                        <button
                          type="button"
                          onClick={() => setAnalysisReview({ isOpen: true, review: fileUpload.pendingReview })}
                          style={{
                            border: 'none',
                            background: 'var(--whatquiz-blue-50)',
                            color: 'var(--whatquiz-primary)',
                            borderRadius: '10px',
                            padding: '2px 8px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          분석 내용 확인
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleFileRemove}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--whatquiz-red-600)',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '18px',
                        lineHeight: 1,
                        position: 'relative',
                        zIndex: 2
                      }}
                      title="파일 제거"
                    >
                      ×
                    </button>
                  </div>
                  {renderPlanFileList()}
                  
                  {/* 파일 읽는 중 프로그레스 UI (순환 메시지) */}
                  {fileUpload.uploading && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: 'var(--whatquiz-gray-200)',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          height: '100%',
                          background: 'linear-gradient(90deg, var(--whatquiz-primary) 0%, var(--whatquiz-blue-200) 100%)',
                          borderRadius: '3px',
                          animation: 'saenggibu-progress-fill 120s ease-out forwards',
                          width: '0%'
                        }} />
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--whatquiz-gray-500)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{
                          opacity: progressMsgFade ? 1 : 0,
                          transition: 'opacity 0.3s ease'
                        }}>
                          {progressMessages[progressMsgIdx]}...
                        </span>
                        <span style={{ color: 'var(--whatquiz-gray-400)', fontSize: '12px' }}>(1~2분 소요)</span>
                      </div>
                    </div>
                  )}
                  
                  {renderPlanItemChips('항목을 선택하면 바로 위 입력란에 반영돼요')}
                </div>
              )}
              
              {/* 파일 에러 표시 (교과용) */}
              {fileUpload.error && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: 'var(--whatquiz-red-50)',
                  border: '1px solid var(--whatquiz-error)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: 'var(--whatquiz-red-700)'
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  {fileUpload.error}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 빠르게 모드 + 교과_고등: 학생 활동 자료 → 성취기준 → 성장 포인트 */}
        {uiMode === 'quick' && selectedCategory === '교과_고등' && (
          <div style={{ marginBottom: '15px' }}>
            {/* 고등 모드 파일 input (학생 활동 자료용) */}
            <input
              type="file"
              id="highschool-file-attach-input"
              accept={SUPPORTED_UPLOAD_ACCEPT}
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files);
                if (files.length > 0) {
                  handleHighSchoolFileUpload(files);
                }
                e.target.value = '';
              }}
              style={{ display: 'none' }}
            />
            
            {/* 평가 계획표 파일 input (성취기준 자동 입력용) */}
            <input
              type="file"
              id="achievement-file-input"
              accept={SUPPORTED_UPLOAD_ACCEPT}
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  handleFileUpload(files);
                }
                e.target.value = '';
              }}
              style={{ display: 'none' }}
            />
            
            {/* 1. 학생 활동 자료 (텍스트 입력 + 간소화된 파일 첨부) */}
            <div
              className="saenggibu-inline-file-drop-target"
              data-saenggibu-drop-target="student-activity"
              data-saenggibu-drop-label="학생 활동자료를 여기에 놓아주세요"
              style={{ marginBottom: '16px' }}
            >
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--whatquiz-gray-800)', 
                marginBottom: '8px',
                fontWeight: '600'
              }}>
                <SaenggibuHelpLabel help={HELP_TEXT.highStudentActivity}>학생 활동 자료</SaenggibuHelpLabel>
              </p>
              
              {/* 텍스트 입력 영역 */}
              <textarea
                value={highSchoolData.studentActivity}
                onChange={(e) => setHighSchoolData(prev => ({ ...prev, studentActivity: e.target.value }))}
                placeholder="수행평가/활동 내용 입력"
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '12px',
                  border: '1px solid var(--whatquiz-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                  boxSizing: 'border-box'
                }}
              />
              
              {/* 간소화된 파일 첨부 (한 줄) */}
              <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                onDragOver={handleHighSchoolDragOver}
                onDragLeave={handleHighSchoolDragLeave}
                onDrop={handleHighSchoolDrop}
                onClick={() => {
                  if (!auth.currentUser) {
                    setShowLoginPrompt(true);
                    return;
                  }
                  if (!isProUser) {
                    setShowProModal(true);
                    return;
                  }
                  if (highSchoolUploadProgress) {
                    showToast('파일을 분석하는 중이에요. 잠시만 기다려주세요.', 'info');
                    return;
                  }
                  document.getElementById('highschool-file-attach-input').click();
                }}
                style={{
                  marginTop: '6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: 'transparent',
                  borderRadius: '6px',
                  border: isHighSchoolDragging ? '1.5px dashed var(--whatquiz-primary)' : '1px dashed var(--whatquiz-gray-300)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  color: 'var(--whatquiz-primary)',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                <span className="sgb-upload-row">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
                  {highSchoolUploadProgress
                    ? `파일 분석 중 ${highSchoolUploadProgress.current}/${highSchoolUploadProgress.total} (${highSchoolUploadProgress.percent}%)`
                    : highSchoolData.files.length > 0
                    ? `${highSchoolData.files.length}개 파일 첨부됨` 
                    : '학생 활동자료 파일 첨부'}
                  <FieldHelpTooltip title={HELP_TEXT.highStudentFile.title} items={HELP_TEXT.highStudentFile.items} ariaLabel="학생 활동자료 파일 안내" />
                </span>
              </div>
              {rosterPickLine}
              {highSchoolUploadProgress && (
                <div style={{
                  marginTop: '6px',
                  fontSize: '12px',
                  color: 'var(--whatquiz-gray-600)'
                }}>
                  큰 파일도 안전하게 분석 준비 중이에요. 이 창을 닫지 말아주세요.
                </div>
              )}
              
              {/* 업로드된 파일 목록 (간소화) */}
              {highSchoolData.files.length > 0 && (
                <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {highSchoolData.files.map((file, index) => (
                    <div key={index} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 8px',
                      backgroundColor: 'var(--whatquiz-white, #fff)',
                      borderRadius: '4px',
                      border: '1px solid var(--whatquiz-gray-200)',
                      fontSize: '12px'
                    }}>
                      <span style={{ color: 'var(--whatquiz-gray-800)', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.name}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeHighSchoolFile(index);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--whatquiz-error)',
                          cursor: 'pointer',
                          fontSize: '14px',
                          padding: '8px',
                          minWidth: '36px',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1
                        }}
                        aria-label="삭제"
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              {highSchoolData.files.length >= 2 && (
                <label
                  data-testid="separate-reports-toggle"
                  style={{
                    marginTop: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    color: 'var(--whatquiz-gray-700)',
                    cursor: 'pointer',
                    minHeight: '36px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={separateReports}
                    onChange={(e) => setSeparateReports(e.target.checked)}
                    style={{ width: '18px', height: '18px' }}
                  />
                  보고서마다 따로 쓰기
                  <span style={{ color: 'var(--whatquiz-gray-500)', fontSize: '12px' }}>
                    서로 다른 보고서를 억지로 잇지 않아요
                  </span>
                </label>
              )}
            </div>

            {/* 2. 성취기준(평가 계획표) 입력 */}
            <div
              className="saenggibu-inline-file-drop-target"
              data-saenggibu-drop-target="plan"
              data-saenggibu-drop-label="학교평가운영계획을 여기에 놓아주세요"
              style={{ marginBottom: '12px' }}
            >
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--whatquiz-gray-800)', 
                marginBottom: '8px',
                fontWeight: '600'
              }}>
                <SaenggibuHelpLabel help={HELP_TEXT.highAchievement}>성취기준(평가 계획표)</SaenggibuHelpLabel>
              </p>
              <textarea
                aria-label="성취기준(평가 계획표)"
                value={highSchoolData.achievementStandard}
                onChange={(e) => setHighSchoolData(prev => ({ ...prev, achievementStandard: e.target.value }))}
                placeholder="성취기준(평가 계획표) 입력"
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px',
                  border: '1px solid var(--whatquiz-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                  boxSizing: 'border-box'
                }}
              />
              <AchievementStandardEntry
                notice={achievementRestoreNotice}
                onOpen={() => setIsAchievementPickerOpen(true)}
                testId="asp-open-high"
              />
              
              {/* 📎 파일 첨부 - 간소화된 한 줄 UI. 자료함에 자료가 있으면 클릭 시 "파일 올리기 / 자료함에서 고르기" 메뉴 (Phase 4c) */}
              {(fileUpload.files?.length || 0) < EVAL_PLAN_MAX_FILES && (
                <SaenggibuAttachMenu
                  currentUser={auth.currentUser}
                  category={selectedCategory}
                  libraryEnabled={!fileUpload.file && !fileUpload.uploading}
                  onPick={handleLibraryMaterialPick}
                  onUploadClick={() => {
                    if (!auth.currentUser) {
                      setShowLoginPrompt(true);
                      return;
                    }
                    if (!isProUser) {
                      setShowProModal(true);
                      return;
                    }
                    document.getElementById('achievement-file-input').click();
                  }}
                  renderTrigger={(triggerProps) => (
                    <div
                      role="button"
                      tabIndex={0}
                      {...triggerProps}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      style={{
                        marginTop: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '8px',
                        padding: '6px 12px',
                        backgroundColor: 'transparent',
                        borderRadius: '6px',
                        border: isDragging ? '1.5px dashed var(--whatquiz-primary)' : '1px dashed var(--whatquiz-gray-300)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        color: 'var(--whatquiz-primary)',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <span className="sgb-upload-row">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
                        <b>학교평가운영계획</b> {(fileUpload.files?.length || 0) > 0 ? '추가' : '첨부'}
                        <FieldHelpTooltip title={HELP_TEXT.subjectPlanFile.title} items={HELP_TEXT.subjectPlanFile.items} ariaLabel="평가계획표 파일 안내" />
                      </span>
                    </div>
                  )}
                />
              )}
              
              {/* 첨부된 파일 정보 표시 (교과_고등용) */}
              {fileUpload.file && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: fileUpload.uploading ? 'var(--whatquiz-blue-50)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? 'var(--whatquiz-blue-50)' : 'var(--whatquiz-green-50)',
                  border: fileUpload.uploading ? '1px solid var(--whatquiz-primary)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? '1px solid var(--whatquiz-primary)' : '1px solid var(--whatquiz-success)',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}>
                  {/* 파일 헤더 */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, overflow: 'hidden' }}>
                      <FileIcon size={16} color={fileUpload.uploading ? 'var(--whatquiz-primary)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? 'var(--whatquiz-primary)' : 'var(--whatquiz-success)'} />
                      <span style={{ 
                        color: fileUpload.uploading ? 'var(--whatquiz-yellow-800)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? 'var(--whatquiz-blue-700)' : 'var(--whatquiz-green-700)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {(fileUpload.files?.length || 0) > 1 ? `${fileUpload.files.length}개 파일` : fileUpload.file.name}
                      </span>
                      <span style={{
                        backgroundColor: getFileReviewBadgeColor(getFileReviewStatus('과목')),
                        color: 'var(--whatquiz-white, #fff)',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {getFileReviewStatus('과목').label}
                      </span>
                      {fileUpload.pendingReview && (
                        <button
                          type="button"
                          onClick={() => setAnalysisReview({ isOpen: true, review: fileUpload.pendingReview })}
                          style={{
                            border: 'none',
                            background: 'var(--whatquiz-blue-50)',
                            color: 'var(--whatquiz-primary)',
                            borderRadius: '10px',
                            padding: '2px 8px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          분석 내용 확인
                        </button>
                      )}
                    </div>
                    <button
                      onClick={handleFileRemove}
                      disabled={fileUpload.uploading}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: fileUpload.uploading ? 'var(--whatquiz-gray-400)' : 'var(--whatquiz-error)',
                        cursor: fileUpload.uploading ? 'not-allowed' : 'pointer',
                        fontSize: '18px',
                        padding: '0 5px',
                        lineHeight: 1
                      }}
                    >
                      ×
                    </button>
                  </div>
                  {renderPlanFileList()}
                  
                  {/* 파일 읽는 중 프로그레스 UI (순환 메시지) */}
                  {fileUpload.uploading && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: 'var(--whatquiz-gray-200)',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          height: '100%',
                          background: 'linear-gradient(90deg, var(--whatquiz-primary) 0%, var(--whatquiz-blue-200) 100%)',
                          borderRadius: '3px',
                          animation: 'saenggibu-progress-fill 120s ease-out forwards',
                          width: '0%'
                        }} />
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--whatquiz-gray-500)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{
                          opacity: progressMsgFade ? 1 : 0,
                          transition: 'opacity 0.3s ease'
                        }}>
                          {progressMessages[progressMsgIdx]}...
                        </span>
                        <span style={{ color: 'var(--whatquiz-gray-400)', fontSize: '12px' }}>(1~2분 소요)</span>
                      </div>
                    </div>
                  )}
                  
                  {renderPlanItemChips('과목을 선택하면 바로 위 입력란에 반영돼요')}
                </div>
              )}
              
              {/* 파일 에러 표시 (교과_고등용) */}
              {fileUpload.error && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: 'var(--whatquiz-red-50)',
                  border: '1px solid var(--whatquiz-error)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: 'var(--whatquiz-red-700)'
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  {fileUpload.error}
                </div>
              )}
            </div>

            {/* 3. 학생 정보 (접힘/펼침) */}
            <div style={{ marginBottom: '12px' }}>
              <button
                type="button"
                onClick={() => setShowStudentInfo(!showStudentInfo)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: showStudentInfo ? 'var(--whatquiz-gray-50)' : 'var(--whatquiz-white, #fff)',
                  border: '1px solid var(--whatquiz-gray-200)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <span style={{ 
                  fontSize: '14px',
                  color: 'var(--whatquiz-gray-500)',
                  transition: 'transform 0.2s ease',
                  transform: showStudentInfo ? 'rotate(90deg)' : 'rotate(0deg)'
                }}>
                  ▶
                </span>
                <span style={{ 
                  fontSize: '14px', 
                  color: 'var(--whatquiz-gray-800)', 
                  fontWeight: '600',
                  flex: 1,
                  textAlign: 'left'
                }}>
                  <SaenggibuHelpLabel help={HELP_TEXT.highStudentInfo}>학생 정보 (선택)</SaenggibuHelpLabel>
                </span>
                {highSchoolData.growthPoints && (
                  <span style={{
                    fontSize: '12px',
                    padding: '3px 8px',
                    background: 'var(--whatquiz-green-50)',
                    color: 'var(--whatquiz-green-600)',
                    borderRadius: '4px',
                    fontWeight: '500'
                  }}>
                    입력됨
                  </span>
                )}
              </button>
              
              {showStudentInfo && (
                <div style={{ marginTop: '8px' }}>
                  <textarea
                    value={highSchoolData.growthPoints}
                    onChange={(e) => setHighSchoolData(prev => ({ ...prev, growthPoints: e.target.value }))}
                    placeholder="성취수준, 희망학과 등"
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '12px',
                      border: '1px solid var(--whatquiz-gray-300)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      lineHeight: '1.5'
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* 선택된 카테고리별 예시 버튼들 - 교과, 교과_고등, 동아리, 진로는 숨김 */}
        {!(uiMode === 'quick' && (selectedCategory === '교과' || selectedCategory === '교과_고등')) && 
         selectedCategory !== '동아리' && selectedCategory !== '진로' && selectedCategory !== '비슷한문장' && (
          <div style={{ marginBottom: '15px' }}>
          
          {/* 행동발달 빠르게 모드: 학생 특성 칩 */}
          {(selectedCategory === '행동발달' && uiMode === 'quick') ? (
            <BehaviorTraitPicker
              prompt={generationData.prompt}
              onPromptChange={handleBehaviorPromptChange}
              isMobile={isMobile}
              customExamples={customExamples}
              onSaveCustom={(label, text) => saveCustomExample('행동발달', label, text)}
              onDeleteCustom={(label, text) => deleteCustomExample('행동발달', label, text)}
              isAnonymousUser={isAnonymousUser}
              onRequireLogin={() => setShowLoginPrompt(true)}
              usageScope={auth?.currentUser?.uid || 'anon'}
              onTrack={trackBehaviorTraitChip}
            />
          ) : (
            /* 기존 방식 (창체, 교과) - 간단한 회색 톤 */
            <div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                marginBottom: '8px'
              }}>
                {getCurrentExamples().map((example, index) => {
                  const exampleText = typeof example === 'object' ? example.text : example;
                  const isCustom = example?.isCustom || false;
                  
                  // 커스텀인 경우 회색, 아니면 원래 스타일 유지
                  let borderColor, textColor, hoverBg;
                  if (isCustom) {
                    // 커스텀: 회색
                    borderColor = 'var(--whatquiz-gray-300)';
                    textColor = 'var(--whatquiz-gray-700)';
                    hoverBg = 'var(--whatquiz-gray-200)';
                  } else {
                    // 원래 예시: 기본 스타일
                    borderColor = 'var(--whatquiz-gray-300)';
                    textColor = 'var(--whatquiz-gray-800)';
                    hoverBg = 'var(--whatquiz-blue-50)';
                  }
                  
                  return (
                    <button
                      key={index}
                      onClick={() => handleExampleClick('request', exampleText)}
                      className="sgf-hover-bg"
                      style={{
                        position: 'relative',
                        padding: '6px 12px',
                        border: `1px solid ${borderColor}`,
                        borderRadius: '16px',
                        '--sgf-bg': isCustom ? 'var(--whatquiz-gray-100)' : 'var(--whatquiz-white, #fff)',
                        '--sgf-hover-bg': hoverBg,
                        color: textColor,
                        fontSize: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {exampleText}
                      {/* 커스텀 예시만 삭제 버튼 */}
                      {isCustom && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            if (selectedCategory === '창체') {
                              deleteCustomExample('창체', null, exampleText);
                            } else if (selectedCategory === '교과' && selectedSubject) {
                              deleteCustomExample('교과', selectedSubject, exampleText);
                            }
                          }}
                          style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--whatquiz-gray-500)',
                            color: 'var(--whatquiz-white, #fff)',
                            fontSize: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          ×
                        </span>
                      )}
                    </button>
                  );
                })}
                
                {/* + 추가 버튼 */}
                {!(addingTo?.category === selectedCategory && !addingTo?.subcategory) && (
                  <button
                    onClick={() => {
                      // 비로그인 사용자 체크
                      if (isAnonymousUser) {
                        setShowLoginPrompt(true);
                        return;
                      }
                      setAddingTo({ category: selectedCategory });
                    }}
                    className="sgf-hover-bg"
                    style={{
                      padding: '6px 12px',
                      border: '1px dashed #9ca3af',
                      borderRadius: '16px',
                      '--sgf-bg': 'var(--whatquiz-white, #fff)',
                      '--sgf-hover-bg': 'var(--whatquiz-gray-50)',
                      color: 'var(--whatquiz-gray-500)',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    + 추가
                  </button>
                )}
              </div>
              
              {/* 입력창 (별도 줄) */}
              {addingTo?.category === selectedCategory && !addingTo?.subcategory && (
                <div style={{
                  marginTop: '8px',
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    placeholder="예시 입력..."
                    value={newExampleText}
                    onChange={(e) => setNewExampleText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newExampleText.trim()) {
                        if (selectedCategory === '창체') {
                          saveCustomExample('창체', null, newExampleText.trim());
                        } else if (selectedCategory === '교과' && selectedSubject) {
                          saveCustomExample('교과', selectedSubject, newExampleText.trim());
                        }
                        setNewExampleText('');
                        setAddingTo(null);
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid var(--whatquiz-gray-300)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      if (newExampleText.trim()) {
                        if (selectedCategory === '창체') {
                          saveCustomExample('창체', null, newExampleText.trim());
                        } else if (selectedCategory === '교과' && selectedSubject) {
                          saveCustomExample('교과', selectedSubject, newExampleText.trim());
                        }
                        setNewExampleText('');
                        setAddingTo(null);
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--whatquiz-gray-700)',
                      color: 'var(--whatquiz-white, #fff)',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    추가
                  </button>
                  <button
                    onClick={() => {
                      setAddingTo(null);
                      setNewExampleText('');
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--whatquiz-gray-200)',
                      color: 'var(--whatquiz-gray-500)',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    취소
                  </button>
                </div>
              )}
            </div>
          )}
          </div>
        )}

        {/* 프롬프트 입력 영역 (파일 첨부 버튼 포함) - 교과/교과_고등 빠르게 모드, 동아리, 진로는 숨김 */}
        {!(uiMode === 'quick' && (selectedCategory === '교과' || selectedCategory === '교과_고등')) && 
         selectedCategory !== '동아리' && selectedCategory !== '진로' && selectedCategory !== '비슷한문장' && (
          <div>
        <div style={{ marginBottom: '15px' }}>
          {(selectedCategory === '창체' || selectedCategory === '행동발달') && (
            <SchoolEventTopics
              currentUser={auth?.currentUser || null}
              category={selectedCategory}
              onInsert={handleSchoolEventTopicInsert}
            />
          )}

          {/* 빠르게 모드에서만 소제목 표시 */}
          {uiMode === 'quick' && (
            <p style={{ 
              fontSize: '13px', 
              color: 'var(--whatquiz-gray-700)', 
              margin: '0 0 8px 0',
              fontWeight: '600'
            }}>
              {selectedCategory === '창체' ? '활동 내용' : '학생 특성'}
            </p>
          )}
          
          <div
            className="saenggibu-inline-file-drop-target"
            data-saenggibu-drop-target="primary"
            data-saenggibu-drop-label={
              selectedCategory === '창체'
                ? '창체 활동 파일을 여기에 놓아주세요'
                : selectedCategory === '행동발달'
                  ? '누가기록 파일을 여기에 놓아주세요'
                  : '학교평가운영계획을 여기에 놓아주세요'
            }
            style={{ position: 'relative' }}
          >
            <textarea
             ref={promptTextareaRef}
             value={generationData.prompt}
           onChange={(e) => {
             handleInputChange('prompt', e.target.value);
             if (selectedCategory === '창체') {
               const nextProfile = classifyCreativeInputProfile(e.target.value);
               setCreativeInputProfile(nextProfile);
               setGenerationData(prev => ({
                 ...prev,
                 generationCount: nextProfile === 'detailed_evidence'
                   ? 1
                   : Math.max(2, prev.generationCount),
               }));
             }
             // 사용자가 입력을 시작하면 로컬 에러 초기화
             if (localError && e.target.value.trim()) {
               setLocalError('');
             }
           }}
           onFocus={() => {
             // 포커스 시에도 로컬 에러 초기화
             if (localError) {
               setLocalError('');
             }
           }}
           placeholder={getPlaceholderText()}
           style={{
             width: '100%',
             height: '120px',
             padding: '12px',
             paddingRight: '50px',
             border: '1px solid var(--whatquiz-gray-300)',
             borderRadius: '8px',
             fontSize: '14px',
             resize: 'vertical',
             fontFamily: 'inherit',
             lineHeight: '1.5',
             boxSizing: 'border-box'
           }}
         />
         
        {/* 📎 파일 첨부 - 컴팩트 버전 (카테고리별 맞춤 안내) */}
        <input
          type="file"
          id="file-attach-input"
          accept={SUPPORTED_UPLOAD_ACCEPT}
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              handleFileUpload(file);
            }
            e.target.value = ''; // 같은 파일 재선택 가능하도록
          }}
          style={{ display: 'none' }}
        />
         
         {!fileUpload.file && (
           <div 
             ref={fileAttachRef}
             onDragOver={handleDragOver}
             onDragLeave={handleDragLeave}
             onDrop={handleDrop}
             onClick={() => {
               if (!auth.currentUser) {
                 setShowLoginPrompt(true);
                 return;
               }
               if (!isProUser) {
                 setShowProModal(true);
                 return;
               }
               document.getElementById('file-attach-input').click();
             }}
             style={{
               marginTop: '6px',
               display: 'inline-flex',
               alignItems: 'center',
               gap: '6px',
               padding: '6px 12px',
               backgroundColor: 'transparent',
               borderRadius: '6px',
               border: isDragging ? '1.5px dashed var(--whatquiz-primary)' : '1px dashed var(--whatquiz-gray-300)',
               cursor: 'pointer',
               transition: 'all 0.2s ease',
               color: 'var(--whatquiz-primary)',
               fontSize: '13px',
               fontWeight: '500'
             }}
           >
             <span className="sgb-upload-row">
               <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                 <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
               </svg>
               {selectedCategory === '창체' ? '창체 활동 파일 첨부' : '누가기록 파일 첨부'}
               <FieldHelpTooltip
                 title={(selectedCategory === '창체' ? HELP_TEXT.changcheFile : HELP_TEXT.behaviorRecord).title}
                 items={(selectedCategory === '창체' ? HELP_TEXT.changcheFile : HELP_TEXT.behaviorRecord).items}
                 ariaLabel={`${selectedCategory === '창체' ? '창체 활동' : '누가기록'} 파일 안내`}
               />
             </span>
           </div>
         )}
         {rosterPickLine}
         
         {/* 첨부된 파일 정보 표시 */}
         {fileUpload.file && (
           <div style={{
             marginTop: '8px',
             padding: '8px 12px',
             backgroundColor: fileUpload.uploading ? 'var(--whatquiz-blue-50)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? 'var(--whatquiz-blue-50)' : 'var(--whatquiz-green-50)',
             border: fileUpload.uploading ? '1px solid var(--whatquiz-primary)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? '1px solid var(--whatquiz-primary)' : '1px solid var(--whatquiz-success)',
             borderRadius: '6px',
             fontSize: '13px'
           }}>
             {/* 파일 정보 헤더 */}
             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
               <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, overflow: 'hidden' }}>
                 <FileIcon size={16} color={fileUpload.uploading ? 'var(--whatquiz-primary)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? 'var(--whatquiz-primary)' : 'var(--whatquiz-success)'} />
                 <span style={{ 
                   color: fileUpload.uploading ? 'var(--whatquiz-yellow-800)' : (fileUpload.multiStudent && !fileUpload.selectedStudent) ? 'var(--whatquiz-blue-700)' : 'var(--whatquiz-green-700)',
                   overflow: 'hidden',
                   textOverflow: 'ellipsis',
                   whiteSpace: 'nowrap'
                 }}>
                   {fileUpload.file.name}
                 </span>
                 <span style={{
                   backgroundColor: getFileReviewBadgeColor(getFileReviewStatus('학생')),
                   color: 'var(--whatquiz-white, #fff)',
                   padding: '2px 8px',
                   borderRadius: '10px',
                   fontSize: '12px',
                   fontWeight: '600',
                   whiteSpace: 'nowrap'
                 }}>
                   {getFileReviewStatus('학생').label}
                 </span>
                 {fileUpload.pendingReview && (
                   <button
                     type="button"
                     onClick={() => setAnalysisReview({ isOpen: true, review: fileUpload.pendingReview })}
                     style={{
                       border: 'none',
                       background: 'var(--whatquiz-blue-50)',
                       color: 'var(--whatquiz-primary)',
                       borderRadius: '10px',
                       padding: '2px 8px',
                       fontSize: '12px',
                       fontWeight: '700',
                       cursor: 'pointer',
                       whiteSpace: 'nowrap'
                     }}
                   >
                     분석 내용 확인
                   </button>
                 )}
               </div>
               <button
                 type="button"
                 onClick={handleFileRemove}
                 style={{
                   background: 'none',
                   border: 'none',
                   color: 'var(--whatquiz-red-600)',
                   cursor: 'pointer',
                   padding: '4px',
                   display: 'flex',
                   alignItems: 'center',
                   fontSize: '18px',
                   lineHeight: 1,
                   position: 'relative',
                   zIndex: 2
                 }}
                title="파일 제거"
              >
                ×
              </button>
            </div>
            
            {/* 파일 읽는 중 프로그레스 UI */}
            {fileUpload.uploading && (
              <div style={{ marginTop: '10px' }}>
                {/* 프로그레스 바 - 0에서 90%까지 60초에 걸쳐 상승 */}
                <div style={{
                  width: '100%',
                  height: '6px',
                  backgroundColor: 'var(--whatquiz-gray-200)',
                  borderRadius: '3px',
                  overflow: 'hidden',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    height: '100%',
                    background: 'linear-gradient(90deg, var(--whatquiz-primary) 0%, var(--whatquiz-blue-200) 100%)',
                    borderRadius: '3px',
                    animation: 'saenggibu-progress-fill 120s ease-out forwards',
                    width: '0%'
                  }} />
                </div>
                {/* 읽는 중 멘트 (순환 메시지) */}
                <div style={{
                  fontSize: '12px',
                  color: 'var(--whatquiz-gray-500)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{
                    display: 'inline-block',
                    opacity: progressMsgFade ? 1 : 0,
                    transition: 'opacity 0.3s ease'
                  }}>
                    {progressMessages[progressMsgIdx]}...
                  </span>
                  <span style={{ color: 'var(--whatquiz-gray-400)', fontSize: '12px' }}>
                    (파일 크기에 따라 1~2분 소요)
                  </span>
                </div>
              </div>
            )}
             
             {/* 다중 항목 선택 UI (학생/과목/활동) */}
             {fileUpload.multiStudent && fileUpload.parsedStudents.length > 0 && (
               <div style={{ 
                 marginTop: '10px',
                 padding: '14px',
                 backgroundColor: '#f8f9fa',
                 border: 'none',
                 borderRadius: '16px'
               }}>
                 <div style={{ 
                   fontSize: '13px',
                   color: 'var(--whatquiz-gray-700)',
                   marginBottom: '8px',
                   fontWeight: '600',
                   display: 'flex',
                   alignItems: 'center',
                   gap: '6px'
                 }}>
                   {fileUpload.selectedStudent 
                     ? `선택한 항목: ${fileUpload.selectedStudent.name}`
                     : selectedCategory === '교과' 
                       ? '과목/활동을 선택하면 분석 내용을 확인할 수 있어요'
                       : selectedCategory === '교과_고등'
                         ? '과목을 선택하면 분석 내용을 확인할 수 있어요'
                         : '학생을 선택하면 분석 내용을 확인할 수 있어요'}
                 </div>
                 <div style={{ 
                   display: 'flex', 
                   flexWrap: 'wrap', 
                   gap: '8px'
                 }}>
                   {fileUpload.parsedStudents.map((student) => (
                     <button
                       key={student.id}
                       type="button"
                       onClick={() => handleStudentSelect(student)}
                       style={{
                         minHeight: '36px',
                         padding: '8px 14px',
                         borderRadius: '999px',
                         border: fileUpload.selectedStudent?.id === student.id 
                           ? 'none'
                           : '1px solid var(--whatquiz-gray-200)',
                         backgroundColor: fileUpload.selectedStudent?.id === student.id 
                           ? 'var(--whatquiz-primary)' 
                           : 'var(--whatquiz-white, #fff)',
                         color: fileUpload.selectedStudent?.id === student.id 
                           ? 'var(--whatquiz-white, #fff)' 
                           : 'var(--whatquiz-gray-700)',
                         fontSize: '13px',
                         fontWeight: fileUpload.selectedStudent?.id === student.id ? '600' : '500',
                         cursor: 'pointer',
                         transition: 'all 0.15s ease',
                         display: 'flex',
                         alignItems: 'center',
                         gap: '4px',
                         boxShadow: fileUpload.selectedStudent?.id === student.id ? '0 4px 12px rgba(49, 130, 246, 0.20)' : '0 1px 2px rgba(0, 0, 0, 0.04)'
                       }}
                     >
                       {fileUpload.selectedStudent?.id === student.id && (
                         <CheckCircleIcon size={12} color="currentColor" />
                       )}
                       {student.name}
                     </button>
                   ))}
                 </div>
                 
                 {/* 선택된 항목 상세 정보 (축약) */}
                 {fileUpload.selectedStudent && (
                   <div style={{
                     marginTop: '10px',
                     padding: '8px 10px',
                     backgroundColor: 'var(--whatquiz-white, #fff)',
                     borderRadius: '12px',
                     fontSize: '12px',
                     color: 'var(--whatquiz-gray-600)',
                     lineHeight: '1.5'
                   }}>
                     <div style={{ fontWeight: '600', color: 'var(--whatquiz-gray-900)', marginBottom: '4px' }}>
                       {selectedCategory === '교과' || selectedCategory === '교과_고등'
                         ? `${fileUpload.selectedStudent.name}`
                         : `${fileUpload.selectedStudent.name}의 기록`}
                     </div>
                     <div style={{
                       maxHeight: '60px',
                       overflow: 'hidden',
                       textOverflow: 'ellipsis',
                       display: '-webkit-box',
                       WebkitLineClamp: 3,
                       WebkitBoxOrient: 'vertical'
                     }}>
                       {fileUpload.selectedStudent.achievement_standard || 
                        fileUpload.selectedStudent.records || 
                        fileUpload.selectedStudent.traits ||
                        fileUpload.selectedStudent.activity_content}
                     </div>
                   </div>
                 )}

                 {canMakeFileStudentsBatch && (
                   <div className="sgb-file-batch-offer">
                     <span>반 전체를 만든다면 한 번에 만들 수 있어요</span>
                     <button type="button" onClick={handleMakeFileStudentsBatch}>
                       {!isProUser && <span className="sgb-file-batch-offer__pro">PRO</span>}
                       이 파일로 {fileBatchStudentCount}명 한 번에 만들기
                     </button>
                   </div>
                 )}
               </div>
             )}
           </div>
         )}
         
         {/* 파일 에러 표시 */}
         {fileUpload.error && (
           <div style={{
             marginTop: '8px',
             padding: '8px 12px',
             backgroundColor: 'var(--whatquiz-red-50)',
             border: '1px solid var(--whatquiz-error)',
             borderRadius: '6px',
             fontSize: '13px',
             color: 'var(--whatquiz-red-700)'
           }}>
             <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
             {fileUpload.error}
           </div>
         )}
        </div>
      </div>
        </div>
      )}
      </div>

      {/* 생성 설정 - 행동발달만 입력 후 노출, 나머지는 기본 노출 */}
      {shouldShowGenerationSettings && (
      <div className="sgb-options-section sgb-settings-reveal">
        <div className="sgb-options-detail">
        {/* 바이트 수 설정 */}
        <div className="sgb-option-card">
          <label className="sgb-option-label">
            목표 바이트 <span className="sgb-option-value">{generationData.wordCount}B</span>
          </label>
          <input
            type="range"
            aria-label="목표 바이트 수 설정"
            className="saenggibu-range-slider"
            min="100"
            max={singleMaxBytes}
            step="100"
            value={generationData.wordCount}
            onChange={(e) => handleInputChange('wordCount', parseInt(e.target.value))}
            style={{ background: getSliderBg(generationData.wordCount, 100, singleMaxBytes) }}
          />
          <div className="saenggibu-range-labels">
            <span>100B</span>
            <span>{singleMaxBytes}B</span>
          </div>
          
          {/* 창체: 바이트 용도 가이드 */}
          {selectedCategory === '창체' && (
            <div className={`sgb-byte-guide ${generationData.wordCount <= 200 ? 'nugagi' : 'saenggibu'}`}>
              {generationData.wordCount <= 200 ? (
                <><strong>누가기록</strong>에 적합한 분량이에요 (100~200B)</>
              ) : (
                <><strong>생기부</strong>에 적합한 분량이에요 (300~1000B)</>
              )}
            </div>
          )}
          
          {/* 교과: 학교급 선택 */}
          {selectedCategory === '교과' && (
            <div className="sgb-school-level-section">
              <label className="sgb-option-label">학교급</label>
              <div className="sgb-school-level-buttons">
                {['초등', '중등', '고등'].map((level) => (
                  <button
                    key={level}
                    type="button"
                    className={`sgb-school-level-btn ${schoolLevel === level ? 'active' : ''}`}
                    onClick={() => handleSchoolLevelChange(level)}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 생성 개수 설정 */}
        <div className="sgb-option-card">
          {selectedCategory === '교과' ? (
            <>
              <div className="sgb-option-header">
                <span className="sgb-option-label">성취수준별 학생 수</span>
                <span className={`sgb-count-badge ${totalLevelCount > proLevelTotalMax ? 'over-limit' : totalLevelCount === proLevelTotalMax ? 'limit' : ''}`}>
                  반당 {totalLevelCount}/{proLevelTotalMax}명
                </span>
              </div>
              {subjectLevelScheme.map((level) => (
                <div key={level.key} className="sgb-level-row">
                  <div className="sgb-level-header">
                    <span className={`sgb-level-badge ${level.className}`}>
                      {level.label}
                    </span>
                    <span className="sgb-level-count">
                      {levelCounts[level.key] || 0}명
                    </span>
                  </div>
                  <input
                    type="range"
                    aria-label={`성취수준 ${level.label} 학생 수`}
                    className="saenggibu-range-slider"
                    min="0"
                    max={proLevelTotalMax}
                    step="1"
                    value={levelCounts[level.key] || 0}
                    onChange={(e) => {
                      if (!canUseProCounts) {
                        setShowProModal(true);
                        return;
                      }
                      const newValue = parseInt(e.target.value);
                      setLevelCounts(prev => ({ ...prev, [level.key]: newValue }));
                    }}
                    onClick={() => {
                      if (!canUseProCounts) setShowProModal(true);
                    }}
                    style={{ 
                      opacity: canUseProCounts ? 1 : 0.6,
                      background: getSliderBg(levelCounts[level.key] || 0, 0, proLevelTotalMax)
                    }}
                  />
                </div>
              ))}

              <div className="sgb-multiclass-panel">
                <div className="sgb-option-header">
                  <span className="sgb-option-label">몇 개 반을 만들까요?</span>
                  <span className={`sgb-count-badge ${totalMultiClassCount > multiClassTotalMax ? 'over-limit' : totalMultiClassCount === multiClassTotalMax ? 'limit' : ''}`}>
                    {subjectClassCount}개 반 × 반당 {totalLevelCount}명 = 총 {totalMultiClassCount}명
                  </span>
                </div>
                <input
                  type="range"
                  aria-label="생성할 반 수"
                  className="saenggibu-range-slider"
                  min="1"
                  max={MAX_SUBJECT_CLASSES}
                  step="1"
                  value={subjectClassCount}
                  onChange={(e) => setSubjectClassCount(e.target.value)}
                  style={{
                    background: getSliderBg(subjectClassCount, 1, MAX_SUBJECT_CLASSES)
                  }}
                />
                <div className="saenggibu-range-labels">
                  <span>1개 반</span>
                  <span>{MAX_SUBJECT_CLASSES}개 반</span>
                </div>
              </div>
            </>
          ) : selectedCategory === '창체' && creativeInputProfile === 'detailed_evidence' ? (
            <>
              <div className="sgb-option-header">
                <span className="sgb-option-label">생성 결과</span>
                <span className="sgb-count-badge">1개</span>
              </div>
              <div className="sgb-byte-guide saenggibu">
                한 학생의 활동 기록은 섞이지 않도록 문장 1개로 만들어요.
              </div>
            </>
          ) : (
            <>
              <div className="sgb-option-header">
                <span className="sgb-option-label">
                  <SaenggibuHelpLabel help={HELP_TEXT.generationCount}>
                    {selectedCategory === '창체' ? '학생 수' : '다양한 버전'}
                  </SaenggibuHelpLabel>
                </span>
                <span className="sgb-count-badge">
                  {generationData.generationCount}{selectedCategory === '창체' ? '명' : '개'}
                </span>
              </div>
              {!isProUser && firstDayUnlock?.active && (
                <p className="sgb-first-day-note" data-testid="first-day-unlock-note">
                  가입 첫날이라 오늘은 {selectedCategory === '창체' ? '학생 수' : '버전 수'}를 PRO만큼 쓸 수 있어요
                </p>
              )}
              <input
                type="range"
                aria-label={selectedCategory === '창체' ? '학생 수 설정' : '생성 개수 설정'}
                className="saenggibu-range-slider"
                min={2}
                max={maxGenerationCount}
                step="1"
                value={generationData.generationCount}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value);
                  if (!canUseProCounts && newValue > freeLimit) {
                    setShowProModal(true);
                    return;
                  }
                  handleGenerationCountChange(newValue);
                }}
                onClick={(e) => {
                  if (!generationSliderOpen) {
                    setShowProModal(true);
                    e.preventDefault();
                  }
                }}
                style={{
                  cursor: generationSliderOpen ? 'pointer' : 'not-allowed',
                  opacity: generationSliderOpen ? 1 : 0.6,
                  background: getSliderBg(generationData.generationCount, 2, maxGenerationCount)
                }}
              />
              <div className="saenggibu-range-labels">
                <span>{selectedCategory === '창체' ? '2명' : '2개'}</span>
                <span>{selectedCategory === '창체' ? `${maxGenerationCount}명` : `${maxGenerationCount}개`}</span>
              </div>
            </>
          )}
        </div>
      </div>
      </div>
      )}

      {/* 강조 포인트 - 자세히 모드에서만 표시 */}
      {uiMode === 'detailed' && (
        <div style={{
          backgroundColor: 'var(--whatquiz-gray-50)',
          padding: '14px',
          borderRadius: '10px',
          marginBottom: '16px'
        }}>
          <h3 style={{ 
            marginBottom: '12px', 
            color: 'var(--whatquiz-gray-700)',
            fontSize: '13px',
            fontWeight: '600'
          }}>
  강조할 포인트 (선택)
          </h3>
        
        {/* 강조포인트 예시 버튼들 - 아코디언 형태 */}
        <div style={{ marginBottom: '15px' }}>
          <p style={{ 
            fontSize: '14px', 
            color: 'var(--whatquiz-gray-800)', 
            marginBottom: '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--whatquiz-gray-400)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/></svg>
            카테고리별 강조 포인트 (클릭하여 펼치기)
          </p>
          
          {/* 선택된 포인트 요약 표시 */}
          {generationData.emphasizePoints.length > 0 && (
            <div style={{
              backgroundColor: 'var(--whatquiz-blue-50)',
              borderRadius: '12px',
              padding: '10px',
              marginBottom: '15px',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)'
            }}>
              <p style={{ fontSize: '12px', color: 'var(--whatquiz-primary)', marginBottom: '6px', fontWeight: '600' }}>
선택된 강조 포인트 ({generationData.emphasizePoints.length}개)
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {generationData.emphasizePoints.map((point, index) => (
                  <span key={index} style={{
                    fontSize: '12px',
                    backgroundColor: 'var(--whatquiz-white, #fff)',
                    color: 'var(--whatquiz-primary)',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    border: '1px solid var(--whatquiz-primary)'
                  }}>
                    {point}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {Object.entries(getCurrentEmphasisPoints()).map(([category, points]) => {
            const isExpanded = expandedCategories[category];
            const selectedCount = points.filter(point => generationData.emphasizePoints.includes(point)).length;
            
            return (
              <div key={category} style={{ 
                marginBottom: '8px',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)',
                background: 'var(--whatquiz-white, #fff)'
              }}>
                <button
                  onClick={() => toggleCategory(category)}
                  className="sgf-hover-bg"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    '--sgf-bg': isExpanded ? 'var(--whatquiz-gray-50)' : 'var(--whatquiz-white, #fff)',
                    '--sgf-hover-bg': 'var(--whatquiz-gray-50)',
                    border: 'none',
                    borderBottom: isExpanded ? '1px solid var(--whatquiz-gray-100)' : 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--whatquiz-gray-800)',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--whatquiz-gray-400)', flexShrink: 0 }}>
                        <path d="M12 2a4 4 0 0 1 4 4c0 3-4 6-4 6s-4-3-4-6a4 4 0 0 1 4-4z"/><circle cx="12" cy="6" r="1.5" fill="currentColor" stroke="none"/>
                        <line x1="12" y1="12" x2="12" y2="22"/>
                      </svg>
                      {category}
                    </span>
                    {selectedCount > 0 && (
                      <span style={{
                        fontSize: '12px',
                        backgroundColor: 'var(--whatquiz-primary)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontWeight: '500'
                      }}>
                        {selectedCount}개 선택
                      </span>
                    )}
                  </div>
                  <span style={{ 
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    fontSize: '12px'
                  }}>
                    ▼
                  </span>
                </button>
                
                {isExpanded && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: 'var(--whatquiz-white, #fff)'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {points.map((point, index) => (
                        <button
                          key={`${category}-${index}`}
                          onClick={() => handleExampleClick('emphasis', point)}
                          className="sgb-emphasis-tag sgf-hover-bg sgf-hover-border"
                          style={{
                            '--sgf-bg': generationData.emphasizePoints.includes(point) 
                              ? 'var(--whatquiz-blue-50)' 
                              : 'var(--whatquiz-white, #fff)',
                            '--sgf-hover-bg': 'var(--whatquiz-blue-50)',
                            color: generationData.emphasizePoints.includes(point) 
                              ? 'var(--whatquiz-primary)' 
                              : 'var(--whatquiz-gray-800)',
                            '--sgf-border': generationData.emphasizePoints.includes(point) 
                              ? 'var(--whatquiz-primary)' 
                              : 'var(--whatquiz-gray-300)',
                            '--sgf-hover-border': 'var(--whatquiz-primary)'
                          }}
                        >
                          {generationData.emphasizePoints.includes(point) ? '✓ ' : ''}{point}
                        </button>
                      ))}
                    </div>
                    {category.includes('성취기준') && (
                      <div style={{
                        marginTop: '16px',
                        padding: '12px',
                        fontSize: '12px',
                        color: 'var(--whatquiz-gray-600)',
                        backgroundColor: 'var(--whatquiz-gray-50)',
                        borderRadius: '8px'
                      }}>
                        성취기준은 참고용입니다. 정확한 정보는{' '}
                        <a 
                          href="https://ncic.re.kr/dwn/ogf/inventory.cs" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: 'var(--whatquiz-primary-dark)', fontWeight: '600', textDecoration: 'underline' }}
                        >
                          국가교육과정정보센터
                        </a>
                        {' '}에서 찾으시는 것을 권장드립니다.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
          <p style={{ fontSize: '13px', color: 'var(--whatquiz-gray-400)', marginBottom: '15px' }}>
            복수 선택 가능
          </p>
        </div>
      )}

      {/* AI 모델 선택 - 행동발달만 입력 후 노출, 나머지는 기본 노출 */}
      {shouldShowGenerationSettings && (
      <div className="sgb-settings-reveal" style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'inline-flex',
          gap: '4px',
          padding: '3px',
          backgroundColor: 'var(--whatquiz-gray-100)',
          borderRadius: '12px'
        }}>
          {SAENGGIBU_MODELS.map((model) => {
            const isSelected = generationData.selectedModel === model.id;
            
            return (
              <button
                key={model.id}
                onClick={() => {
                  setGenerationData(prev => ({ ...prev, selectedModel: model.id }));
                }}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '10px',
                  backgroundColor: isSelected ? 'var(--whatquiz-white, #fff)' : 'transparent',
                  color: isSelected ? 'var(--whatquiz-gray-900)' : 'var(--whatquiz-gray-500)',
                  fontSize: '14px',
                  fontWeight: isSelected ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: isSelected ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {model.name}
                {model.isPro && (
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: isSelected ? 'var(--whatquiz-primary)' : 'var(--whatquiz-gray-400)',
                    padding: '2px 5px',
                    borderRadius: '4px',
                    backgroundColor: isSelected ? 'var(--whatquiz-blue-50)' : 'transparent',
                    letterSpacing: '0.02em'
                  }}>
                    PRO
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {/* 선택된 모델 설명 */}
        <p style={{ 
          fontSize: '12px', 
          color: 'var(--whatquiz-gray-400)', 
          marginTop: '6px',
          marginBottom: 0,
          marginLeft: '4px'
        }}>
          {SAENGGIBU_MODELS.find(m => m.id === generationData.selectedModel)?.description || ''}
        </p>
      </div>
      )}

      {canLinkRoster && (
        <RosterStudentPicker
          open={Boolean(rosterPickTarget)}
          rosters={classRosters}
          selected={rosterLink?.recordId && rosterLink.recordId === savedRecordId ? rosterLink : null}
          title={versions.length > 1
            ? `${resultCardLabel(rosterPickTarget?.versionIndex ?? 0)} · 어느 학생에게 담을까요?`
            : '어느 학생에게 담을까요?'}
          note={rosterLink?.recordId && rosterLink.recordId === savedRecordId && versions.length > 1
            && rosterLink.versionIndex !== (rosterPickTarget?.versionIndex ?? 0)
            ? `한 결과에서는 글 하나만 담겨요. 지금은 ${resultCardLabel(rosterLink.versionIndex)} 글이 ${rosterLink.name} 기록에 있어요.`
            : ''}
          onPick={handleRosterPick}
          onClear={rosterLink?.recordId && rosterLink.recordId === savedRecordId
            && rosterLink.versionIndex === (rosterPickTarget?.versionIndex ?? 0)
            ? handleRosterUnlink
            : null}
          onClose={() => setRosterPickTarget(null)}
        />
      )}

      {/* 생성 버튼 (모바일 스티키) */}
      {selectedCategory !== '비슷한문장' && (
      <div className="saenggibu-generate-sticky">
        {selectedCategory === '행동발달' && uiMode === 'quick' && (
          <div
            aria-live="polite"
            className={`saenggibu-mobile-prompt-preview${selectedPromptTraits.length ? '' : ' saenggibu-mobile-prompt-preview--empty'}`}
          >
            <div className="saenggibu-mobile-prompt-preview__header">
              <span className="saenggibu-mobile-prompt-preview__label">선택된 특성</span>
              {selectedPromptTraits.length > 0 && (
                <span className="saenggibu-mobile-prompt-preview__count">{selectedPromptTraits.length}개</span>
              )}
            </div>
            {selectedPromptTraits.length > 0 ? (
              <div className="saenggibu-mobile-prompt-preview__chips" aria-label="선택된 학생 특성">
                {selectedPromptTraits.map((trait, index) => (
                  <span key={`${trait}-${index}`} className="saenggibu-mobile-prompt-preview__chip">
                    {trait}
                  </span>
                ))}
              </div>
            ) : (
              <span className="saenggibu-mobile-prompt-preview__text">태그를 누르면 여기에 추가돼요</span>
            )}
          </div>
        )}
        <button
          onClick={isGenerating ? handleCancelGeneration : generateSaenggibu}
          disabled={fileUpload.uploading || Boolean(highSchoolUploadProgress) || isCancellingGeneration || (isGenerating && activeGenerationJobIds.length === 0)}
          style={{
            width: '100%',
            padding: '14px 24px',
            backgroundColor: (fileUpload.uploading || highSchoolUploadProgress)
              ? 'var(--whatquiz-gray-300)'
              : isGenerating
                ? 'var(--whatquiz-red-500, #ef4444)'
                : 'var(--whatquiz-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: (fileUpload.uploading || highSchoolUploadProgress || isCancellingGeneration || (isGenerating && activeGenerationJobIds.length === 0)) ? 'not-allowed' : 'pointer',
            marginBottom: localError ? '8px' : '0',
            transition: 'all 0.2s ease',
            boxShadow: (fileUpload.uploading || highSchoolUploadProgress || isGenerating) ? 'none' : '0 2px 8px rgba(49, 130, 246, 0.25)'
          }}
        >
          {fileUpload.uploading
            ? '파일을 읽고 있어요...'
            : highSchoolUploadProgress
              ? '파일을 분석하고 있어요...'
            : isGenerating
              ? (isCancellingGeneration
                ? '중단하고 있어요...'
                : activeGenerationJobIds.length === 0
                  ? '생성 준비 중...'
                  : '생성 중단하기')
              : (subjectClassCount > 1 && selectedCategory === '교과'
                ? `${subjectClassCount}개 반 ${totalMultiClassCount}명 생성하기`
                : '생기부 생성하기')}
        </button>
      </div>
      )}

      {/* 로컬 오류 메시지 */}
      {localError && (
        <div style={{
          backgroundColor: 'var(--whatquiz-red-50)',
          border: '1px solid var(--whatquiz-red-200)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              color: 'var(--whatquiz-red-600)',
              fontSize: '14px',
              fontWeight: '500',
              lineHeight: '1.4'
            }}>
              {localError}
            </div>
            {localError.includes('입력해주세요') && (
              <div style={{
                color: 'var(--whatquiz-red-700)',
                fontSize: '12px',
                marginTop: '4px',
                opacity: 0.8
              }}>
                위의 텍스트 상자에 생성하고 싶은 생기부 내용을 자세히 입력해주세요.
              </div>
            )}
          </div>
          <button
            onClick={() => setLocalError('')}
            className="sgf-error-close"
            title="오류 메시지 닫기"
          >
            ✕
          </button>
        </div>
      )}

      {/* 생성 결과 */}
      {selectedCategory !== '비슷한문장' && (generationResult || isStreaming || versions.length > 0 || multiClassResults.length > 0) && (
        <div ref={generationStatusRef} style={{
          marginTop: '16px'
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--whatquiz-gray-900)',
                margin: 0
              }}>
                생성 결과
              </h4>
              {hasVisibleResult && (
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--whatquiz-primary)',
                  backgroundColor: 'var(--whatquiz-blue-50)',
                  padding: '3px 8px',
                  borderRadius: '6px'
                }}>
                  {getCategoryLabel(resultCategory)}
                </span>
              )}
              {isStreaming && (
                <span style={{
                  fontSize: '12px',
                  color: 'var(--whatquiz-primary)',
                  fontWeight: '500'
                }}>
                  작성 중...
                </span>
              )}
            </div>
            
            {/* 모델 태그 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {resultModel === 'gemini-2.5-flash' ? (
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--whatquiz-primary)',
                  backgroundColor: 'var(--whatquiz-blue-50)',
                  padding: '3px 8px',
                  borderRadius: '4px'
                }}>
                  고급
                </span>
              ) : (
                <>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--whatquiz-gray-400)',
                  }}>
                    빠른 모드
                  </span>
                  {!isProUser && (
                    <button
                      className="sgf-pro-retry-btn"
                      onClick={() => setShowProModal(true)}
                    >
                      고급 모드로 다시 생성
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <SaenggibuResultActions
            ariaLabel={`${getCategoryLabel(resultCategory)} 결과 작업`}
            filenamePrefix={`생기부_${getCategoryLabel(resultCategory)}`}
            items={resultActionItems}
            sheetName={getCategoryLabel(resultCategory)}
          />

          {multiClassResults.length > 0 && (() => {
            const activeResult = multiClassResults.find(result => result.id === activeClassResultId) || multiClassResults[0];
            const activeClassStartIndex = multiClassResults
              .slice(0, Math.max(0, multiClassResults.findIndex(result => result.id === activeResult?.id)))
              .reduce((sum, result) => sum + (result.versions || []).filter(Boolean).length, 0);
            return (
              <div className="sgb-multiclass-results">
                <div className="sgb-multiclass-result-tabs">
                  {multiClassResults.map(result => (
                    <button
                      key={result.id}
                      type="button"
                      className={activeResult?.id === result.id ? 'active' : ''}
                      onClick={() => setActiveClassResultId(result.id)}
                    >
                      {result.name} {result.status === 'generating' ? '작성 중' : `${result.totalCount || 0}명`}
                    </button>
                  ))}
                </div>
                {multiClassProgress.message && (
                  <div style={{
                    padding: '10px 12px',
                    borderRadius: '10px',
                    background: 'var(--whatquiz-blue-50)',
                    color: 'var(--whatquiz-blue-700)',
                    fontSize: '13px',
                    fontWeight: 600
                  }}>
                    {multiClassProgress.current}/{multiClassProgress.total} · {multiClassProgress.message}
                  </div>
                )}
                {activeResult && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {!(activeResult.versions || []).some(Boolean) && (
                      <div style={{
                        borderRadius: '12px',
                        padding: '18px',
                        background: 'var(--whatquiz-white, #fff)',
                        border: '1px solid var(--whatquiz-gray-200)',
                        color: 'var(--whatquiz-gray-500)',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}>
                        {activeResult.status === 'generating'
                          ? `${activeResult.name} 문장을 작성하고 있어요...`
                          : `${activeResult.name} 생성 대기 중`}
                      </div>
                    )}
                    {(activeResult.versions || []).map((version, index) => {
                      const globalIndex = activeClassStartIndex + index;
                      return version && (
                        <div key={`${activeResult.id}-${index}`} style={{
                          borderRadius: '12px',
                          overflow: 'hidden',
                          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                          background: 'var(--whatquiz-white, #fff)'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '6px',
                            padding: '8px 12px',
                            backgroundColor: 'var(--whatquiz-gray-50)'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                              <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--whatquiz-gray-700)' }}>
                                {activeResult.name} 학생 {index + 1}
                              </span>
                              {activeResult.levels?.[index] && (
                                <span style={{
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  ...getLevelBadgeStyle(activeResult.levels[index]),
                                  padding: '1px 6px',
                                  borderRadius: '3px'
                                }}>
                                  {activeResult.levels[index]}
                                </span>
                              )}
                              <span style={{ fontSize: '12px', color: 'var(--whatquiz-gray-400)', fontWeight: '500' }}>
                                {calculateNEISBytes(version)}B
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                              {!isStreaming && (() => {
                                const refineActionDisabled = Boolean(refineLoadingKey);
                                return (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenRefinePanel(globalIndex)}
                                    disabled={refineActionDisabled}
                                    style={{
                                      ...resultActionButtonStyle,
                                      backgroundColor: refinePanelKey === getRefineKey(globalIndex) ? '#e8f3ff' : 'transparent',
                                      color: '#3182f6',
                                      border: '1px solid #d6e9ff',
                                      cursor: refineActionDisabled ? 'not-allowed' : 'pointer',
                                      opacity: refineActionDisabled ? 0.55 : 1
                                    }}
                                  >
                                    AI로 다듬기
                                  </button>
                                );
                              })()}
                              <button
                                type="button"
                                onClick={async () => {
                                  await navigator.clipboard.writeText(version);
                                  showToast('문장을 복사했어요.', 'success');
                                }}
                                style={{
                                  ...resultActionButtonStyle,
                                  backgroundColor: 'var(--whatquiz-primary)',
                                  color: 'white',
                                  border: 'none',
                                  fontWeight: '500',
                                  cursor: 'pointer'
                                }}
                              >
                                복사
                              </button>
                              {!isStreaming && (() => {
                                const refineActionDisabled = Boolean(refineLoadingKey);
                                return (
                                  <SaenggibuResultMoreMenu
                                    items={[
                                      {
                                        key: 'manual_edit',
                                        label: '직접 수정',
                                        disabled: refineActionDisabled,
                                        onSelect: () => handleOpenManualEdit(globalIndex),
                                      },
                                      {
                                        key: 'similar',
                                        label: '비슷한 문장',
                                        disabled: refineActionDisabled,
                                        onSelect: () => toggleInlineSimilar(getRefineKey(globalIndex), version),
                                      },
                                    ]}
                                    saveExample={{
                                      text: version,
                                      category: resultCategory || selectedCategory,
                                      categoryHint: initialState?.categoryHint,
                                      wasEdited: manuallyEditedKeys.has(getRefineKey(globalIndex)),
                                      isPro: isProUser,
                                      versionIndex: globalIndex,
                                    }}
                                  />
                                );
                              })()}
                            </div>
                          </div>
                          <div style={{
                            padding: '12px',
                            fontSize: '13px',
                            lineHeight: '1.7',
                            whiteSpace: 'pre-wrap',
                            color: 'var(--whatquiz-gray-700)'
                          }}>
                            {renderResultText(getRefineKey(globalIndex), version)}
                          </div>
                          {renderEvidenceSummary(getRefineKey(globalIndex))}
                          {renderManualEditPanel(activeClassStartIndex + index)}
                          {renderRefinePanel(activeClassStartIndex + index)}
                          {renderInlineSimilarPanel(getRefineKey(globalIndex))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* 다중 버전 */}
          {multiClassResults.length === 0 && versions.length > 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {versions.map((version, index) => (
                version && (
                  <div key={index} style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                    background: 'var(--whatquiz-white, #fff)'
                  }}>
                    {/* 버전 헤더 */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '6px',
                      padding: '8px 12px',
                      backgroundColor: 'var(--whatquiz-gray-50)'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        minWidth: 0
                      }}>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: 'var(--whatquiz-gray-700)'
                        }}>
                          {resultCardLabel(index)}
                        </span>
                        {versionLevels[index] && (
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            ...getLevelBadgeStyle(versionLevels[index]),
                            padding: '1px 6px',
                            borderRadius: '3px'
                          }}>
                            {versionLevels[index]}
                          </span>
                        )}
                        <span style={{
                          fontSize: '12px',
                          color: 'var(--whatquiz-gray-400)',
                          fontWeight: '500'
                        }}>
                          {calculateNEISBytes(version)}B
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        {!isStreaming && (() => {
                          const refineActionDisabled = Boolean(refineLoadingKey);
                          return (
                            <button
                              type="button"
                              onClick={() => handleOpenRefinePanel(index)}
                              disabled={refineActionDisabled}
                              style={{
                                ...resultActionButtonStyle,
                                backgroundColor: refinePanelKey === getRefineKey(index) ? '#e8f3ff' : 'transparent',
                                color: '#3182f6',
                                border: '1px solid #d6e9ff',
                                cursor: refineActionDisabled ? 'not-allowed' : 'pointer',
                                opacity: refineActionDisabled ? 0.55 : 1
                              }}
                            >
                              AI로 다듬기
                            </button>
                          );
                        })()}
                        {renderCardRosterLink(index)}
                        <button
                          onClick={() => handleCopyResult(index)}
                          style={{
                            ...resultActionButtonStyle,
                            backgroundColor: copyStatus[`version-${index}`] === 'copied'
                              ? 'var(--whatquiz-success)'
                              : copiedHistory.has(`version-${index}`)
                                ? 'var(--whatquiz-primary-dark)'
                                : 'var(--whatquiz-primary)',
                            color: 'white',
                            border: 'none',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          {copyStatus[`version-${index}`] === 'copied' ? '복사됨' : '복사'}
                        </button>
                        {!isStreaming && (() => {
                          const refineActionDisabled = Boolean(refineLoadingKey);
                          return (
                            <SaenggibuResultMoreMenu
                              items={[
                                {
                                  key: 'manual_edit',
                                  label: '직접 수정',
                                  disabled: refineActionDisabled,
                                  onSelect: () => handleOpenManualEdit(index),
                                },
                                {
                                  key: 'similar',
                                  label: '비슷한 문장',
                                  disabled: refineActionDisabled,
                                  onSelect: () => toggleInlineSimilar(getRefineKey(index), version),
                                },
                              ]}
                              saveExample={{
                                text: version,
                                category: resultCategory || selectedCategory,
                                categoryHint: initialState?.categoryHint,
                                wasEdited: manuallyEditedKeys.has(getRefineKey(index)),
                                isPro: isProUser,
                                versionIndex: index,
                              }}
                            />
                          );
                        })()}
                      </div>
                    </div>
                    
                    {/* 버전 내용 */}
                    <div style={{
                      padding: '12px',
                      fontSize: '13px',
                      lineHeight: '1.7',
                      whiteSpace: 'pre-wrap',
                      color: 'var(--whatquiz-gray-700)'
                    }}>
                      {renderResultText(getRefineKey(index), version)}
                    </div>
                    {renderEvidenceSummary(getRefineKey(index))}
                    {renderManualEditPanel(index)}
                    {renderRefinePanel(index)}
                    {renderInlineSimilarPanel(getRefineKey(index))}
                  </div>
                )
              ))}
            </div>
          )}

          {/* 단일 버전 */}
          {multiClassResults.length === 0 && versions.length <= 1 && (
            <div style={{
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              background: 'var(--whatquiz-white, #fff)'
            }}>
              {/* 헤더 */}
              {generationResult && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: 'var(--whatquiz-gray-50)'
                }}>
                  <span style={{
                    fontSize: '12px',
                    color: 'var(--whatquiz-gray-400)',
                    fontWeight: '500'
                  }}>
                    {calculateNEISBytes(generationResult)}B
                  </span>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                    {!isStreaming && (() => {
                      const refineActionDisabled = Boolean(refineLoadingKey);
                      return (
                        <button
                          type="button"
                          onClick={() => handleOpenRefinePanel(null)}
                          disabled={refineActionDisabled}
                          style={{
                            ...resultActionButtonStyle,
                            backgroundColor: refinePanelKey === 'main' ? '#e8f3ff' : 'transparent',
                            color: '#3182f6',
                            border: '1px solid #d6e9ff',
                            cursor: refineActionDisabled ? 'not-allowed' : 'pointer',
                            opacity: refineActionDisabled ? 0.55 : 1
                          }}
                        >
                          AI로 다듬기
                        </button>
                      );
                    })()}
                    {renderCardRosterLink(0)}
                    <button
                      onClick={() => handleCopyResult(null)}
                      style={{
                        ...resultActionButtonStyle,
                        backgroundColor: copyStatus['main'] === 'copied'
                          ? 'var(--whatquiz-success)'
                          : copiedHistory.has('main')
                            ? 'var(--whatquiz-primary-dark)'
                            : 'var(--whatquiz-primary)',
                        color: 'white',
                        border: 'none',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      {copyStatus['main'] === 'copied' ? '복사됨' : '복사'}
                    </button>
                    {!isStreaming && (() => {
                      const refineActionDisabled = Boolean(refineLoadingKey);
                      return (
                        <SaenggibuResultMoreMenu
                          items={[
                            {
                              key: 'manual_edit',
                              label: '직접 수정',
                              disabled: refineActionDisabled,
                              onSelect: () => handleOpenManualEdit(null),
                            },
                            {
                              key: 'similar',
                              label: '비슷한 문장',
                              disabled: refineActionDisabled,
                              onSelect: () => toggleInlineSimilar('main', generationResult || versions[0]),
                            },
                          ]}
                          saveExample={{
                            text: generationResult || versions[0],
                            category: resultCategory || selectedCategory,
                            categoryHint: initialState?.categoryHint,
                            wasEdited: manuallyEditedKeys.has('main'),
                            isPro: isProUser,
                            versionIndex: 0,
                          }}
                        />
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* 내용 */}
              <div style={{
                minHeight: '60px',
                padding: '12px',
                fontSize: '13px',
                lineHeight: '1.7',
                whiteSpace: 'pre-wrap',
                color: 'var(--whatquiz-gray-700)'
              }}>
                {generationResult ? renderResultText('main', generationResult) : (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <TossLoadingSpinner 
                      type="saenggibu"
                      message={streamProgressMessage || null}
                      size="medium"
                      showIcon={true}
                      showProgress={true}
                      showTips={true}
                      scrollToModeButtons={scrollToModeButtons}
                      scrollToFileAttach={scrollToFileAttach}
                      slowReasons={getGenerationSlowReasons()}
                    />
                  </div>
                )}
                {isStreaming && generationResult && (
                  <span style={{
                    animation: 'blink 1s infinite',
                    marginLeft: '2px'
                  }}>
                    ▊
                  </span>
                )}
              </div>
              {generationResult && renderEvidenceSummary('main')}
              {generationResult && renderManualEditPanel(null)}
              {generationResult && renderRefinePanel(null)}
              {generationResult && renderInlineSimilarPanel('main')}
            </div>
          )}

          {renderEvidenceDialog()}

          <SaenggibuSuggestionMount
            recordId={savedRecordId}
            isGenerating={isGenerating || isStreaming}
            isAnonymous={isAnonymousUser}
            suggestionApi={suggestionApi}
            onAction={handleSuggestionAction}
          />

          {!isStreaming && currentInputMatchesResult && (generationResult || versions.some(Boolean) || multiClassResults.length > 0) && (
            <div className="sgb-regen-panel">
              <RegenerationChainList
                items={regenerationChain}
                onChange={(next) => {
                  regenerationChainEditedRef.current = true;
                  setRegenerationChain(next);
                }}
                disabled={regenerateLoading}
                surface="single"
                category={resultCategory || selectedCategory}
              />
              <div className="sgb-regen-presets">
                {getSingleRegeneratePresets().map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className={`sgb-regen-preset ${regeneratePresetId === preset.id ? 'sgb-regen-preset--active' : ''}`}
                    onClick={() => handleSelectRegeneratePreset(preset)}
                    disabled={regenerateLoading}
                    title={preset.instruction}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <textarea
                className="sgb-regen-input"
                value={regenerateInstruction}
                onChange={(event) => {
                  setRegenerateInstruction(event.target.value);
                  setRegeneratePresetId(null);
                }}
                placeholder="프리셋을 고르거나 직접 요청을 적어주세요."
                rows={3}
                disabled={regenerateLoading}
              />
              <div className="sgb-regen-actions">
                <button
                  type="button"
                  className="sgb-regen-primary"
                  onClick={handleRegenerateAll}
                  disabled={regenerateLoading || isGenerating || !regenerateInstruction.trim()}
                  title="성공 전까지 기존 결과는 유지돼요."
                >
                  {regenerateLoading ? 'AI가 다시 쓰는 중...' : isGenerating ? '결과 마무리하는 중...' : 'AI 재요청하기'}
                </button>
              </div>
              {/* 🎨 내 스타일 유도 (스타일 미보유·미적용자에게만, 새 탭) */}
              {!(myStyleMasterEnabled
                && isMyStyleActiveForCategory(appliedStyleProfiles, selectedCategory)) && (
                <p style={{
                  margin: '12px 0 0',
                  fontSize: '12px',
                  color: '#9ca3af',
                  textAlign: 'center'
                }}>
                  자주 쓰는 문체·형식은{' '}
                  <a
                    href="/saenggibu-my-style"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#3182f6', fontWeight: 600, textDecoration: 'none' }}
                  >
                    내 스타일
                  </a>
                  에 저장하면 매번 자동 적용돼요
                </p>
              )}
            </div>
          )}

          {!isStreaming && !isGenerating && hasVisibleResult && multiClassResults.length === 0 && !isAnonymousUser
            && (canSaveInputAsMemo || supportsNextStudent(resultCategory)) && (
            <div className="sgb-after-actions">
              {canSaveInputAsMemo && (
                <div className="sgb-roster-linked" data-testid="sgb-roster-linked">
                  <span>{rosterLink.name} 기록에 담겼어요</span>
                  <span aria-hidden="true">·</span>
                  <button type="button" data-testid="sgb-save-memo" onClick={saveInputAsMemo}>입력한 관찰을 메모로 남기기</button>
                </div>
              )}
              {supportsNextStudent(resultCategory) && (
                <button
                  type="button"
                  className="sgb-next-student"
                  onClick={handleNextStudent}
                  disabled={regenerateLoading}
                >
                  다음 학생 쓰기
                </button>
              )}
            </div>
          )}

          {/* 비로그인 사용자: 저장 + 연속 생성 복합 CTA */}
          {!isStreaming && multiClassResults.length === 0 && generationResult && isAnonymousUser && showSaveCTA && (
            <div style={{
              marginTop: '16px',
              padding: '20px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #EFF6FF 0%, #F0F9FF 100%)',
              border: '1px solid #DBEAFE',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '15px',
                fontWeight: '700',
                color: '#111827',
                margin: '0 0 6px 0'
              }}>
                이 결과가 마음에 드셨나요?
              </p>
              <p style={{
                fontSize: '13px',
                color: '#6B7280',
                margin: '0 0 16px 0',
                lineHeight: '1.5'
              }}>
                무료 가입하면 생성 기록이 자동 저장되고,<br/>
                나머지 학생도 연속으로 생성할 수 있어요
              </p>
              <button
                onClick={() => {
                  const currentPath = window.location.pathname + window.location.search;
                  const redirectParam = encodeURIComponent(currentPath);
                  navigate(`/auth?tab=login&redirect=${redirectParam}`);
                }}
                className="sgf-login-cta"
              >
                무료로 시작하기
              </button>
              <p style={{
                fontSize: '12px',
                color: '#9CA3AF',
                margin: '10px 0 0 0'
              }}>
                Google 계정으로 3초 가입
              </p>
            </div>
          )}

          {/* 로그인 사용자: 하단 액션 링크 */}
          {!isStreaming && multiClassResults.length === 0 && generationResult && !isAnonymousUser && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              marginTop: '4px'
            }} data-record-id={savedRecordId || undefined}>
              <button
                onClick={() => navigate('/saenggibu-history')}
                className="sgb-view-history-link"
              >
                내 기록 보기
              </button>
              {uiMode !== 'batch' && (
                <>
                  <span style={{
                    width: '1px',
                    height: '14px',
                    background: 'var(--whatquiz-gray-200)'
                  }} />
                  <button
                    onClick={() => {
                      setUiMode('batch');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="sgb-view-history-link"
                  >
                    30명 한번에 만들기
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* 스타일은 saenggibu-generation.css로 이동됨 */}
      
      {/* 카테고리 변경 확인 모달 */}
      {showCategoryConfirm && (
        <SaenggibuCategoryChangeDialog
          dialog={categoryChangeDialog}
          onCancel={() => setShowCategoryConfirm(null)}
          onConfirm={confirmCategoryChange}
        />
      )}

      <SaenggibuAnalysisReviewModal
        isOpen={analysisReview.isOpen}
        review={analysisReview.review}
        onConfirm={handleAnalysisReviewConfirm}
        onClose={resetAnalysisReview}
        onReset={(review) => {
          if (review?.resetAction === 'single') {
            switchPlanToSingle();
          } else if (review?.resetLabel === '다시 선택') {
            resetAnalysisReview();
          } else {
            handleFileRemove();
          }
        }}
      />

      {/* PRO 업그레이드 모달 */}
      {showProModal && (
        <ProUpgradeModal
          isOpen={showProModal}
          onClose={() => {
            setShowProModal(false);
            setProModalFeature('상위 모델 & 2026 최신데이터 팩트체크');
          }}
          feature={proModalFeature}
          description="상위 모델과 실시간 팩트체크로 더 정확하고 품질 높은 생기부를 만들어요."
        />
      )}
      
      {/* 🔍 파일 사전 검사 모달 */}
      <FilePreCheckModal
        isOpen={showPreCheckModal}
        onClose={handlePreCheckCancel}
        onConfirm={handlePreCheckConfirm}
        checkResult={preCheckResult}
        isLoading={preCheckLoading}
      />

      {/* 바이트 기준 가이드 모달 */}
      {showByteGuide && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 'var(--whatquiz-space-4)',
          backdropFilter: 'blur(4px)'
        }} onClick={() => setShowByteGuide(false)}>
          <div style={{
            backgroundColor: 'var(--whatquiz-white)',
            borderRadius: 'var(--whatquiz-radius-xl)',
            padding: 'var(--whatquiz-space-6)',
            maxWidth: '800px',
            maxHeight: '85vh',
            overflow: 'auto',
            position: 'relative',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid var(--whatquiz-gray-200)'
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* 헤더 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--whatquiz-space-6)',
              paddingBottom: 'var(--whatquiz-space-4)',
              borderBottom: '1px solid var(--whatquiz-gray-200)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--whatquiz-space-3)' }}>
                <DocumentIcon size={24} color="var(--whatquiz-primary)" />
                <h3 style={{
                  margin: 0,
                  color: 'var(--whatquiz-gray-900)',
                  fontSize: 'var(--whatquiz-text-xl)',
                  fontWeight: 'var(--whatquiz-font-bold)',
                  fontFamily: 'var(--whatquiz-font-family)'
                }}>
                  2026 학교생활기록부 기재요령
                </h3>
              </div>
              
              {/* 닫기 버튼 */}
              <button
                onClick={() => setShowByteGuide(false)}
                className="sgf-icon-close"
              >
                <CloseIcon size={20} color="var(--whatquiz-gray-500)" />
              </button>
            </div>
            
            {/* NEIS 바이트 계산 방식 */}
            <div style={{
              backgroundColor: 'var(--whatquiz-blue-50)',
              padding: 'var(--whatquiz-space-4)',
              borderRadius: 'var(--whatquiz-radius-lg)',
              marginBottom: 'var(--whatquiz-space-6)',
              border: '1px solid var(--whatquiz-blue-200)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--whatquiz-space-2)', 
                marginBottom: 'var(--whatquiz-space-3)' 
              }}>
                <RulerIcon size={20} color="var(--whatquiz-primary)" />
                <h4 style={{ 
                  margin: 0, 
                  color: 'var(--whatquiz-gray-800)',
                  fontSize: 'var(--whatquiz-text-lg)',
                  fontWeight: 'var(--whatquiz-font-semibold)'
                }}>
                  NEIS 바이트 계산 방식
                </h4>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'var(--whatquiz-space-3)',
                fontSize: 'var(--whatquiz-text-sm)',
                color: 'var(--whatquiz-gray-700)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 'var(--whatquiz-space-2)',
                  padding: 'var(--whatquiz-space-2)',
                  backgroundColor: 'var(--whatquiz-white)',
                  borderRadius: 'var(--whatquiz-radius-md)',
                  border: '1px solid var(--whatquiz-blue-200)'
                }}>
                  <span style={{ fontWeight: 'var(--whatquiz-font-semibold)' }}>한글 1자:</span>
                  <span style={{ color: 'var(--whatquiz-primary)', fontWeight: 'var(--whatquiz-font-bold)' }}>3 바이트</span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 'var(--whatquiz-space-2)',
                  padding: 'var(--whatquiz-space-2)',
                  backgroundColor: 'var(--whatquiz-white)',
                  borderRadius: 'var(--whatquiz-radius-md)',
                  border: '1px solid var(--whatquiz-blue-200)'
                }}>
                  <span style={{ fontWeight: 'var(--whatquiz-font-semibold)' }}>영어/숫자/공백:</span>
                  <span style={{ color: 'var(--whatquiz-primary)', fontWeight: 'var(--whatquiz-font-bold)' }}>1 바이트</span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 'var(--whatquiz-space-2)',
                  padding: 'var(--whatquiz-space-2)',
                  backgroundColor: 'var(--whatquiz-white)',
                  borderRadius: 'var(--whatquiz-radius-md)',
                  border: '1px solid var(--whatquiz-blue-200)'
                }}>
                  <span style={{ fontWeight: 'var(--whatquiz-font-semibold)' }}>엔터(Enter):</span>
                  <span style={{ color: 'var(--whatquiz-primary)', fontWeight: 'var(--whatquiz-font-bold)' }}>2 바이트</span>
                </div>
              </div>
            </div>
            
            {/* 영역별 바이트 제한 */}
            <div style={{ marginBottom: 'var(--whatquiz-space-6)' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--whatquiz-space-2)', 
                marginBottom: 'var(--whatquiz-space-4)' 
              }}>
                <ChartIcon size={20} color="var(--whatquiz-saenggibu)" />
                <h4 style={{ 
                  margin: 0, 
                  color: 'var(--whatquiz-gray-800)',
                  fontSize: 'var(--whatquiz-text-lg)',
                  fontWeight: 'var(--whatquiz-font-semibold)'
                }}>
                  영역별 최대 바이트 제한
                </h4>
              </div>
              
              <div style={{ display: 'grid', gap: 'var(--whatquiz-space-4)' }}>
                {/* 초등학교 */}
                <div style={{
                  backgroundColor: 'var(--whatquiz-gray-50)',
                  padding: 'var(--whatquiz-space-4)',
                  borderRadius: 'var(--whatquiz-radius-lg)',
                  border: '1px solid var(--whatquiz-gray-200)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 'var(--whatquiz-space-2)', 
                    marginBottom: 'var(--whatquiz-space-3)' 
                  }}>
                    <SchoolIcon size={18} color="var(--whatquiz-success)" />
                    <h5 style={{ 
                      margin: 0, 
                      color: 'var(--whatquiz-success)',
                      fontSize: 'var(--whatquiz-text-base)',
                      fontWeight: 'var(--whatquiz-font-semibold)'
                    }}>
                      초등학교
                    </h5>
                  </div>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
                    gap: 'var(--whatquiz-space-2)', 
                    fontSize: 'var(--whatquiz-text-sm)' 
                  }}>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      행동특성 및 종합의견: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>1,500 Byte</strong>
                    </div>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      과목별세부능력특기사항: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>1,500 Byte</strong>
                    </div>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      창체 자율·동아리활동: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>3,000 Byte</strong>
                    </div>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      창체 진로활동: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>2,100 Byte</strong>
                    </div>
                  </div>
                </div>
                
                {/* 중학교 */}
                <div style={{
                  backgroundColor: 'var(--whatquiz-gray-50)',
                  padding: 'var(--whatquiz-space-4)',
                  borderRadius: 'var(--whatquiz-radius-lg)',
                  border: '1px solid var(--whatquiz-gray-200)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 'var(--whatquiz-space-2)', 
                    marginBottom: 'var(--whatquiz-space-3)' 
                  }}>
                    <SchoolIcon size={18} color="var(--whatquiz-warning)" />
                    <h5 style={{ 
                      margin: 0, 
                      color: 'var(--whatquiz-warning)',
                      fontSize: 'var(--whatquiz-text-base)',
                      fontWeight: 'var(--whatquiz-font-semibold)'
                    }}>
                      중학교
                    </h5>
                  </div>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
                    gap: 'var(--whatquiz-space-2)', 
                    fontSize: 'var(--whatquiz-text-sm)' 
                  }}>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      행동특성 및 종합의견: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>1,500 Byte</strong>
                    </div>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      과목별세부능력특기사항: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>1,500 Byte</strong>
                    </div>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      창체 자율·동아리활동: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>1,500 Byte</strong>
                    </div>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      창체 진로활동: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>2,100 Byte</strong>
                    </div>
                  </div>
                </div>
                
                {/* 고등학교 */}
                <div style={{
                  backgroundColor: 'var(--whatquiz-gray-50)',
                  padding: 'var(--whatquiz-space-4)',
                  borderRadius: 'var(--whatquiz-radius-lg)',
                  border: '1px solid var(--whatquiz-gray-200)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 'var(--whatquiz-space-2)', 
                    marginBottom: 'var(--whatquiz-space-3)' 
                  }}>
                    <SchoolIcon size={18} color="var(--whatquiz-saenggibu)" />
                    <h5 style={{ 
                      margin: 0, 
                      color: 'var(--whatquiz-saenggibu)',
                      fontSize: 'var(--whatquiz-text-base)',
                      fontWeight: 'var(--whatquiz-font-semibold)'
                    }}>
                      고등학교
                    </h5>
                  </div>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
                    gap: 'var(--whatquiz-space-2)', 
                    fontSize: 'var(--whatquiz-text-sm)' 
                  }}>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      행동특성 및 종합의견: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>1,500 Byte</strong>
                    </div>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      과목별세부능력특기사항: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>1,500 Byte</strong>
                    </div>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      창체 자율·동아리활동: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>1,500 Byte</strong>
                    </div>
                    <div style={{ color: 'var(--whatquiz-gray-700)' }}>
                      창체 진로활동: <strong style={{ color: 'var(--whatquiz-gray-900)' }}>2,100 Byte</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* PDF 다운로드 섹션 */}
            <div style={{ marginBottom: 'var(--whatquiz-space-6)' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--whatquiz-space-2)', 
                marginBottom: 'var(--whatquiz-space-4)' 
              }}>
                <FileIcon size={20} color="var(--whatquiz-info)" />
                <h4 style={{ 
                  margin: 0, 
                  color: 'var(--whatquiz-gray-800)',
                  fontSize: 'var(--whatquiz-text-lg)',
                  fontWeight: 'var(--whatquiz-font-semibold)'
                }}>
                  공식 기재요령 PDF 다운로드
                </h4>
              </div>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
                gap: 'var(--whatquiz-space-3)' 
              }}>
                {[
                  { level: '초등학교', url: 'https://star.moe.go.kr/afile/fileDownload/3enmn', color: 'var(--whatquiz-success)' },
                  { level: '중학교', url: 'https://star.moe.go.kr/afile/fileDownload/HRnmn', color: 'var(--whatquiz-warning)' },
                  { level: '고등학교', url: 'https://star.moe.go.kr/afile/fileDownload/Yenmn', color: 'var(--whatquiz-saenggibu)' }
                ].map((item, index) => (
                  <button
                    key={index}
                    onClick={() => window.open(item.url, '_blank')}
                    className="sgf-hover-bg sgf-hover-color"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--whatquiz-space-2)',
                      padding: 'var(--whatquiz-space-3)',
                      '--sgf-bg': 'var(--whatquiz-white)',
                      '--sgf-hover-bg': item.color,
                      '--sgf-color': item.color,
                      '--sgf-hover-color': 'white',
                      border: `2px solid ${item.color}`,
                      borderRadius: 'var(--whatquiz-radius-lg)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: 'var(--whatquiz-text-sm)',
                      fontWeight: 'var(--whatquiz-font-medium)',
                      textAlign: 'left'
                    }}
                  >
                    <FileIcon size={18} />
                    <div>
                      <div style={{ fontWeight: 'var(--whatquiz-font-semibold)' }}>
                        2026 {item.level} 기재요령
                      </div>
                      <div style={{ fontSize: 'var(--whatquiz-text-xs)', opacity: 0.8 }}>
                        공식 PDF 다운로드
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* 주의사항 */}
            <div style={{
              backgroundColor: 'var(--whatquiz-yellow-50)',
              padding: 'var(--whatquiz-space-4)',
              borderRadius: 'var(--whatquiz-radius-lg)',
              border: '1px solid var(--whatquiz-yellow-200)',
              marginBottom: 'var(--whatquiz-space-6)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 'var(--whatquiz-space-2)' 
              }}>
                <BellIcon size={18} color="var(--whatquiz-warning)" style={{ marginTop: '2px', flexShrink: 0 }} />
                <div style={{ fontSize: 'var(--whatquiz-text-sm)', color: 'var(--whatquiz-gray-700)' }}>
                  <strong style={{ color: 'var(--whatquiz-warning)' }}>주의사항:</strong> 
                  학교마다 세부 규정이 다를 수 있으니, 해당 학교의 기재요령을 반드시 확인해주세요.
                </div>
              </div>
            </div>
            
            {/* 확인 버튼 */}
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => setShowByteGuide(false)}
                className="sgf-hover-bg"
                style={{
                  '--sgf-bg': 'var(--whatquiz-primary)',
                  '--sgf-hover-bg': 'var(--whatquiz-primary-hover)',
                  color: 'var(--whatquiz-white)',
                  border: 'none',
                  padding: 'var(--whatquiz-space-3) var(--whatquiz-space-8)',
                  borderRadius: 'var(--whatquiz-radius-lg)',
                  cursor: 'pointer',
                  fontSize: 'var(--whatquiz-text-base)',
                  fontWeight: 'var(--whatquiz-font-semibold)',
                  fontFamily: 'var(--whatquiz-font-family)',
                  transition: 'background-color 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--whatquiz-space-2)',
                  margin: '0 auto'
                }}
              >
                <CheckCircleIcon size={18} />
                확인
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* 로그인 모달 */}
      {showLoginPrompt && (
        <LoginPrompt 
          isOpen={showLoginPrompt}
          onClose={() => {
            setShowLoginPrompt(false);
            setLoginPromptFeature('saenggibu');
          }}
          feature={loginPromptFeature}
        />
      )}

      {/* 과목 칩은 자세히 모드에서만 보이므로, 교사가 직접 고른 그 경우에만 과목을 미리 연다(설계 D2). */}
      <AchievementStandardPicker
        initialSelectedKeys={selectedStandards.map(recordIdentity)}
        initialSubjectLabel={selectedCategory === '교과' && uiMode === 'detailed' ? selectedSubject : undefined}
        isOpen={isAchievementPickerOpen}
        onClose={() => setIsAchievementPickerOpen(false)}
        onInsert={handleInsertAchievementStandards}
        // 교과: 폼 학교급 카드와 같은 상태·같은 핸들러(성취수준 학생 수도 함께 초기화) — 단일 동기화.
        // 세특(고): 학교급이 카테고리로 고정이라 세그먼트를 내지 않는다.
        onSchoolLevelChange={selectedCategory === '교과' ? handleSchoolLevelChange : undefined}
        schoolLevel={selectedCategory === '교과_고등' ? '고등' : schoolLevel}
      />
    </div>
  );
};

export default SaenggibuGenerationForm; 